import { useState, useRef, useEffect, useMemo } from 'react';
import logo from './uprightA.png';
import { ref, get, set as rtdbSet, onValue, runTransaction, update as rtdbUpdate, push as rtdbPush } from "firebase/database";
import { RecaptchaVerifier, signInWithPhoneNumber, onAuthStateChanged, signInAnonymously } from "firebase/auth";
import { collection, addDoc, getDocs, getDoc, setDoc, doc, updateDoc, deleteDoc, query, orderBy, where, onSnapshot, serverTimestamp } from "firebase/firestore";
import { auth, rtdb, db } from "./firebase";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import SignatureCanvas from 'react-signature-canvas';

import './App.css';

// =====================================================================
// Achintya constants — 2 Oct 2019 foundational-investor epoch, 10 Cr
// liability envelope, and 31 Dec 2036 maturity (epoch + 6300 days) at
// which the foundational investor's 2%/30d CCI converges with their
// 1 PPD SI at 64×. All investors' SI is realisable on the same date.
// =====================================================================
const ACHINTYA_EPOCH = '2019-10-02';              // Foundational Investor start
const MATURITY_DATE = '2036-12-31';               // Convergence / maturity date
const ANCHOR_PRINCIPAL = 50000000;                // Rs 5,00,00,000 — Class A
const CLASS_B_CAP = 50000000;                     // Rs 5,00,00,000 — Class B aggregate cap
const TOTAL_LIABILITY_PRINCIPAL = ANCHOR_PRINCIPAL + CLASS_B_CAP; // Rs 10 Cr
const MATURITY_MULTIPLIER = 64;                   // 1 PPD SI = 2% CCI = 64x at day 6300
const DURATION_OPTIONS_MONTHS = [3, 6, 9, 12, 15, 18, 24, 36]; // Class B slot durations
const MS_PER_DAY = 1000 * 60 * 60 * 24;
// localStorage key for the secret/passkey login session (see handleVerifyOtp's secretMode
// branch + the onAuthStateChanged listener). signInAnonymously() sessions persist across a
// page reload (browserLocalPersistence) but carry no phone number, so this record is what lets
// the listener tell "my own restored secret-login session" apart from a genuinely abandoned
// anonymous session on reload.
// SECURITY: the value stored here is ONLY `{ uid }` — a bare pointer saying "this anonymous
// Firebase uid came from a secret-login flow". It must NEVER also carry username/isAdmin: those
// are re-derived from a live RTDB `users` read on every restore (matching whichever user record
// currently carries this uid), so a phone/role reassignment in RTDB takes effect on the very
// next reload instead of a stale identity persisting until an explicit logout.
const SECRET_SESSION_KEY = 'fireact.secretSession';

// Pure helpers — no side effects, safe to use anywhere
function daysBetween(fromISO, toISO) {
  const a = new Date(fromISO); a.setHours(0,0,0,0);
  const b = new Date(toISO); b.setHours(0,0,0,0);
  return Math.floor((b - a) / MS_PER_DAY);
}
function daysSinceEpoch(nowMs = Date.now()) {
  const epoch = new Date(ACHINTYA_EPOCH); epoch.setHours(0,0,0,0);
  return Math.max(0, (nowMs - epoch.getTime()) / MS_PER_DAY);
}
// 1 PPD simple interest multiplier: principal -> principal * (1 + 0.01 * days)
function siMultiplier(days) { return 1 + 0.01 * days; }
// 2% per 30 days, continuously compounded: equivalent to (1.02)^(days/30)
function cciMultiplier(days) { return Math.pow(1.02, days / 30); }
function formatINR(n, opts = {}) {
  const sign = n < 0 ? '-' : '';
  const v = Math.abs(Math.round(n || 0));
  return sign + '\u20B9' + v.toLocaleString('en-IN', opts);
}
// Add N months to a date (preserves day of month)
function addMonthsISO(startISO, months) {
  const d = new Date(startISO); d.setHours(0,0,0,0);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}
// Today's date in YYYY-MM-DD
function todayISO() {
  const d = new Date(); d.setHours(0,0,0,0);
  return d.toISOString().slice(0, 10);
}
// RTDB /wallets txns store dates as 'DD/MM/YYYY' (distinct from Firestore's 'YYYY-MM-DD' and
// the /statement node's 'DD-MM-YYYY') — convert to/from ISO so the rest of the file's date math
// (daysBetween, string-sort by date, etc., all of which assume ISO) works unmodified.
function ddmmyyyyToISO(s) {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(s || '').trim());
  if (!m) return '';
  const [, d, mo, y] = m;
  return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
}
function isoToDDMMYYYY(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || '').trim());
  if (!m) return '';
  const [, y, mo, d] = m;
  return `${d}/${mo}/${y}`;
}
// RTDB /wallets txns are WRITTEN as lowercase type: 'credit'|'debit' (see the schema note
// wherever txns[...] is constructed), but an earlier import round this session used capitalized
// 'Credit'/'Debit' (and a since-removed 'Rcommndr Debit' variant) — if any row imported under
// that earlier schema is still sitting in RTDB uncorrected, a strict `t.type === 'credit'` silently
// fails to match 'Credit', misclassifying it as a debit and collapsing that user's principal walk
// to 0. Normalize whatever's actually stored before comparing; anything not recognizably "credit"
// still falls through to debit via the caller's ternary/else, same fallback as before this fix.
function isWalletCredit(type) {
  return String(type || '').trim().toLowerCase() === 'credit';
}
// Single walk over a user's successful txns (chronological), used by the `walked` useMemo below
// for the currently-viewed user AND, on demand, by the Admin Settings panel for a separately
// picked user (extracted as a standalone pure function so both call sites share one
// implementation). Yields:
//   - principalRemaining: invest-sum minus principal-sourced withdrawals (clamped 0).
//                         CCI-interest-sourced withdrawals do not touch it.
//   - interest:           cumulative 1 PPD SI accrued on principalRemaining over time
//                         (freezes whenever principalRemaining hits 0).
//   - cciClaimable:       savings-account balance — +invest, −any withdraw (floor 0),
//                         grows at 2%/30d when positive.
// Withdraw txns may carry source: 'principal' | 'cciInterest'. Legacy withdrawals
// (no source) default to 'principal' to match pre-source behaviour.
function walkPrincipal(successfulTxns) {
  if (successfulTxns.length === 0) {
    return { principalRemaining: 0, interest: 0, cciClaimable: 0 };
  }
  const sorted = [...successfulTxns].sort((a, b) => a.date.localeCompare(b.date));
  const todayMs = new Date(todayISO()).getTime();
  let principalRemaining = 0;
  let cciBal = 0;
  let totalInterest = 0;
  let lastMs = null;
  for (const t of sorted) {
    const ms = new Date(t.date).getTime();
    if (lastMs !== null) {
      if (principalRemaining > 0) {
        const days = Math.max(0, Math.floor((ms - lastMs) / MS_PER_DAY));
        totalInterest += principalRemaining * 0.01 * days;
      }
      if (cciBal > 0) {
        const daysFloat = (ms - lastMs) / MS_PER_DAY;
        cciBal *= Math.pow(1.02, daysFloat / 30);
      }
    }
    const amt = Number(t.amount);
    const isInvest = t.sender !== 'achintya';
    if (isInvest) {
      principalRemaining += amt;
      cciBal += amt;
    } else {
      cciBal = Math.max(0, cciBal - amt);
      if (t.source !== 'cciInterest') {
        principalRemaining = Math.max(0, principalRemaining - amt);
      }
    }
    lastMs = ms;
  }
  if (lastMs !== null) {
    if (principalRemaining > 0) {
      const days = Math.max(0, Math.floor((todayMs - lastMs) / MS_PER_DAY));
      totalInterest += principalRemaining * 0.01 * days;
    }
    if (cciBal > 0) {
      const daysFloat = (todayMs - lastMs) / MS_PER_DAY;
      cciBal *= Math.pow(1.02, daysFloat / 30);
    }
  }
  return {
    principalRemaining: Math.round(principalRemaining * 100) / 100,
    interest: Math.round(totalInterest * 100) / 100,
    cciClaimable: Math.round(cciBal * 100) / 100,
  };
}
// Sweep-line: given slots [{startDate, endDate, principal, status}], return
// max principal simultaneously active in [winStart, winEnd).
// Skips matured/cancelled slots.
function maxActivePrincipal(slots, winStartISO, winEndISO) {
  const events = [];
  const winStart = new Date(winStartISO).getTime();
  const winEnd = new Date(winEndISO).getTime();
  for (const s of slots) {
    if (s.status === 'cancelled' || s.status === 'matured') continue;
    const st = Math.max(new Date(s.startDate).getTime(), winStart);
    const en = Math.min(new Date(s.endDate).getTime(), winEnd);
    if (st >= en) continue;
    events.push([st, +s.principal]);
    events.push([en, -s.principal]);
  }
  events.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  let cur = 0, max = 0;
  for (const [, delta] of events) {
    cur += delta;
    if (cur > max) max = cur;
  }
  return max;
}
// Available capacity in window [start, end) against CLASS_B_CAP
function availableForWindow(slots, winStartISO, winEndISO) {
  return Math.max(0, CLASS_B_CAP - maxActivePrincipal(slots, winStartISO, winEndISO));
}

function App() {
  const [loginState, setLoginState] = useState('login');
  // login → Enter button
  // phone → enter phone number
  // otp → enter OTP code

  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [otp, setOtp] = useState('');
  const [errorState, setErrorState] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [allUsers, setAllUsers] = useState([]); // [{key, phone, ...}]
  const [selectedUsername, setSelectedUsername] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [myUsername, setMyUsername] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      // Secret login flow manages state manually — skip the listener's override
      if (authListenerSkipRef.current) {
        setLoading(false);
        return;
      }
      if (firebaseUser) {
        if (!firebaseUser.phoneNumber) {
          // Anonymous Firebase user — either a genuine leftover from an abandoned secret-login
          // attempt in another tab, OR (far more likely, since this fires on every page refresh
          // once browserLocalPersistence restores the session) a legitimate secret/passkey login
          // from *this* session that survived the reload. authListenerSkipRef is in-memory only
          // and always resets to false on a fresh mount, so it can't tell these apart — check
          // localStorage instead, which does survive the reload.
          //
          // SECURITY: the cached record is trusted ONLY as a bare pointer — "this anonymous uid
          // belongs to a secret-login flow" — never as a source of truth for WHO that session is
          // or what it's allowed to do. username/isAdmin are re-derived from a live RTDB read
          // below on every single restore, exactly like the real phone-OTP path already does.
          // Do not "optimise" this by caching username/isAdmin — that would let a stale identity
          // (e.g. an account whose phone/role was since reassigned) survive indefinitely across
          // reloads instead of just until the next explicit logout.
          let restored = false;
          try {
            const raw = localStorage.getItem(SECRET_SESSION_KEY);
            const stored = raw ? JSON.parse(raw) : null;
            if (stored && stored.uid === firebaseUser.uid) {
              const usersSnap = await get(ref(rtdb, 'users'));
              let freshUsername = '';
              let freshPhone = '';
              if (usersSnap.exists()) {
                usersSnap.forEach((childSnap) => {
                  const data = childSnap.val();
                  if (data && data.uid === firebaseUser.uid) {
                    freshUsername = childSnap.key;
                    freshPhone = String(data.phone || '');
                  }
                });
              }
              if (freshUsername) {
                const phoneNumber = freshPhone.startsWith('+') ? freshPhone : `+${freshPhone}`;
                setUser({ phoneNumber, uid: `secret-${freshUsername}` });
                setMyUsername(freshUsername);
                setIsAdmin(freshUsername === 'achintya');
                setLoginState('done');
                restored = true;
              }
              // No RTDB user currently carries this uid (e.g. reassigned/removed since login) —
              // fall through to the stale-session sign-out below rather than granting access.
            }
          } catch (e) { console.error('secret session restore failed:', e); }
          if (restored) { setLoading(false); return; }
          // No matching (or no longer valid) localStorage record — stale/abandoned/revoked
          // anonymous session.
          try { localStorage.removeItem(SECRET_SESSION_KEY); } catch (e) {}
          try { await auth.signOut(); } catch (e) {}
          setUser(null);
          setLoginState('login');
          setMyUsername('');
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        setUser(firebaseUser);
        setLoginState('done');
        // Fetch username from RTDB (non-blocking for login)
        try {
          const matchPhone = firebaseUser.phoneNumber.replace('+', '');
          const usersSnap = await get(ref(rtdb, 'users'));
          if (usersSnap.exists()) {
            usersSnap.forEach((childSnap) => {
              const data = childSnap.val();
              const storedPhone = String(data.phone).replace('+', '');
              if (storedPhone === matchPhone) {
                setMyUsername(childSnap.key);
                if (childSnap.key === 'achintya') {
                  setIsAdmin(true);
                }
              }
            });
          }
        } catch (e) { console.error(e); }
      } else {
        setUser(null);
        setLoginState('login');
        setMyUsername('');
        setIsAdmin(false);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const countryCodes = [
    { code: '+91', label: 'IN +91' },
    { code: '+1', label: 'US +1' },
    { code: '+44', label: 'GB +44' },
    { code: '+61', label: 'AU +61' },
    { code: '+81', label: 'JP +81' },
    { code: '+49', label: 'DE +49' },
    { code: '+33', label: 'FR +33' },
    { code: '+86', label: 'CN +86' },
    { code: '+971', label: 'AE +971' },
    { code: '+65', label: 'SG +65' },
    { code: '+966', label: 'SA +966' },
    { code: '+55', label: 'BR +55' },
    { code: '+7', label: 'RU +7' },
    { code: '+82', label: 'KR +82' },
    { code: '+39', label: 'IT +39' },
    { code: '+34', label: 'ES +34' },
    { code: '+31', label: 'NL +31' },
    { code: '+46', label: 'SE +46' },
    { code: '+41', label: 'CH +41' },
    { code: '+64', label: 'NZ +64' },
    { code: '+27', label: 'ZA +27' },
    { code: '+234', label: 'NG +234' },
    { code: '+254', label: 'KE +254' },
    { code: '+62', label: 'ID +62' },
    { code: '+60', label: 'MY +60' },
    { code: '+63', label: 'PH +63' },
    { code: '+66', label: 'TH +66' },
    { code: '+84', label: 'VN +84' },
    { code: '+880', label: 'BD +880' },
    { code: '+92', label: 'PK +92' },
    { code: '+94', label: 'LK +94' },
    { code: '+977', label: 'NP +977' },
  ];

  const confirmationRef = useRef(null);
  const recaptchaRef = useRef(null);

  // Secret dev login: phone + trailing '0' skips OTP; entering 664488 logs into that user
  const [secretMode, setSecretMode] = useState(false);
  const [secretPhone, setSecretPhone] = useState('');
  const authListenerSkipRef = useRef(false);

  function setupRecaptcha() {
    if (!recaptchaRef.current) {
      recaptchaRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
      });
    }
  }

  async function handleSendOtp() {
    const raw = phone.trim();
    if (!raw) {
      setErrorState('Enter a phone number');
      return;
    }

    // Secret dev login: 11 digits ending in 0 → bypass OTP, expect code 664488
    if (raw.length === 11 && raw.endsWith('0')) {
      setSecretMode(true);
      setSecretPhone(raw.slice(0, -1));
      setErrorState('');
      setStatusMsg('');
      setOtp('');
      setLoginState('otp');
      return;
    }
    setSecretMode(false);

    const fullPhone = `${countryCode}${raw}`;

    try {
      setErrorState('');
      setStatusMsg('Sending OTP...');

      // Always recreate the verifier on each Send OTP click so retries don't
      // hit "reCAPTCHA has already been rendered in this element".
      if (recaptchaRef.current) {
        try { recaptchaRef.current.clear(); } catch (_) {}
        recaptchaRef.current = null;
      }
      const container = document.getElementById('recaptcha-container');
      if (container) container.innerHTML = '';
      setupRecaptcha();

      const confirmation = await signInWithPhoneNumber(auth, fullPhone, recaptchaRef.current);
      confirmationRef.current = confirmation;

      setStatusMsg('');
      setErrorState('');
      setLoginState('otp');
    } catch (err) {
      setStatusMsg('');
      setErrorState(err.message || 'Failed to send OTP');
      if (recaptchaRef.current) {
        recaptchaRef.current.clear();
        recaptchaRef.current = null;
      }
    }
  }

  async function handleVerifyOtp() {
    const code = otp.trim();
    if (!code) {
      setErrorState('Enter the OTP');
      return;
    }

    // Secret dev login path
    if (secretMode) {
      if (code !== '664488') {
        setErrorState('Invalid code');
        return;
      }
      try {
        setErrorState('');
        setStatusMsg('Authenticating...');
        // Tell the auth listener to ignore the anonymous sign-in we're about to trigger
        authListenerSkipRef.current = true;
        const anonCred = await signInAnonymously(auth);
        const anonUid = anonCred.user.uid;
        setStatusMsg('Checking registration...');
        const matchPhone = `${countryCode}${secretPhone}`.replace('+', '');
        const snap = await get(ref(rtdb, 'users'));
        let found = false;
        let matchedUsername = '';
        if (snap.exists()) {
          snap.forEach((childSnap) => {
            const data = childSnap.val();
            const storedPhone = String(data.phone).replace('+', '');
            if (storedPhone === matchPhone) {
              found = true;
              matchedUsername = childSnap.key;
            }
          });
        }
        if (!found) {
          try { await auth.signOut(); } catch (e) {}
          authListenerSkipRef.current = false;
          setStatusMsg('');
          setErrorState('Unregistered user');
          setLoginState('phone');
          setOtp('');
          setSecretMode(false);
          return;
        }
        // Stamp this anonymous uid onto the matched RTDB user record — same convention the real
        // phone-OTP path already uses (see the `users/<username>/uid` write further down) — so a
        // future reload can re-derive "which user is this" from a live RTDB read instead of a
        // cached one. SECURITY: only the bare `uid` pointer goes into localStorage below, never
        // the username or admin status — those must always be re-checked against RTDB, never
        // trusted from cache (see the onAuthStateChanged listener for why).
        await rtdbSet(ref(rtdb, `users/${matchedUsername}/uid`), anonUid);
        try { localStorage.setItem(SECRET_SESSION_KEY, JSON.stringify({ uid: anonUid })); } catch (e) {}
        setUser({ phoneNumber: `${countryCode}${secretPhone}`, uid: `secret-${matchedUsername}` });
        setMyUsername(matchedUsername);
        setIsAdmin(matchedUsername === 'achintya');
        setStatusMsg('');
        setLoginState('done');
        setSecretMode(false);
        return;
      } catch (err) {
        console.error('secret login error:', err);
        try { await auth.signOut(); } catch (e) {}
        authListenerSkipRef.current = false;
        setStatusMsg('');
        setErrorState(err?.message ? `Failed to log in: ${err.message}` : 'Failed to log in');
        return;
      }
    }

    try {
      setErrorState('');
      setStatusMsg('Verifying...');

      const result = await confirmationRef.current.confirm(code);
      // Match without '+' — e.g. "919876543210"
      const matchPhone = `${countryCode}${phone.trim()}`.replace('+', '');

      // Now authenticated — check if phone is registered in RTDB
      setStatusMsg('Checking registration...');
      const usersRef = ref(rtdb, 'users');
      const snap = await get(usersRef);

      let found = false;
      let matchedUsername = '';
      if (snap.exists()) {
        snap.forEach((childSnap) => {
          const data = childSnap.val();
          const storedPhone = String(data.phone).replace('+', '');
          if (storedPhone === matchPhone) {
            found = true;
            matchedUsername = childSnap.key;
          }
        });
      }

      if (!found) {
        await auth.signOut();
        setStatusMsg('');
        setErrorState('Unregistered user');
        setLoginState('phone');
        setOtp('');
        return;
      }

      // Save uid to RTDB for admin lookup
      await rtdbSet(ref(rtdb, `users/${matchedUsername}/uid`), result.user.uid);
      setUser(result.user);
      setMyUsername(matchedUsername);
      setIsAdmin(matchedUsername === 'achintya');
      setStatusMsg('');
      setLoginState('done');
    } catch (err) {
      setStatusMsg('');
      setErrorState('Invalid OTP');
    }
  }

  // ---- Dashboard state ----
  const [toast, setToast] = useState('');
  const [tab, setTab] = useState('transactions'); // homepage for all logged-in users (fundraiser hidden)
  const [transactions, setTransactions] = useState([]);
  const [txnAmount, setTxnAmount] = useState('');
  const [txnDate, setTxnDate] = useState(new Date());
  const [txnType, setTxnType] = useState('invest');
  const [txnStatus, setTxnStatus] = useState('');
  const [txnFormStatus, setTxnFormStatus] = useState('pending');

  const [editingTxnId, setEditingTxnId] = useState(null);

  const [bank, setBank] = useState({ name: '', accountNumber: '', ifsc: '', type: 'savings' });
  const [bankStatus, setBankStatus] = useState('');
  const [linkStep, setLinkStep] = useState(0);
  const [confirmAccountNumber, setConfirmAccountNumber] = useState('');
  const [linkSubmitting, setLinkSubmitting] = useState(false);

  // NDA state
  const [ndaAgreed, setNdaAgreed] = useState(false);
  const [ndaSigned, setNdaSigned] = useState(null);
  const [ndaName, setNdaName] = useState('');
  const [ndaStatus, setNdaStatus] = useState('');
  const sigPadRef = useRef(null);

  // Loan Agreement state
  const [loanAgreed, setLoanAgreed] = useState(false);
  const [loanSigned, setLoanSigned] = useState(null);
  const [loanStatus, setLoanStatus] = useState('');

  // Share Pledge Agreement state
  const [pledgeAgreed, setPledgeAgreed] = useState(false);
  const [pledgeSigned, setPledgeSigned] = useState(null);
  const [pledgeStatus, setPledgeStatus] = useState('');

  // Slots (Class B bookings) & role
  const [userRole, setUserRole] = useState('classB');     // 'anchor' | 'classB' | 'admin'
  const [slots, setSlots] = useState([]);                 // all slots across users (for availability)
  const [mySlots, setMySlots] = useState([]);             // current wallet owner's slots
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotForm, setSlotForm] = useState({ principal: '', duration: 3, startDate: todayISO() });
  const [slotStatus, setSlotStatus] = useState('');
  const [slotSubmitting, setSlotSubmitting] = useState(false);

  // RTDB users aggregate (legacy principal field, values in lakhs)
  const [rtdbUsers, setRtdbUsers] = useState([]); // [{name, principal, profit, role, ...}]
  useEffect(() => {
    if (loginState !== 'done' || !user) return;
    (async () => {
      try {
        const snap = await get(ref(rtdb, 'users'));
        if (!snap.exists()) return;
        const list = [];
        snap.forEach((child) => { list.push({ name: child.key, ...child.val() }); });
        setRtdbUsers(list);
      } catch (e) { console.error(e); }
    })();
  }, [loginState, user]);

  // Exclude synthetic/system entries from Class B principal aggregation
  const RTDB_SYSTEM_USERS = useMemo(() => new Set([
    'achintya', 'mohit-aggarwal', 'mohit', 'anothermohit', 'undefined', 'ayana'
  ]), []);
  const classBRtdb = useMemo(() => {
    return rtdbUsers
      .filter(u => !RTDB_SYSTEM_USERS.has(u.name))
      .map(u => ({ name: u.name, principal: Number(u.principal || 0), profit: Number(u.profit || 0) }))
      .filter(u => Number.isFinite(u.principal));
  }, [rtdbUsers, RTDB_SYSTEM_USERS]);
  const classBPrincipalLakhs = useMemo(() =>
    classBRtdb.reduce((s, u) => s + (u.principal || 0), 0), [classBRtdb]);
  const classBPrincipalRupees = classBPrincipalLakhs * 100000;
  // CLASS_B_CAP is in rupees; utilisation ratio against 5Cr
  const classBRtdbUtilPct = (classBPrincipalRupees / CLASS_B_CAP) * 100;

  // ── AI chat agent (per-user thread, admin-moderated) ──
  const [chatMessages, setChatMessages] = useState([]);      // current viewer's thread
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatError, setChatError] = useState('');
  const [pendingDrafts, setPendingDrafts] = useState([]);    // admin queue across all users
  const [draftEdits, setDraftEdits] = useState({});          // { [msgPath]: edited content }
  const chatEndRef = useRef(null);

  // Admin: live aggregate of every user's app sessions (one doc per visit)
  const [visits, setVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(false);

  // Live subscription to the current viewer's chat thread
  useEffect(() => {
    if (loginState !== 'done' || !user) return;
    const who = (isAdmin && selectedUsername) ? selectedUsername : myUsername;
    if (!who) return;
    const q = query(collection(db, 'aiChats', who, 'messages'), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      setChatMessages(list);
    }, (err) => console.error('chat onSnapshot:', err));
    return () => unsub();
  }, [loginState, user, isAdmin, selectedUsername, myUsername]);

  // Presence tracking — every authenticated user (incl. admin) gets one `visits` doc per session.
  // - Created on auth-complete with startedAt.
  // - lastSeenAt heartbeats every 60s and on visibility change.
  // - endedAt written on unmount, pagehide, or beforeunload (best effort — some browsers drop async work).
  // Admin reads the aggregate in the User Activity view.
  useEffect(() => {
    if (loginState !== 'done' || !user || !myUsername) return;

    let visitId = null;
    let cancelled = false;
    let heartbeat = null;

    (async () => {
      try {
        const ref = await addDoc(collection(db, 'visits'), {
          username: myUsername,
          isAdmin: !!isAdmin,
          startedAt: serverTimestamp(),
          lastSeenAt: serverTimestamp(),
          endedAt: null,
          userAgent: (navigator.userAgent || '').slice(0, 240),
        });
        if (cancelled) {
          updateDoc(ref, { endedAt: serverTimestamp() }).catch(() => {});
          return;
        }
        visitId = ref.id;
        heartbeat = setInterval(() => {
          if (!visitId) return;
          updateDoc(doc(db, 'visits', visitId), { lastSeenAt: serverTimestamp() }).catch(() => {});
        }, 60000);
      } catch (e) { console.error('visit start:', e); }
    })();

    const ping = () => {
      if (!visitId) return;
      updateDoc(doc(db, 'visits', visitId), { lastSeenAt: serverTimestamp() }).catch(() => {});
    };
    const end = () => {
      if (!visitId) return;
      updateDoc(doc(db, 'visits', visitId), { endedAt: serverTimestamp(), lastSeenAt: serverTimestamp() }).catch(() => {});
    };
    const onVisibility = () => { document.visibilityState === 'hidden' ? end() : ping(); };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', end);
    window.addEventListener('beforeunload', end);

    return () => {
      cancelled = true;
      if (heartbeat) clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', end);
      window.removeEventListener('beforeunload', end);
      end();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginState, user, myUsername]);

  // Admin-only: live snapshot of all user sessions (newest first), shown in the User Activity view.
  useEffect(() => {
    if (!isAdmin || loginState !== 'done' || tab !== 'visits') return;
    setVisitsLoading(true);
    const q = query(collection(db, 'visits'), orderBy('startedAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      setVisits(list.slice(0, 300));
      setVisitsLoading(false);
    }, (err) => { console.error('visits snap:', err); setVisitsLoading(false); });
    return () => unsub();
  }, [isAdmin, loginState, tab]);

  // Admin-only: aggregate pending-approval drafts across all users (polls once, + re-poll on tab entry)
  useEffect(() => {
    if (!isAdmin || loginState !== 'done' || tab !== 'airev') return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await get(ref(rtdb, 'users'));
        if (!snap.exists() || cancelled) return;
        const usernames = [];
        snap.forEach(child => usernames.push(child.key));
        const drafts = [];
        await Promise.all(usernames.map(async (uname) => {
          try {
            const qs = await getDocs(query(
              collection(db, 'aiChats', uname, 'messages'),
              where('status', '==', 'pending'),
              where('role', '==', 'assistant')
            ));
            qs.forEach(d => drafts.push({ id: d.id, username: uname, ...d.data() }));
          } catch { /* user may have no thread */ }
        }));
        if (!cancelled) {
          drafts.sort((a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0));
          setPendingDrafts(drafts);
        }
      } catch (e) { console.error('pending drafts:', e); }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, loginState, tab]);

  // Scroll to bottom of chat when new messages arrive
  useEffect(() => {
    if (tab === 'ask') chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [chatMessages, tab]);

  const handleSendChat = async () => {
    const text = chatInput.trim();
    if (!text) return;
    const who = (isAdmin && selectedUsername) ? selectedUsername : myUsername;
    if (!who) { setChatError('No wallet'); return; }
    setChatSending(true); setChatError('');
    try {
      await addDoc(collection(db, 'aiChats', who, 'messages'), {
        role: 'user',
        content: text,
        status: 'approved',       // user messages need no approval
        createdAt: serverTimestamp(),
      });
      setChatInput('');
    } catch (e) {
      console.error(e);
      setChatError('Send failed: ' + (e.message || e.code));
    } finally {
      setChatSending(false);
    }
  };

  const handleApproveDraft = async (draft, editedContent) => {
    try {
      const final = (editedContent ?? draft.content ?? draft.draftContent ?? '').trim();
      if (!final) return;
      await updateDoc(doc(db, 'aiChats', draft.username, 'messages', draft.id), {
        content: final,
        status: 'approved',
        approvedAt: serverTimestamp(),
        approvedBy: myUsername,
      });
      setPendingDrafts(prev => prev.filter(d => !(d.username === draft.username && d.id === draft.id)));
      setDraftEdits(prev => { const c = { ...prev }; delete c[`${draft.username}/${draft.id}`]; return c; });
    } catch (e) { console.error(e); alert('Approve failed: ' + e.message); }
  };

  const handleRejectDraft = async (draft) => {
    try {
      await updateDoc(doc(db, 'aiChats', draft.username, 'messages', draft.id), {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
        rejectedBy: myUsername,
      });
      setPendingDrafts(prev => prev.filter(d => !(d.username === draft.username && d.id === draft.id)));
    } catch (e) { console.error(e); alert('Reject failed: ' + e.message); }
  };

  // Live ticking clock for SI displays — rAF-style via setInterval, 1s cadence
  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Route anchor to their view by default after role loads
  useEffect(() => {
    if (userRole === 'anchor' && tab === 'transactions') setTab('anchor');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  // When admin switches between users (or clears selection), land on the
  // selected user's natural home tab — simulates "logging in as them".
  // Admin-only tabs (calendar, airev) get reset since they're hidden while impersonating.
  useEffect(() => {
    if (!isAdmin) return;
    if (selectedUsername) {
      // Entering impersonation — reset to transactions; anchor-route effect takes over if applicable.
      setTab('transactions');
    } else {
      // Leaving impersonation — back to admin's own transactions tab.
      setTab('transactions');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUsername]);

  // Whichever username is currently being viewed — admin's search selection, or the logged-in
  // user's own account when there is no selection (i.e. everyone, not just admin). Same
  // derivation the rest of the file already uses in several places (walletName, the
  // transactions-loading effect, etc.), hoisted here so the /wallets read below can share it.
  const viewedUsername = (isAdmin && selectedUsername) ? selectedUsername : myUsername;

  // RTDB /wallets/<username> as the SOURCE OF TRUTH for a handful of families whose real
  // transactions are missing/misattributed in Firestore — not a separate UI, just different
  // underlying data for the exact same balance-card/add-transaction-form/transaction-list every
  // viewer already sees, admin or not. Populated manually via Console import (not by the app).
  // Live via onValue so an edit via the Console, or a second tab, is reflected immediately.
  // Applies to WHOEVER is being viewed (viewedUsername), not admin-search-only: if Kiran Garg
  // logs in herself and /wallets/kiran-garg has data, she sees it too, same as when admin
  // searches her. Writing back stays admin-only (the add-transaction form and the txn-list's
  // click-to-edit are both already isAdmin-gated at the UI level, unchanged by this). Schema:
  // txns/<serial>: {username, date: 'DD/MM/YYYY', type: 'credit'|'debit', amount}. No `client`
  // field on the parent node (display name is derived from the username, same as elsewhere) and
  // no `note` field on a txn.
  const [rtdbWallet, setRtdbWallet] = useState(null); // {summary, txns} | null
  useEffect(() => {
    if (!viewedUsername) { setRtdbWallet(null); return; }
    setRtdbWallet(null);
    const wRef = ref(rtdb, `wallets/${viewedUsername}`);
    const unsub = onValue(wRef, (snap) => {
      setRtdbWallet(snap.exists() ? snap.val() : null);
    }, (e) => { console.error('rtdb wallet overlay load failed:', e); setRtdbWallet(null); });
    return () => unsub();
  }, [viewedUsername]);
  // True exactly when the standard transactions view for whoever is currently being viewed
  // should be driven from /wallets instead of the Firestore `transactions` collection — see the
  // transactions-loading effect below (the actual switch) and handleAddTransaction (the
  // matching write-side switch, which stays admin-only since only admin can reach that form).
  const viewingWallets = !!(viewedUsername && rtdbWallet && rtdbWallet.txns);

  // Recompute the wallet-level summary from its txns — called on every add/edit/delete so the
  // cached summary can never go stale relative to the underlying rows.
  function recomputeRtdbWalletSummary(txns) {
    let totalInvested = 0, totalWithdrawn = 0;
    Object.values(txns || {}).forEach((t) => {
      const amt = Number(t?.amount) || 0;
      const normalizedType = String(t?.type || '').trim().toLowerCase();
      if (normalizedType === 'credit') totalInvested += amt;
      else if (normalizedType === 'debit') totalWithdrawn += amt;
    });
    return {
      totalInvested,
      totalWithdrawn,
      netPrincipal: totalInvested - totalWithdrawn,
      lastUpdated: new Date().toISOString(),
    };
  }

  // Leveraged profit-withdrawal REQUESTS (the only enabled withdrawal type) live entirely in
  // RTDB, not Firestore — withdrawalRequests/<username>/<pushId>: {amount, date, status:
  // 'pending'|'successful'|'rejected', pledgePrincipalAmount, pledgeStatus: 'held'|'released',
  // submittedBy, submittedAt}. This is deliberately a SEPARATE node from wallets/<username>/txns
  // (whose schema stays strictly {username, date, type, amount} — no status/pending concept, per
  // its own doc comment above): a request is a mutable in-flight thing, the wallets ledger only
  // ever holds settled rows. Built this way (rather than in Firestore, as the withdraw flow
  // originally was) specifically because Firestore-only writes are invisible to a viewingWallets
  // user — that view never opens a Firestore subscription (see the transactions-loading effect
  // below) — so a wallets-sourced user's own withdrawal request would never show up as pending or
  // hold anything. RTDB-only sidesteps that split entirely: it works identically regardless of
  // whether the viewed user's settled ledger happens to live in Firestore or /wallets yet.
  const [withdrawalRequests, setWithdrawalRequests] = useState({});
  useEffect(() => {
    if (!viewedUsername) { setWithdrawalRequests({}); return; }
    setWithdrawalRequests({});
    const wrRef = ref(rtdb, `withdrawalRequests/${viewedUsername}`);
    const unsub = onValue(wrRef, (snap) => {
      setWithdrawalRequests(snap.exists() ? snap.val() : {});
    }, (e) => { console.error('withdrawalRequests load failed:', e); setWithdrawalRequests({}); });
    return () => unsub();
  }, [viewedUsername]);
  const withdrawalRequestList = useMemo(
    () => Object.entries(withdrawalRequests || {}).map(([id, r]) => ({ id, ...r })),
    [withdrawalRequests]
  );
  const pendingWithdrawalRequests = useMemo(
    () => withdrawalRequestList.filter(r => r.status === 'pending'),
    [withdrawalRequestList]
  );
  const pendingWithdrawalTotal = useMemo(
    () => pendingWithdrawalRequests.reduce((acc, r) => acc + (Number(r.amount) || 0), 0),
    [pendingWithdrawalRequests]
  );
  // Pending PLUS 'processing' — the latter is a different kind of entry (e.g. a refund already
  // recorded as a settled debit elsewhere, just visibly flagged in-flight here — see
  // ledgerRecordedSeparately on such an entry, and its guard in handleResolveWithdrawalRequest)
  // that should show in the Transactions list / admin review, but never counts toward the
  // profitLeverage cap or the card's own pending banner (both of those stay scoped to
  // pendingWithdrawalRequests above, unaffected).
  const visibleWithdrawalRequests = useMemo(
    () => withdrawalRequestList.filter(r => r.status === 'pending' || r.status === 'processing'),
    [withdrawalRequestList]
  );
  // Held pledges from RTDB requests (any status — a pledge stays 'held' until explicitly
  // released on approve/reject, regardless of the request's own status field), added to the
  // Firestore-sourced heldPledgeAmount below so a leftover legacy Firestore-pledge (from before
  // this RTDB rebuild) and a new RTDB one are both accounted for during the transition.
  const rtdbHeldPledge = useMemo(
    () => withdrawalRequestList.reduce((acc, r) => acc + (r.pledgeStatus === 'held' ? (Number(r.pledgePrincipalAmount) || 0) : 0), 0),
    [withdrawalRequestList]
  );

  // FAQ state (expanded question index, or -1 for all collapsed)
  const [faqOpen, setFaqOpen] = useState(-1);

  // User-submitted transaction request (defaults to pending for admin approval)
  const [submitType, setSubmitType] = useState('invest');
  const [submitAmount, setSubmitAmount] = useState('');
  const [submitDate, setSubmitDate] = useState(new Date());
  const [submitStatus, setSubmitStatus] = useState('');
  const [submitBusy, setSubmitBusy] = useState(false);

  // Quick-withdraw form on the wallet card (inline). `mode` picks the cap source:
  // 'principal' caps at netPrincipal; 'cciInterest' caps at cciProfit.
  const [showWalletWithdraw, setShowWalletWithdraw] = useState(false);
  const [walletWMode, setWalletWMode] = useState('principal');
  const [walletWAmount, setWalletWAmount] = useState('');
  const [walletWStatus, setWalletWStatus] = useState('');

  // Live total of the VIEWED user's currently-held "pledge" holds — an optional
  // pledgePrincipalAmount + pledgeStatus:'held' pair some withdrawal transactions carry (e.g. a
  // portion of a profit/interest withdrawal that's frozen from PRINCIPAL until that specific
  // withdrawal is approved/rejected — see handleSavePendingEdit, which flips pledgeStatus to
  // 'released' on resolution). Sourced directly from Firestore (where('receiver','==',
  // viewedUsername), filtered client-side for pledgeStatus==='held' to avoid needing a composite
  // index, matching this file's existing single-field-query convention) rather than from the
  // `transactions` state, because `transactions` can be RTDB /wallets-sourced (viewingWallets)
  // for a migrated user — a pledge hold is a Firestore-only concept and must be visible
  // regardless of which source is currently driving the displayed balance. Subtracted from
  // "available principal to withdraw" everywhere that's computed below (handleWalletWithdraw,
  // the Principal balance section) — keyed on viewedUsername (not myUsername) so it's correct
  // whether the viewer is the account owner or admin searching this user.
  const [firestoreHeldPledge, setFirestoreHeldPledge] = useState(0);
  useEffect(() => {
    if (!viewedUsername) { setFirestoreHeldPledge(0); return; }
    const q = query(collection(db, 'transactions'), where('receiver', '==', viewedUsername));
    const unsub = onSnapshot(q, (snap) => {
      let total = 0;
      snap.forEach((d) => {
        const data = d.data();
        if (data.pledgeStatus === 'held') total += Number(data.pledgePrincipalAmount) || 0;
      });
      setFirestoreHeldPledge(total);
    }, (e) => { console.error('held pledge query failed:', e); setFirestoreHeldPledge(0); });
    return () => unsub();
  }, [viewedUsername]);
  // Combined total: legacy Firestore-held pledges (other withdrawal types, pre-rebuild) + the
  // new RTDB-held ones (rtdbHeldPledge, declared above) — see its declaration for why both.
  const heldPledgeAmount = firestoreHeldPledge + rtdbHeldPledge;

  // Admin Settings panel (its own tab, decoupled from the header search / searched-wallet view —
  // that view must stay a pure mirror of what the user themselves sees, no admin controls mixed
  // in): reject a picked user's currently-pending withdrawals and replace them with one new
  // pending profit/interest withdrawal sized to their current net principal, 1/10th of it
  // pledged/held against principal until that new withdrawal resolves. Was being done by hand
  // via direct Firestore writes — this is the generalised, any-user version of the exact same
  // mechanic already enforced by the pledge machinery from commit b518aa8 (release-on-resolve,
  // wallets sync on approve). This panel only creates the docs; it does not duplicate that
  // enforcement logic.
  const PROFIT_WITHDRAWAL_MIN_PRINCIPAL = 100000; // ₹1,00,000 — below this, the action is disabled
  // Self-service profitLeverage requests must be at least this amount — it's WHY the principal
  // floor above is ₹1,00,000: pledge = requestedAmount / 5, so the minimum pledge at the minimum
  // withdrawal (₹5,00,000 / 5) is exactly ₹1,00,000, matching PROFIT_WITHDRAWAL_MIN_PRINCIPAL.
  const PROFIT_WITHDRAWAL_MIN_AMOUNT = 500000; // ₹5,00,000
  // Own user picker (same allUsers search/select pattern as the header search box above), fully
  // independent of `selectedUsername` — picking someone here must not affect the header search
  // or navigate admin "into" that user's wallet view.
  const [adminSettingsUsername, setAdminSettingsUsername] = useState(null);
  const [adminSettingsSearch, setAdminSettingsSearch] = useState('');
  // Net principal for the picked user, fetched on demand (not live) when the pick changes —
  // mirrors exactly how the unified balance card derives it (walkPrincipal over successful txns,
  // sourced from /wallets if that user has data there, else Firestore) so the number always
  // matches what admin would see if they opened that user's wallet.
  const [adminSettingsPrincipal, setAdminSettingsPrincipal] = useState(null); // null = not loaded
  const [adminSettingsLoading, setAdminSettingsLoading] = useState(false);
  useEffect(() => {
    if (!adminSettingsUsername) { setAdminSettingsPrincipal(null); return; }
    let cancelled = false;
    setAdminSettingsLoading(true);
    setAdminSettingsPrincipal(null);
    (async () => {
      try {
        let successful;
        const walletSnap = await get(ref(rtdb, `wallets/${adminSettingsUsername}`));
        const walletData = walletSnap.exists() ? walletSnap.val() : null;
        if (walletData && walletData.txns) {
          successful = Object.values(walletData.txns).map((t) => ({
            sender: isWalletCredit(t.type) ? adminSettingsUsername : 'achintya',
            receiver: isWalletCredit(t.type) ? 'achintya' : adminSettingsUsername,
            amount: Number(t.amount) || 0,
            date: ddmmyyyyToISO(t.date),
            status: 'successful',
          }));
        } else {
          const [snapR, snapS] = await Promise.all([
            getDocs(query(collection(db, 'transactions'), where('receiver', '==', adminSettingsUsername))),
            getDocs(query(collection(db, 'transactions'), where('sender', '==', adminSettingsUsername))),
          ]);
          const map = new Map();
          snapR.forEach((d) => map.set(d.id, { id: d.id, ...d.data() }));
          snapS.forEach((d) => map.set(d.id, { id: d.id, ...d.data() }));
          successful = Array.from(map.values()).filter((t) => t.status === 'successful');
        }
        if (!cancelled) setAdminSettingsPrincipal(walkPrincipal(successful).principalRemaining);
      } catch (e) {
        console.error('admin settings principal fetch failed:', e);
        if (!cancelled) setAdminSettingsPrincipal(0);
      } finally {
        if (!cancelled) setAdminSettingsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [adminSettingsUsername]);
  // Admin-configurable multiplier/pledge% for the withdrawal this panel creates — plain strings
  // (not numbers) so the input can be empty/mid-edit without fighting controlled-input coercion;
  // parsed with Number() wherever used. Defaults match the original hardcoded 1×/10% behavior.
  const [adminSettingsMultiplier, setAdminSettingsMultiplier] = useState('1');
  const [adminSettingsPledgePercent, setAdminSettingsPledgePercent] = useState('10');
  const [profitWithdrawBusy, setProfitWithdrawBusy] = useState(false);
  const [profitWithdrawStatus, setProfitWithdrawStatus] = useState('');
  const [bulkRejectBusy, setBulkRejectBusy] = useState(false);
  const [bulkRejectStatus, setBulkRejectStatus] = useState('');

  // "Add Transaction" (admin only, in the Pending Transactions tab) — a direct ledger-correction
  // tool, own independent user picker (same allUsers search/select pattern as Admin Settings'
  // picker), so it's not tied to the header search / selectedUsername or to whichever user's
  // pending list is currently shown. Always records a settled ('successful') entry, never
  // pending — for backdated/historical corrections (e.g. "this was actually settled in 2021").
  const [addTxnUsername, setAddTxnUsername] = useState(null);
  const [addTxnSearch, setAddTxnSearch] = useState('');
  const [addTxnAmount, setAddTxnAmount] = useState('');
  const [addTxnType, setAddTxnType] = useState('invest');
  const [addTxnDate, setAddTxnDate] = useState(new Date());
  const [addTxnBusy, setAddTxnBusy] = useState(false);
  const [addTxnStatus, setAddTxnStatus] = useState('');
  // Withdraw-only option: also drop a visible "Processing" marker (withdrawalRequests, status:
  // 'processing', ledgerRecordedSeparately: true) alongside the settled debit above — for a
  // refund that's already been recorded in the ledger (so principal/leverage figures reflect it
  // immediately) but is still actually in flight (e.g. money not yet sent), so it stays visible
  // until admin marks it complete. See handleResolveWithdrawalRequest's guard on this flag.
  const [addTxnMarkProcessing, setAddTxnMarkProcessing] = useState(false);

  // Admin inline edit of a pending transaction inside the Pending Transactions tab
  const [editingPendingId, setEditingPendingId] = useState(null);
  const [pendingEditType, setPendingEditType] = useState('invest');
  const [pendingEditAmount, setPendingEditAmount] = useState('');
  const [pendingEditDate, setPendingEditDate] = useState(new Date());
  const [pendingEditStatus, setPendingEditStatus] = useState('pending');
  const [pendingEditMsg, setPendingEditMsg] = useState('');

  // Bank Statements — used to validate that submitted txns match a real bank entry
  // (same date + same direction credit/debit). Loaded eagerly:
  //   - in dev: from /dev/bank-statements.json (gitignored, hosting-ignored)
  //   - in prod: from Firestore bankStatements/data (uploaded once via admin button)
  // Errors (missing file / permission-denied / doc-not-uploaded-yet) are silent —
  // validation is best-effort; if data isn't available, submit just isn't blocked.
  const [bankStmts, setBankStmts] = useState(null);
  const [bankStmtsErr, setBankStmtsErr] = useState('');
  const [bankStmtsFilter, setBankStmtsFilter] = useState('all'); // all | matched | unmatched
  const [bankStmtsSearch, setBankStmtsSearch] = useState('');
  const [bankStmtsUploadStatus, setBankStmtsUploadStatus] = useState('');
  // Full /transactions collection — fetched lazily when admin opens the bank-stmts view,
  // so each bank row can be cross-linked to its booked Firestore txn (by date + amount + direction).
  const [allTxnsCache, setAllTxnsCache] = useState(null);
  useEffect(() => {
    if (bankStmts !== null) return;
    if (process.env.NODE_ENV === 'development') {
      fetch('/dev/bank-statements.json')
        .then(r => r.ok ? r.json() : Promise.reject(r.status))
        .then(setBankStmts)
        .catch(() => setBankStmtsErr('Failed to load /dev/bank-statements.json — file present?'));
      return;
    }
    if (!user) return;                  // wait until auth so the Firestore read is allowed
    getDoc(doc(db, 'bankStatements', 'data'))
      .then(snap => { if (snap.exists()) setBankStmts(snap.data().rows || []); })
      .catch(() => { /* permission-denied or offline → skip validation, no UI noise */ });
  }, [user, bankStmts]);
  // Lazy-load every txn once when admin opens the bank-statements or recommender-statements
  // tab (both need to cross-link raw statement rows against already-booked transactions).
  useEffect(() => {
    if ((tab !== 'bankstmts' && tab !== 'recommenderstmts') || !isAdmin) return;
    if (allTxnsCache !== null) return;
    getDocs(collection(db, 'transactions'))
      .then(snap => setAllTxnsCache(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(e => console.error('bankstmts: load all txns failed', e));
  }, [tab, isAdmin, allTxnsCache]);

  // Recommender Statements — Achintya's own ICICI current account (058305003865, "Rcommndr",
  // ICIC0000583), already fully parsed into Firestore by a past session as the `rcommndr`
  // collection (one doc per statement line item, plus one `_meta` doc) in this same named
  // database ('delhi', see firebase.js). This is a READ of that existing collection — there is
  // no upload step here, unlike Bank Statements. Rows with withdrawal/deposit > ₹50,000 that
  // aren't already linked to a `transactions` doc (via `rcommndrId`, or by a legacy
  // date+amount+direction match — see the `icici`/`hdfc` bankstmts tab above for that pattern)
  // are actionable: admin assigns a user + invest/withdraw direction and Approves, which books
  // a new `transactions` doc stamped with `rcommndrId`/`bankVerified`/`bankVerifiedAt` — the
  // same linkage convention already used by the org's ledger/verify_investment.py tooling.
  const RECOMMENDER_THRESHOLD = 50000; // strictly greater than this is actionable
  const [recRows, setRecRows] = useState(null); // array of rcommndr docs ({id, ...}), or null while loading
  const [recRowsErr, setRecRowsErr] = useState('');
  const [recStmtsFilter, setRecStmtsFilter] = useState('actionable'); // actionable | processed | ignored | all
  const [recStmtsSearch, setRecStmtsSearch] = useState('');
  const [recStmtsRowAction, setRecStmtsRowAction] = useState({}); // {[rowId]: {username, direction}}
  const [recStmtsBusyId, setRecStmtsBusyId] = useState(null); // rowId currently being approved
  useEffect(() => {
    if (tab !== 'recommenderstmts' || !isAdmin) return;
    if (recRows !== null) return;
    getDocs(collection(db, 'rcommndr'))
      .then(snap => setRecRows(snap.docs.filter(d => d.id !== '_meta').map(d => ({ id: d.id, ...d.data() }))))
      .catch(e => { console.error('rcommndr: load failed', e); setRecRowsErr('Failed to load rcommndr collection'); });
  }, [tab, isAdmin, recRows]);

  // Statement — raw bank-statement rows imported wholesale into RTDB at /statement (a genuine
  // JS array on import, so .val() may come back as an array OR an object depending on exactly
  // how the import landed — handled defensively below). Pure browse + classify: admin can tag
  // any row with a `username` (no amount threshold, no Firestore/wallets write — see the render
  // block for why that's deliberately out of scope for this feature). Live via onValue so an
  // admin editing the Console, or a second admin tab, is reflected immediately.
  const [stmtRows, setStmtRows] = useState(null); // array of {_key, id, date, remarks, debit, credit, username?}, or null while loading
  const [stmtErr, setStmtErr] = useState('');
  const [stmtFilter, setStmtFilter] = useState('unclassified'); // unclassified | classified | all
  const [stmtSearch, setStmtSearch] = useState('');
  const [stmtRowAction, setStmtRowAction] = useState({}); // {[rowKey]: username}
  const [stmtBusyKey, setStmtBusyKey] = useState(null); // row _key currently being saved
  useEffect(() => {
    if (tab !== 'statement' || !isAdmin) return;
    const sRef = ref(rtdb, 'statement');
    const unsub = onValue(sRef, (snap) => {
      const v = snap.val();
      const rows = Array.isArray(v)
        ? v.map((row, i) => (row ? { ...row, _key: String(i) } : null)).filter(Boolean)
        : Object.entries(v || {}).map(([k, row]) => ({ ...row, _key: k }));
      setStmtRows(rows);
    }, (e) => { console.error('statement: load failed', e); setStmtErr('Failed to load /statement'); });
    return () => unsub();
  }, [tab, isAdmin]);

  // Tag a statement row with a username — the ONLY write this feature makes. Preserves every
  // other field on the row (update(), not set()); does not touch Firestore `transactions` or
  // RTDB `/wallets` — turning a classified row into an actual booked transaction/wallet entry
  // is a natural next step but explicitly out of scope here (flagged to the user, not built).
  async function handleClassifyStmtRow(rowKey, username) {
    if (!rowKey || !username) return;
    setStmtBusyKey(rowKey);
    try {
      await rtdbUpdate(ref(rtdb, `statement/${rowKey}`), { username });
      setStmtRowAction(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
    } catch (e) {
      console.error(e);
      alert('Classify failed: ' + e.message);
    } finally {
      setStmtBusyKey(null);
    }
  }

  // Cancel Investment state
  const [cancelSigned, setCancelSigned] = useState(null);
  const [cancelAgreed, setCancelAgreed] = useState(false);
  const [cancelName, setCancelName] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelStatus, setCancelStatus] = useState('');
  const cancelSigRef = useRef(null);

  const successfulTxns = useMemo(() => transactions.filter(t => t.status === 'successful'), [transactions]);
  const totalIn = useMemo(() => successfulTxns.reduce((s, t) => t.sender !== 'achintya' ? s + Number(t.amount) : s, 0), [successfulTxns]);
  const totalOut = useMemo(() => successfulTxns.reduce((s, t) => t.sender === 'achintya' ? s + Number(t.amount) : s, 0), [successfulTxns]);
  // Chronological principal/interest/CCI walk for the currently-viewed user — see the shared
  // walkPrincipal() (module scope, above) for the algorithm; also reused on-demand by the Admin
  // Settings panel for a separately-picked user.
  const walked = useMemo(() => walkPrincipal(successfulTxns), [successfulTxns]);

  const netPrincipal = walked.principalRemaining;
  const interest = walked.interest;
  const cciClaimable = walked.cciClaimable;
  // The interest portion of the CCI claimable (excludes principal still in the pot).
  const cciProfit = Math.max(0, cciClaimable - netPrincipal);

  // Admin model: 1% per day compound interest until 31/03/2021, then 1% per day SI after
  const adminProfit = useMemo(() => {
    if (successfulTxns.length === 0) return 0;
    const sorted = [...successfulTxns].sort((a, b) => a.date.localeCompare(b.date));
    const cutoff = new Date('2021-03-31');
    cutoff.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let compoundBal = 0;
    let runPrincipal = 0;
    let profit = 0;
    let compoundFrozen = false;

    for (let i = 0; i < sorted.length; i++) {
      const t = sorted[i];
      const txnDate = new Date(t.date);
      txnDate.setHours(0, 0, 0, 0);
      const isInvest = t.sender !== 'achintya';
      const amt = Number(t.amount);
      const nextDate = i < sorted.length - 1 ? new Date(sorted[i + 1].date) : today;
      nextDate.setHours(0, 0, 0, 0);

      runPrincipal += isInvest ? amt : -amt;
      compoundBal += isInvest ? amt : -amt;

      if (!compoundFrozen && txnDate < cutoff) {
        if (nextDate <= cutoff) {
          const days = Math.max(0, Math.floor((nextDate - txnDate) / 86400000));
          if (compoundBal > 0) compoundBal *= Math.pow(1.01, days);
        } else {
          // Period spans the cutoff
          const days1 = Math.max(0, Math.floor((cutoff - txnDate) / 86400000));
          if (compoundBal > 0) compoundBal *= Math.pow(1.01, days1);
          profit += Math.max(0, compoundBal - runPrincipal);
          compoundFrozen = true;
          const days2 = Math.max(0, Math.floor((nextDate - cutoff) / 86400000));
          if (runPrincipal > 0) profit += runPrincipal * 0.01 * days2;
        }
      } else {
        if (!compoundFrozen) {
          profit += Math.max(0, compoundBal - runPrincipal);
          compoundFrozen = true;
        }
        const days = Math.max(0, Math.floor((nextDate - txnDate) / 86400000));
        if (runPrincipal > 0) profit += runPrincipal * 0.01 * days;
      }
    }

    if (!compoundFrozen) {
      profit = Math.max(0, compoundBal - runPrincipal);
    }

    return Math.round(profit * 100) / 100;
  }, [successfulTxns]);

  const balance = cciClaimable;                    // 2% per 30d CCI — legally payable, withdrawable
  const siBalance = netPrincipal + interest;       // 1 PPD simple interest — notional headline (tracker until 2036)
  const siExcess = Math.max(0, siBalance - cciClaimable); // SI−CCI gap — buyback portion, not cash-payable pre-2036

  const walletName = useMemo(() => {
    const raw = (isAdmin && selectedUsername) ? selectedUsername : myUsername;
    if (!raw) return '';
    return raw.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }, [isAdmin, selectedUsername, myUsername]);

  // Phone number to show under the name on the balance card — looked up from allUsers (already
  // loaded for the header/Admin Settings search, keyed by username with a `.phone` field), so
  // this works the same whether it's admin viewing a searched user or someone viewing their own
  // wallet, without a second read.
  const walletPhone = useMemo(() => {
    const found = (allUsers || []).find(u => u.key === viewedUsername);
    return found?.phone || '';
  }, [allUsers, viewedUsername]);

  const inputStyle = {
    padding: 11, borderRadius: 8, border: '1.5px solid #111', fontSize: 15, width: '100%',
    boxSizing: 'border-box', background: '#fff', color: '#111', fontWeight: 700,
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
  };
  const btnStyle = {
    background: 'black', color: 'white', border: 'none',
    fontSize: 14, cursor: 'pointer', padding: '10px 16px', borderRadius: 8,
  };

  const [txnLoading, setTxnLoading] = useState(false);

  // Load bank/nda/cancel once username is known
  useEffect(() => {
    if (loginState === 'done' && user && myUsername) {
      loadBank(myUsername);
      loadNda(myUsername);
      loadLoan(myUsername);
      loadPledge(myUsername);
      loadCancel(myUsername);
      loadUserRole(myUsername);
      loadSlots();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginState, user, myUsername]);

  // Load admin users list once
  useEffect(() => {
    if (loginState === 'done' && user && myUsername && isAdmin) {
      loadAllUsers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginState, user, myUsername, isAdmin]);

  // Live transaction subscription — uses Firestore onSnapshot with metadata changes so
  // local writes (addDoc/updateDoc) reflect instantly with hasPendingWrites=true (sync spinner),
  // and the spinner disappears when the server confirms. No refresh ever needed.
  //
  // EXCEPT: when the currently-viewed user's real transactions live in RTDB /wallets instead of
  // Firestore (viewingWallets — see its declaration above; applies to whoever is being viewed,
  // admin search or a normal login, not admin-only), `transactions` is derived from that node
  // instead, converted into the exact shape the rest of the file (walked/netPrincipal/balance
  // card/transaction list) already expects — those all render completely unmodified, just fed
  // different underlying data. No Firestore subscription is created for that view. Each
  // synthetic row carries `origin: 'wallets'` + `walletKey` so handleAddTransaction/the delete
  // button (below, both admin-only UI regardless of data source) know to write back to RTDB
  // instead of Firestore for these rows.
  useEffect(() => {
    if (loginState !== 'done' || !user || !myUsername) return;
    const viewedUsername = (isAdmin && selectedUsername) ? selectedUsername : myUsername;
    setTransactions([]);
    setTxnLoading(true);
    setEditingTxnId(null);

    if (viewingWallets) {
      const walletTxns = Object.entries(rtdbWallet.txns || {}).map(([key, t]) => ({
        id: `wallet-${key}`,
        sender: isWalletCredit(t.type) ? viewedUsername : 'achintya',
        receiver: isWalletCredit(t.type) ? 'achintya' : viewedUsername,
        amount: Number(t.amount) || 0,
        date: ddmmyyyyToISO(t.date),
        status: 'successful',
        origin: 'wallets',
        walletKey: key,
      })).sort((a, b) => b.date.localeCompare(a.date));
      setTransactions(walletTxns);
      setTxnLoading(false);
      return; // no Firestore subscription for this view
    }

    const senderMap = new Map();
    const receiverMap = new Map();
    const merge = () => {
      const all = new Map();
      senderMap.forEach((v, k) => all.set(k, v));
      receiverMap.forEach((v, k) => all.set(k, v));
      setTransactions(Array.from(all.values()).sort((a, b) => b.date.localeCompare(a.date)));
    };
    const q1 = query(collection(db, 'transactions'), where('sender', '==', viewedUsername));
    const q2 = query(collection(db, 'transactions'), where('receiver', '==', viewedUsername));
    const unsub1 = onSnapshot(q1, { includeMetadataChanges: true }, (snap) => {
      senderMap.clear();
      snap.forEach(d => senderMap.set(d.id, { id: d.id, ...d.data(), syncing: d.metadata.hasPendingWrites }));
      merge();
      setTxnLoading(false);
    }, (err) => { console.error('txn sender snapshot:', err); setTxnLoading(false); });
    const unsub2 = onSnapshot(q2, { includeMetadataChanges: true }, (snap) => {
      receiverMap.clear();
      snap.forEach(d => receiverMap.set(d.id, { id: d.id, ...d.data(), syncing: d.metadata.hasPendingWrites }));
      merge();
      setTxnLoading(false);
    }, (err) => { console.error('txn receiver snapshot:', err); setTxnLoading(false); });
    return () => { unsub1(); unsub2(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginState, user, myUsername, isAdmin, selectedUsername, viewingWallets, rtdbWallet]);

  // Auto-migrate a user into RTDB /wallets the first time ADMIN looks them up (search box),
  // so /wallets organically becomes the source of truth for everyone over time instead of a
  // big-bang backfill. Admin-only trigger — a user logging into their own account never causes
  // this write for themselves (reading/displaying wallets data, once it exists, works for
  // anyone — see viewingWallets above — this effect is only about who's allowed to CREATE it).
  //
  // HARD SAFETY REQUIREMENT: must never touch a path that already has data — the family's 6
  // hand-reconciled entries must be preserved byte-for-byte. rtdbWallet===null (this effect's
  // trigger condition) is the live onValue state and can in principle be stale/racy at the
  // moment we decide to migrate, so it is NOT trusted as the actual safety check. The write
  // itself goes through runTransaction on wallets/<username>, whose update function is re-run by
  // the SDK against the CURRENT server value at commit time and aborts (returns undefined) the
  // instant that value is non-null — an atomic compare-and-swap, not a get()-then-set() pair, so
  // a concurrent write (e.g. two admin tabs, or the data arriving between trigger and commit)
  // can never be clobbered. When in doubt this errs on the side of not migrating.
  useEffect(() => {
    if (!isAdmin || !selectedUsername) return;     // admin-search-only trigger
    if (rtdbWallet !== null) return;                // already has data, or not loaded yet — never touch
    if (txnLoading) return;                          // Firestore transactions for this selection still loading
    const successful = transactions.filter(t => t.status === 'successful' && !t.origin);
    if (successful.length === 0) return;              // nothing to migrate

    const migratingUser = selectedUsername;
    const sorted = [...successful].sort((a, b) => a.date.localeCompare(b.date));
    const txns = {};
    sorted.forEach((t, i) => {
      txns[String(i + 1)] = {
        username: migratingUser,
        date: isoToDDMMYYYY(t.date),
        type: t.sender === migratingUser ? 'credit' : 'debit',
        amount: Number(t.amount) || 0,
      };
    });
    const summary = recomputeRtdbWalletSummary(txns);

    runTransaction(ref(rtdb, `wallets/${migratingUser}`), (current) => {
      if (current !== null) return; // abort — someone/something already wrote here; never overwrite
      return { txns, summary };
    }).then((result) => {
      if (result.committed) {
        console.log(`[wallets migration] ${migratingUser}: migrated ${sorted.length} txn(s) from Firestore`);
      } else {
        console.log(`[wallets migration] ${migratingUser}: skipped — already has /wallets data`);
      }
    }).catch((e) => console.error('wallets migration failed:', e));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, selectedUsername, rtdbWallet, transactions, txnLoading]);

  // Reload other per-user data when admin switches viewed user (or returns to own view)
  useEffect(() => {
    if (loginState !== 'done' || !user || !isAdmin) return;
    const target = selectedUsername || myUsername;
    if (!target) return;
    loadBank(target);
    loadNda(target);
    loadLoan(target);
    loadPledge(target);
    loadCancel(target);
    loadUserRole(target);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUsername]);

  async function loadAllUsers() {
    try {
      const snap = await get(ref(rtdb, 'users'));
      if (snap.exists()) {
        const list = [];
        snap.forEach((child) => {
          list.push({ key: child.key, ...child.val() });
        });
        setAllUsers(list);
      }
    } catch (e) { console.error(e); }
  }

  async function loadTransactions(username) {
    if (!username) return;
    setTxnLoading(true);
    try {
      // Query where username appears as sender OR receiver
      const q1 = query(collection(db, 'transactions'), where('sender', '==', username));
      const q2 = query(collection(db, 'transactions'), where('receiver', '==', username));
      const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
      const map = new Map();
      snap1.docs.forEach(d => map.set(d.id, { id: d.id, ...d.data() }));
      snap2.docs.forEach(d => map.set(d.id, { id: d.id, ...d.data() }));
      const all = Array.from(map.values()).sort((a, b) => b.date.localeCompare(a.date));
      setTransactions(all);
    } catch (e) {
      console.error('loadTransactions error:', e);
    }
    setTxnLoading(false);
  }

  async function loadBank(username) {
    if (!username) return;
    try {
      const snap = await get(ref(rtdb, `users/${username}/bank`));
      if (snap.exists()) {
        const b = snap.val();
        setBank(b);
        setLinkStep(3);
        setConfirmAccountNumber(b.accountNumber || '');
      } else {
        setBank({ name: '', accountNumber: '', ifsc: '', type: 'savings' });
        setLinkStep(0);
        setConfirmAccountNumber('');
      }
    } catch (e) {
      console.error(e);
    }
  }

  // Add or edit a transaction. Live snapshot listener handles UI updates instantly
  // (Firestore commits to local cache first → row appears with hasPendingWrites=true → spinner shows
  // until server confirms, then spinner disappears). No manual temp-ID juggling needed.
  async function handleAddTransaction() {
    if (!txnAmount) { setTxnStatus('Enter amount'); return; }
    if (isAdmin && !selectedUsername && !editingTxnId) { setTxnStatus('Select a user first'); return; }
    const dateStr = txnDate.toISOString().split('T')[0];
    const isInvest = txnType === 'invest';
    const isReversal = txnType === 'reversal';
    const targetUsername = (isAdmin && selectedUsername) ? selectedUsername : myUsername;
    const amt = Number(txnAmount);

    // Edit existing
    if (editingTxnId) {
      const existing = transactions.find(t => t.id === editingTxnId);
      if (!existing) return;
      const existingUsername = existing.sender !== 'achintya' ? existing.sender : existing.receiver;

      if (existing.origin === 'wallets') {
        // This row is sourced from RTDB /wallets (viewingWallets — see its declaration and the
        // transactions-loading effect above), not a Firestore doc — write the edit back to that
        // same row instead. Reversal type and pending/rejected status aren't offered for these
        // rows (see the form controls above), so every wallets edit is a plain credit/debit.
        setTxnAmount(''); setEditingTxnId(null); setTxnStatus('');
        runTransaction(ref(rtdb, `wallets/${existingUsername}`), (current) => {
          if (!current) return current; // node no longer exists — nothing to save into, abort
          const txns = { ...(current.txns || {}) };
          txns[existing.walletKey] = { username: existingUsername, date: isoToDDMMYYYY(dateStr), type: isInvest ? 'credit' : 'debit', amount: amt };
          return { ...current, txns, summary: recomputeRtdbWalletSummary(txns) };
        }).catch((e) => { console.error(e); setTxnStatus('Error saving'); });
        return;
      }

      const updated = {
        sender: isInvest ? existingUsername : 'achintya',
        receiver: isInvest ? 'achintya' : existingUsername,
        amount: amt,
        date: dateStr,
        status: txnFormStatus,
        ...(isReversal ? { type: 'reversal', remarks: 'Reversed' } : { type: '', remarks: '' }),
      };
      setTxnAmount('');
      setEditingTxnId(null);
      setTxnStatus('');
      updateDoc(doc(db, 'transactions', editingTxnId), updated)
        .catch((e) => { console.error(e); setTxnStatus('Error saving'); });
      return;
    }

    // New
    if (viewingWallets) {
      // Adding to a wallets-sourced view writes a new row into RTDB /wallets/<username> instead
      // of Firestore, keyed by the next sequential serial (matching the convention the existing
      // family data already uses).
      setTxnAmount(''); setTxnStatus('');
      runTransaction(ref(rtdb, `wallets/${targetUsername}`), (current) => {
        if (!current) return current;
        const txns = { ...(current.txns || {}) };
        const maxKey = Object.keys(txns).reduce((m, k) => Math.max(m, Number(k) || 0), 0);
        txns[String(maxKey + 1)] = { username: targetUsername, date: isoToDDMMYYYY(dateStr), type: isInvest ? 'credit' : 'debit', amount: amt };
        return { ...current, txns, summary: recomputeRtdbWalletSummary(txns) };
      }).catch((e) => { console.error(e); setTxnStatus('Error saving'); });
      return;
    }
    const newTxn = {
      sender: isInvest ? targetUsername : 'achintya',
      receiver: isInvest ? 'achintya' : targetUsername,
      amount: amt,
      date: dateStr,
      status: txnFormStatus,
      ...(isReversal ? { type: 'reversal', remarks: 'Reversed' } : {}),
    };
    setTxnAmount('');
    setTxnStatus('');
    addDoc(collection(db, 'transactions'), newTxn)
      .catch((e) => { console.error(e); setTxnStatus('Error saving'); });
  }

  // Admin-only ledger correction from the Pending Transactions tab's own "Add Transaction"
  // section — independent of selectedUsername/viewingWallets (uses addTxnUsername, its own
  // picker), so it works for ANY user regardless of what's currently selected in the header
  // search. Always records a settled entry: a fresh get() decides whether that user has RTDB
  // /wallets data (same maxKey-increment write as everywhere else in this file if so) or falls
  // back to a Firestore doc with status: 'successful' (never 'pending' — this is a direct
  // correction, not a request awaiting review).
  async function handleAddTransactionForUser() {
    if (!isAdmin || !addTxnUsername) { setAddTxnStatus('Select a user first'); return; }
    const amt = Number(addTxnAmount);
    if (!amt || amt <= 0) { setAddTxnStatus('Enter a valid amount'); return; }
    const isInvest = addTxnType === 'invest';
    const dateStr = (addTxnDate instanceof Date ? addTxnDate : new Date(addTxnDate)).toISOString().split('T')[0];
    setAddTxnBusy(true);
    setAddTxnStatus('');
    try {
      const walletSnap = await get(ref(rtdb, `wallets/${addTxnUsername}`));
      if (walletSnap.exists()) {
        await runTransaction(ref(rtdb, `wallets/${addTxnUsername}`), (current) => {
          if (!current) return current;
          const txns = { ...(current.txns || {}) };
          const maxKey = Object.keys(txns).reduce((m, k) => Math.max(m, Number(k) || 0), 0);
          txns[String(maxKey + 1)] = { username: addTxnUsername, date: isoToDDMMYYYY(dateStr), type: isInvest ? 'credit' : 'debit', amount: amt };
          return { ...current, txns, summary: recomputeRtdbWalletSummary(txns) };
        });
      } else {
        await addDoc(collection(db, 'transactions'), {
          sender: isInvest ? addTxnUsername : 'achintya',
          receiver: isInvest ? 'achintya' : addTxnUsername,
          amount: amt,
          date: dateStr,
          status: 'successful',
        });
      }
      if (!isInvest && addTxnMarkProcessing) {
        await rtdbPush(ref(rtdb, `withdrawalRequests/${addTxnUsername}`), {
          amount: amt,
          date: dateStr,
          status: 'processing',
          ledgerRecordedSeparately: true,
          submittedBy: myUsername,
          submittedAt: new Date().toISOString(),
        });
      }
      setAddTxnStatus(`${isInvest ? 'Investment' : 'Withdrawal'} of ${formatINR(amt)} recorded for ${addTxnUsername.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}.`);
      setAddTxnAmount('');
      setAddTxnMarkProcessing(false);
      setTimeout(() => setAddTxnStatus(''), 6000);
    } catch (e) {
      console.error('add transaction failed:', e);
      setAddTxnStatus('Error: ' + e.message);
    } finally {
      setAddTxnBusy(false);
    }
  }

  // User-facing: submit a transaction request in pending status for Achintya approval.
  // Fire-and-forget; the live snapshot listener instantly shows the new row with a sync
  // spinner that disappears once the server confirms. UI never needs a refresh.
  function handleSubmitRequest() {
    const amt = Number(submitAmount);
    if (!amt || amt <= 0) { setSubmitStatus('Enter a valid amount'); return; }
    if (!myUsername) { setSubmitStatus('Account not ready. Please log in again.'); return; }
    // Soft cap: at most 3 pending requests per user at a time. Not surfaced in the
    // UI until they hit it; opens up again as Achintya approves/rejects them.
    const myPendingCount = transactions.filter(t => t.status === 'pending').length;
    if (myPendingCount >= 3) {
      setSubmitStatus("You've reached the limit of 3 pending requests. Please wait for Achintya to review them before submitting another.");
      return;
    }
    const isInvest = submitType === 'invest';
    const dateStr = (submitDate instanceof Date ? submitDate : new Date(submitDate)).toISOString().split('T')[0];

    // Withdrawals: cap at (CCI claimable balance − all already-pending withdrawals),
    // so the sum of pending+new can never exceed what the user can actually claim.
    if (!isInvest) {
      const allPendingWithdrawn = transactions
        .filter(t => t.status === 'pending' && t.sender === 'achintya' && t.receiver === myUsername)
        .reduce((acc, t) => acc + (Number(t.amount) || 0), 0);
      const effectiveCap = Math.max(0, balance - allPendingWithdrawn);
      if (amt > Math.floor(effectiveCap)) {
        const reason = allPendingWithdrawn > 0
          ? `Amount exceeds your remaining claimable balance of ${formatINR(effectiveCap)} (${formatINR(allPendingWithdrawn)} already pending).`
          : `Amount exceeds your available balance of ${formatINR(balance)}.`;
        setSubmitStatus(reason);
        return;
      }
    }
    const txn = {
      sender: isInvest ? myUsername : 'achintya',
      receiver: isInvest ? 'achintya' : myUsername,
      amount: amt,
      date: dateStr,
      status: 'pending',
      submittedBy: myUsername,
      submittedAt: new Date().toISOString(),
    };
    addDoc(collection(db, 'transactions'), txn)
      .catch((e) => { console.error(e); setSubmitStatus('Error submitting. Please try again.'); });
    setSubmitAmount('');
    setSubmitDate(new Date());
    setSubmitType('invest');
    setSubmitStatus('Submitted. Achintya will review and approve shortly.');
    setTimeout(() => setSubmitStatus(''), 4000);
  }

  // Quick withdraw from the wallet card. Capped at either principal or net CCI interest,
  // depending on which section the user clicked. Goes through the same pending-approval
  // flow as submitTab. Same 3-pending cap applies. `source` is metadata. Submits against
  // viewedUsername (the account whose wallet is on screen) so this works identically whether
  // the viewer is that user themselves or an admin who searched them — submittedBy still
  // records myUsername (the actual logged-in account) so who-did-what stays traceable.
  function handleWalletWithdraw() {
    const amt = Number(walletWAmount);
    if (!amt || amt <= 0) { setWalletWStatus('Enter a valid amount'); return; }
    if (!viewedUsername) { setWalletWStatus('Account not ready. Please log in again.'); return; }
    // Firestore-pending (legacy other withdrawal types) + RTDB-pending (profitLeverage, the only
    // remaining path) — see pendingWithdrawalRequests' declaration.
    const myPendingCount = transactions.filter(t => t.status === 'pending').length + pendingWithdrawalRequests.length;
    if (myPendingCount >= 3) {
      setWalletWStatus("You've reached the limit of 3 pending requests. Please wait for Achintya to review them before submitting another.");
      return;
    }
    // Effective claimable = current balance minus any already-pending withdrawals from the same
    // bucket, so the user can never have pending requests in aggregate exceed what they can
    // actually claim. Only used by the legacy Firestore-sourced modes below (principal/
    // cciInterest) — profitLeverage computes its own cap from pendingWithdrawalTotal (RTDB)
    // instead, before this point is reached.
    const pendingForSrc = transactions
      .filter(t => t.status === 'pending' && t.sender === 'achintya' && t.receiver === viewedUsername && t.source === walletWMode)
      .reduce((acc, t) => acc + (Number(t.amount) || 0), 0);

    if (walletWMode === 'profitLeverage') {
      // Self-service leveraged profit withdrawal: up to 5x net principal, with principal/5 of
      // the REQUESTED amount pledged/held (capped at their actual principal) until this specific
      // request resolves — see PROFIT_WITHDRAWAL_MIN_PRINCIPAL / the Admin Settings panel for the
      // admin-initiated equivalent of this same mechanic (different multiplier/pledge% there;
      // this self-service path is fixed at 5x / amount-based-1/5th, not configurable by the user).
      // Written entirely to RTDB (withdrawalRequests/<user>, see its declaration) rather than
      // Firestore — a Firestore-only write here is invisible to a viewingWallets user, since that
      // view never opens a Firestore subscription (see the transactions-loading effect).
      if (netPrincipal < PROFIT_WITHDRAWAL_MIN_PRINCIPAL) {
        setWalletWStatus(`Requires at least ${formatINR(PROFIT_WITHDRAWAL_MIN_PRINCIPAL)} net principal.`);
        return;
      }
      if (amt < PROFIT_WITHDRAWAL_MIN_AMOUNT) {
        setWalletWStatus(`Minimum withdrawal amount is ${formatINR(PROFIT_WITHDRAWAL_MIN_AMOUNT)}.`);
        return;
      }
      const cap = Math.max(0, netPrincipal * 5 - pendingWithdrawalTotal);
      if (amt > Math.floor(cap)) {
        const reason = pendingWithdrawalTotal > 0
          ? `Amount exceeds your remaining leverage room of ${formatINR(cap)} (${formatINR(pendingWithdrawalTotal)} already pending).`
          : `Amount exceeds the maximum of ${formatINR(cap)} (5× your net principal).`;
        setWalletWStatus(reason);
        return;
      }
      const pledgeAmount = Math.min(netPrincipal, Math.round(amt / 5));
      const dateStr = new Date().toISOString().split('T')[0];
      rtdbPush(ref(rtdb, `withdrawalRequests/${viewedUsername}`), {
        amount: amt,
        date: dateStr,
        status: 'pending',
        pledgePrincipalAmount: pledgeAmount,
        pledgeStatus: 'held',
        submittedBy: myUsername,
        submittedAt: new Date().toISOString(),
      }).catch((e) => { console.error(e); setWalletWStatus('Error submitting. Please try again.'); });
      setWalletWAmount('');
      setShowWalletWithdraw(false);
      setWalletWStatus(`Profit withdrawal request for ${formatINR(amt)} submitted (${formatINR(pledgeAmount)} pledged from principal). Achintya will review shortly.`);
      setTimeout(() => setWalletWStatus(''), 6000);
      return;
    }

    // Principal cap excludes any currently-held pledge (see heldPledgeAmount's declaration) —
    // that portion is frozen pending another withdrawal's resolution, not separately claimable.
    const baseCap = walletWMode === 'principal' ? Math.max(0, netPrincipal - heldPledgeAmount) : cciProfit;
    const cap = Math.max(0, baseCap - pendingForSrc);
    const capLabel = walletWMode === 'principal' ? 'principal' : 'CCI interest';
    if (amt > Math.floor(cap)) {
      const reason = pendingForSrc > 0
        ? `Amount exceeds your remaining claimable ${capLabel} of ${formatINR(cap)} (${formatINR(pendingForSrc)} already pending).`
        : `Amount exceeds your available ${capLabel} of ${formatINR(cap)}.`;
      setWalletWStatus(reason);
      return;
    }
    const dateStr = new Date().toISOString().split('T')[0];
    addDoc(collection(db, 'transactions'), {
      sender: 'achintya',
      receiver: viewedUsername,
      amount: amt,
      date: dateStr,
      status: 'pending',
      submittedBy: myUsername,
      submittedAt: new Date().toISOString(),
      source: walletWMode,
    }).catch((e) => { console.error(e); setWalletWStatus('Error submitting. Please try again.'); });
    setWalletWAmount('');
    setShowWalletWithdraw(false);
    setWalletWStatus(`${capLabel === 'principal' ? 'Principal' : 'CCI interest'} withdrawal request for ${formatINR(amt)} submitted. Achintya will review shortly.`);
    setTimeout(() => setWalletWStatus(''), 5000);
  }

  // Admin: save the inline edit of a pending transaction (in the Pending Transactions tab).
  // Allows admin to change type/amount/date and pick approve/reject/pending status without
  // leaving the tab. Snapshot listener refreshes the row instantly.
  //
  // Two extra effects when this transaction carries a pledge hold / touches a wallets-migrated
  // user — both best-effort, secondary to the primary Firestore status write:
  //  1. Releasing a pledge hold: if `existing.pledgeStatus === 'held'` and the status is being
  //     resolved (successful or rejected), `pledgeStatus` flips to 'released' in the SAME
  //     updateDoc call as the status change (one write, both fields together).
  //  2. Wallets sync: if this is being approved (successful) and the counterparty currently has
  //     RTDB /wallets data, append a matching entry there too — this admin-global tab reviews
  //     transactions for ANY user, not just whoever's currently selected/viewed, so `rtdbWallet`
  //     (scoped to viewedUsername) isn't the right signal here; a fresh one-time get() on
  //     wallets/<counterparty> decides it, then the actual append goes through the same
  //     runTransaction pattern used everywhere else in this file for wallets writes.
  async function handleSavePendingEdit() {
    if (!editingPendingId) return;
    const amt = Number(pendingEditAmount);
    if (!amt || amt <= 0) { setPendingEditMsg('Enter a valid amount'); return; }
    const existing = transactions.find(t => t.id === editingPendingId);
    if (!existing) { setPendingEditMsg('Transaction not found'); return; }
    const counterparty = existing.sender !== 'achintya' ? existing.sender : existing.receiver;
    const isInvest = pendingEditType === 'invest';
    const dateStr = (pendingEditDate instanceof Date ? pendingEditDate : new Date(pendingEditDate)).toISOString().split('T')[0];
    const updated = {
      sender: isInvest ? counterparty : 'achintya',
      receiver: isInvest ? 'achintya' : counterparty,
      amount: amt,
      date: dateStr,
      status: pendingEditStatus,
    };
    if (existing.pledgeStatus === 'held' && (updated.status === 'successful' || updated.status === 'rejected')) {
      updated.pledgeStatus = 'released';
    }
    const pendingId = editingPendingId;
    setEditingPendingId(null);
    setPendingEditMsg('');
    try {
      await updateDoc(doc(db, 'transactions', pendingId), updated);
    } catch (e) {
      console.error(e);
      setPendingEditMsg('Error saving');
      return;
    }
    if (updated.status === 'successful') {
      try {
        const walletSnap = await get(ref(rtdb, `wallets/${counterparty}`));
        if (walletSnap.exists()) {
          // Dated to the moment of approval (today), not the transaction's own `date` field —
          // that field may reflect when the request was originally submitted/backlogged.
          const approvalDateISO = new Date().toISOString().split('T')[0];
          await runTransaction(ref(rtdb, `wallets/${counterparty}`), (current) => {
            if (!current) return current;
            const txns = { ...(current.txns || {}) };
            const maxKey = Object.keys(txns).reduce((m, k) => Math.max(m, Number(k) || 0), 0);
            txns[String(maxKey + 1)] = {
              username: counterparty,
              date: isoToDDMMYYYY(approvalDateISO),
              type: isInvest ? 'credit' : 'debit',
              amount: amt,
            };
            return { ...current, txns, summary: recomputeRtdbWalletSummary(txns) };
          });
        }
      } catch (e) {
        // Best-effort — the Firestore status write above already succeeded and is the source of
        // truth; a wallets-sync failure shouldn't surface as a failed approval.
        console.error('wallets sync on approve failed:', e);
      }
    }
  }

  // Admin approve/reject for an RTDB-sourced withdrawal request (withdrawalRequests/<user>, see
  // its declaration) — the RTDB counterpart to handleSavePendingEdit above, for the one
  // withdrawal type that no longer lives in Firestore. Always scoped to viewedUsername, since
  // withdrawalRequests is only ever loaded for whoever's wallet is currently on screen.
  // On approve: settles the request by appending a debit row to wallets/<user>/txns (same
  // maxKey-increment pattern used everywhere else in this file for wallets writes — see
  // handleSavePendingEdit's wallets-sync branch), initializing that node first if the user has no
  // /wallets data yet. On reject: just marks the request, no ledger write. Both release the
  // pledge hold in the same update as the status change. EXCEPT when req.ledgerRecordedSeparately
  // is true — a refund/correction that was already written to the wallet ledger directly at
  // creation time (so its principal effect is immediate, not deferred to approval); this request
  // is then just a visible "Processing" flag, and approving it must NOT also write a second debit.
  async function handleResolveWithdrawalRequest(requestId, approve) {
    if (!isAdmin || !viewedUsername) return;
    const req = withdrawalRequests[requestId];
    if (!req) return;
    try {
      await rtdbUpdate(ref(rtdb, `withdrawalRequests/${viewedUsername}/${requestId}`), {
        status: approve ? 'successful' : 'rejected',
        pledgeStatus: 'released',
      });
    } catch (e) {
      console.error('withdrawal request resolve failed:', e);
      return;
    }
    if (approve && !req.ledgerRecordedSeparately) {
      try {
        const approvalDateISO = new Date().toISOString().split('T')[0];
        await runTransaction(ref(rtdb, `wallets/${viewedUsername}`), (current) => {
          const txns = { ...((current && current.txns) || {}) };
          const maxKey = Object.keys(txns).reduce((m, k) => Math.max(m, Number(k) || 0), 0);
          txns[String(maxKey + 1)] = {
            username: viewedUsername,
            date: isoToDDMMYYYY(approvalDateISO),
            type: 'debit',
            amount: req.amount,
          };
          return { ...(current || { username: viewedUsername }), txns, summary: recomputeRtdbWalletSummary(txns) };
        });
      } catch (e) {
        // Best-effort — the RTDB status write above already succeeded and is the source of
        // truth; a wallets-ledger write failure shouldn't surface as a failed approval.
        console.error('wallets ledger write on approve failed:', e);
      }
    }
  }

  // Admin Settings panel: reject a picked user's currently-pending withdrawals and create one
  // new pending profit/interest withdrawal sized to `principal × multiplier` (default 1×, admin-
  // adjustable in the panel — e.g. 5× for a case needing a larger withdrawal), with
  // `principal × pledgePercent / 100` (default 10%, also admin-adjustable — e.g. 100% for a
  // fully-pledged case) held against principal until the new withdrawal resolves. Both derived
  // amounts are computed from PRINCIPAL, not from the withdrawal amount itself, so pledge % stays
  // meaningful even when multiplier != 1. General-purpose — works for any user, not hardcoded to
  // any family or ratio. Takes explicit params rather than reading component state directly so
  // it isn't implicitly tied to the header-search selectedUsername.
  //
  // The new request is written to RTDB withdrawalRequests/<user> (see its declaration) — NOT
  // Firestore. This used to addDoc into Firestore, which is exactly the bug that produced two
  // duplicate ₹35,00,000 entries for Pulkit Goyal: he's viewingWallets=true, whose UI never
  // opens a Firestore subscription at all, so the first run's Firestore-only write was
  // completely invisible, looked like it silently failed, and got run again. Existing pending
  // requests for this user are rejected from BOTH places — a one-time get() on
  // withdrawalRequests/<user> (current pending, if any) and a query on Firestore `transactions`
  // (legacy pending docs from before this fix, cleaned up here rather than left stray — this is
  // resolving EXISTING data, not creating anything new in Firestore).
  async function handleCreateProfitWithdrawal(targetUsername, principal, multiplier, pledgePercent) {
    if (!isAdmin || !targetUsername) return;
    // Eligibility is against raw principal only — unaffected by multiplier/pledge% below, which
    // just configure what happens once eligible (guarded by the disabled button too).
    if (principal < PROFIT_WITHDRAWAL_MIN_PRINCIPAL) return;
    if (!(multiplier >= 1) || !(pledgePercent >= 1 && pledgePercent <= 100)) return;
    const amount = Math.round(principal * multiplier);
    const pledgeAmount = Math.round(principal * pledgePercent / 100);
    const confirmMsg = `Reject all currently-pending withdrawals for ${targetUsername}, then create one new pending withdrawal of ${formatINR(amount)} for them, pledging ${formatINR(pledgeAmount)} (${pledgePercent}% of principal) until it resolves?`;
    if (!window.confirm(confirmMsg)) return;

    setProfitWithdrawBusy(true);
    setProfitWithdrawStatus('');
    try {
      const nowIso = new Date().toISOString();
      let rejectedCount = 0;

      // Legacy Firestore pending (from before this fix) — resolved, never created anew.
      const snap = await getDocs(query(collection(db, 'transactions'), where('receiver', '==', targetUsername)));
      const pendingFirestore = snap.docs.filter((d) => {
        const data = d.data();
        return data.sender === 'achintya' && data.status === 'pending';
      });
      await Promise.all(pendingFirestore.map((d) => {
        const data = d.data();
        const patch = {
          status: 'rejected',
          rejectedReason: 'Superseded by profit withdrawal creation',
          rejectedAt: nowIso,
        };
        if (data.pledgeStatus === 'held') patch.pledgeStatus = 'released';
        return updateDoc(doc(db, 'transactions', d.id), patch);
      }));
      rejectedCount += pendingFirestore.length;

      // Current RTDB pending requests for this user — one-time read (not the live
      // withdrawalRequests subscription, which is scoped to viewedUsername, not targetUsername).
      const wrSnap = await get(ref(rtdb, `withdrawalRequests/${targetUsername}`));
      const wr = wrSnap.exists() ? wrSnap.val() : {};
      const pendingRtdbIds = Object.entries(wr)
        .filter(([, r]) => r.status === 'pending')
        .map(([id]) => id);
      await Promise.all(pendingRtdbIds.map((id) =>
        rtdbUpdate(ref(rtdb, `withdrawalRequests/${targetUsername}/${id}`), { status: 'rejected', pledgeStatus: 'released' })
      ));
      rejectedCount += pendingRtdbIds.length;

      await rtdbPush(ref(rtdb, `withdrawalRequests/${targetUsername}`), {
        amount,
        date: new Date().toISOString().split('T')[0],
        status: 'pending',
        pledgePrincipalAmount: pledgeAmount,
        pledgeStatus: 'held',
        submittedBy: 'achintya',
        submittedAt: nowIso,
      });

      setProfitWithdrawStatus(
        `Rejected ${rejectedCount} pending withdrawal(s); created new pending profit withdrawal of ${formatINR(amount)}.`
      );
      setTimeout(() => setProfitWithdrawStatus(''), 6000);
    } catch (e) {
      console.error('profit withdrawal creation failed:', e);
      setProfitWithdrawStatus('Error: ' + e.message);
    } finally {
      setProfitWithdrawBusy(false);
    }
  }

  // System-wide, one-time cleanup: reject EVERY currently-pending withdrawal, for every user,
  // across both storage locations — legacy Firestore pending docs (from before withdrawals moved
  // to Profit Withdrawal) and any RTDB withdrawalRequests. Per "this is going to be the only
  // enabling withdrawal for all now" — everything else pending gets rejected so the switch to
  // Profit Withdrawal starts from a clean slate. Releases any held pledge on each as it rejects,
  // same as every other resolve path in this file. No amount is ever touched, only status/
  // pledgeStatus fields.
  async function handleRejectAllPendingWithdrawals() {
    if (!isAdmin) return;
    if (!window.confirm('Reject EVERY currently-pending withdrawal request, for every user, across the whole system? This cannot be undone.')) return;
    setBulkRejectBusy(true);
    setBulkRejectStatus('');
    try {
      const nowIso = new Date().toISOString();

      // Firestore: single-field query (sender only, matching this file's existing convention),
      // filtered client-side for status — every pending withdrawal, any receiver.
      const snap = await getDocs(query(collection(db, 'transactions'), where('sender', '==', 'achintya')));
      const pendingFirestore = snap.docs.filter((d) => d.data().status === 'pending');
      await Promise.all(pendingFirestore.map((d) => {
        const data = d.data();
        const patch = {
          status: 'rejected',
          rejectedReason: 'Bulk reject: only Profit Withdrawal (5x leverage) is enabled now',
          rejectedAt: nowIso,
        };
        if (data.pledgeStatus === 'held') patch.pledgeStatus = 'released';
        return updateDoc(doc(db, 'transactions', d.id), patch);
      }));

      // RTDB: one-time read of the whole withdrawalRequests node (every user), reject every
      // pending entry found.
      const allSnap = await get(ref(rtdb, 'withdrawalRequests'));
      const all = allSnap.exists() ? allSnap.val() : {};
      const rtdbWrites = [];
      Object.entries(all).forEach(([username, requests]) => {
        Object.entries(requests || {}).forEach(([id, r]) => {
          if (r.status === 'pending') {
            rtdbWrites.push(rtdbUpdate(ref(rtdb, `withdrawalRequests/${username}/${id}`), { status: 'rejected', pledgeStatus: 'released' }));
          }
        });
      });
      await Promise.all(rtdbWrites);

      setBulkRejectStatus(`Rejected ${pendingFirestore.length} legacy + ${rtdbWrites.length} RTDB pending withdrawal(s).`);
      setTimeout(() => setBulkRejectStatus(''), 8000);
    } catch (e) {
      console.error('bulk reject failed:', e);
      setBulkRejectStatus('Error: ' + e.message);
    } finally {
      setBulkRejectBusy(false);
    }
  }

  async function handleUploadStmtsToFirestore() {
    setBankStmtsUploadStatus('Checking…');
    try {
      const ref = doc(db, 'bankStatements', 'data');
      const existing = await getDoc(ref);
      if (existing.exists()) {
        setBankStmtsUploadStatus(`Already uploaded (${existing.data().rowCount} rows). Doc is immutable — delete from Firebase console to re-upload.`);
        return;
      }
      const r = await fetch('/dev/bank-statements.json');
      if (!r.ok) throw new Error('Local file fetch failed: ' + r.status);
      const rows = await r.json();
      await setDoc(ref, {
        rows,
        uploadedAt: serverTimestamp(),
        rowCount: rows.length,
      });
      setBankStmtsUploadStatus(`Uploaded ${rows.length} rows ✓ (reload to read from Firestore)`);
    } catch (e) {
      console.error(e);
      setBankStmtsUploadStatus('Upload failed: ' + e.message);
    }
  }

  // ---- Recommender Statements: approve ---------------------------------------------------
  // Turn one unbooked `rcommndr` row into a real, booked (status: successful) transaction,
  // stamped with the same rcommndrId/bankVerified/bankVerifiedAt linkage convention already
  // used by the org's ledger/verify_investment.py tooling, so it renders as processed and can
  // never be double-booked. Admin has already chosen the target user + invest/withdraw
  // direction (possibly overriding the inferred default) before this is called. No write
  // happens on the `rcommndr` doc itself — the linkage lives entirely on the transactions side.
  async function handleApproveRecommenderRow(row, username, direction) {
    if (!row) return;
    if (!username) { alert('Pick a user first'); return; }
    const amount = Math.max(Number(row.withdrawal) || 0, Number(row.deposit) || 0);
    if (!amount) { alert('Row has no amount'); return; }
    setRecStmtsBusyId(row.id);
    try {
      const isInvest = direction === 'invest';
      const nowIso = new Date().toISOString();
      const newTxn = {
        sender: isInvest ? username : 'achintya',
        receiver: isInvest ? 'achintya' : username,
        amount,
        date: row.date,
        status: 'successful',
        submittedBy: myUsername,
        submittedAt: nowIso,
        rcommndrId: row.id,
        bankVerified: true,
        bankVerifiedAt: nowIso,
      };
      const txnRef = await addDoc(collection(db, 'transactions'), newTxn);
      // Reflect the new txn in the already-loaded cache so this row immediately re-renders as
      // "processed" via the same rcommndrId lookup used for rows booked before this session.
      setAllTxnsCache(prev => (prev ? [...prev, { id: txnRef.id, ...newTxn }] : [{ id: txnRef.id, ...newTxn }]));
      setRecStmtsRowAction(prev => { const n = { ...prev }; delete n[row.id]; return n; });
    } catch (e) {
      console.error(e);
      alert('Approve failed: ' + e.message);
    } finally {
      setRecStmtsBusyId(null);
    }
  }

  // Mark a `rcommndr` row as "not an investor transaction" (own-name internal transfers,
  // bank interest credits, unrelated freelance/business income, etc — real data shows a
  // meaningful fraction of >₹50,000 rows on this account are not investor pooling at all).
  // Unlike approve, this creates no `transactions` doc — "ignored" isn't a transactions-side
  // concept, so the marker (ignoredAt/ignoredBy) is written directly onto the rcommndr row.
  async function handleDismissRecommenderRow(row) {
    if (!row) return;
    setRecStmtsBusyId(row.id);
    try {
      const nowIso = new Date().toISOString();
      await updateDoc(doc(db, 'rcommndr', row.id), { ignoredAt: nowIso, ignoredBy: myUsername });
      setRecRows(prev => prev ? prev.map(r => r.id === row.id ? { ...r, ignoredAt: nowIso, ignoredBy: myUsername } : r) : prev);
      setRecStmtsRowAction(prev => { const n = { ...prev }; delete n[row.id]; return n; });
    } catch (e) {
      console.error(e);
      alert('Ignore failed: ' + e.message);
    } finally {
      setRecStmtsBusyId(null);
    }
  }

  async function handleSaveBank() {
    if (!bank.name || !bank.accountNumber || !bank.ifsc) {
      setBankStatus('Fill all fields'); return;
    }
    try {
      setBankStatus('Saving...');
      const docUsername = (isAdmin && selectedUsername) ? selectedUsername : myUsername;
      await rtdbSet(ref(rtdb, `users/${docUsername}/bank`), bank);
      setBankStatus('Saved');
      setTimeout(() => setBankStatus(''), 2000);
    } catch (e) {
      setBankStatus('Error saving');
    }
  }


  async function loadNda(username) {
    if (!username) return;
    try {
      setNdaSigned(null);
      const snap = await get(ref(rtdb, `users/${username}/nda`));
      if (snap.exists()) {
        setNdaSigned(snap.val());
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function handleSignNda() {
    if (!ndaAgreed) { setNdaStatus('You must agree to the terms'); return; }

    try {
      setNdaStatus('Saving...');
      const ndaData = {
        phone: user.phoneNumber,
        signedAt: new Date().toISOString(),
        agreed: true,
      };
      const docUsername = (isAdmin && selectedUsername) ? selectedUsername : myUsername;
      await rtdbSet(ref(rtdb, `users/${docUsername}/nda`), ndaData);
      await loadNda(docUsername);
      setNdaStatus('');
      setToast('NDA accepted successfully');
      setTimeout(() => setToast(''), 3000);
    } catch (e) {
      setNdaStatus('Error saving');
    }
  }

  async function loadLoan(username) {
    if (!username) return;
    try {
      setLoanSigned(null);
      const snap = await get(ref(rtdb, `users/${username}/loanAgreement`));
      if (snap.exists()) {
        setLoanSigned(snap.val());
      }
    } catch (e) { console.error(e); }
  }

  async function handleSignLoan() {
    if (!loanAgreed) { setLoanStatus('You must agree to the terms'); return; }
    try {
      setLoanStatus('Saving...');
      const data = {
        phone: user.phoneNumber,
        signedAt: new Date().toISOString(),
        agreed: true,
      };
      const docUsername = (isAdmin && selectedUsername) ? selectedUsername : myUsername;
      await rtdbSet(ref(rtdb, `users/${docUsername}/loanAgreement`), data);
      await loadLoan(docUsername);
      setLoanStatus('');
      setToast('Loan Agreement accepted');
      setTimeout(() => setToast(''), 3000);
    } catch (e) { setLoanStatus('Error saving'); }
  }

  async function loadPledge(username) {
    if (!username) return;
    try {
      setPledgeSigned(null);
      const snap = await get(ref(rtdb, `users/${username}/sharePledge`));
      if (snap.exists()) {
        setPledgeSigned(snap.val());
      }
    } catch (e) { console.error(e); }
  }

  async function handleSignPledge() {
    if (!pledgeAgreed) { setPledgeStatus('You must agree to the terms'); return; }
    try {
      setPledgeStatus('Saving...');
      const data = {
        phone: user.phoneNumber,
        signedAt: new Date().toISOString(),
        agreed: true,
      };
      const docUsername = (isAdmin && selectedUsername) ? selectedUsername : myUsername;
      await rtdbSet(ref(rtdb, `users/${docUsername}/sharePledge`), data);
      await loadPledge(docUsername);
      setPledgeStatus('');
      setToast('Share Pledge Agreement accepted');
      setTimeout(() => setToast(''), 3000);
    } catch (e) { setPledgeStatus('Error saving'); }
  }

  // Role is stored in RTDB users/{name}/role ('anchor' | 'classB' | 'admin')
  async function loadUserRole(username) {
    if (!username) { setUserRole('classB'); return; }
    try {
      const snap = await get(ref(rtdb, `users/${username}/role`));
      if (snap.exists()) setUserRole(snap.val() || 'classB');
      else if (username === 'achintya') setUserRole('admin');
      else setUserRole('classB');
    } catch (e) { console.error(e); setUserRole('classB'); }
  }

  // Load ALL slots (small collection, needed for availability calc)
  async function loadSlots() {
    setSlotsLoading(true);
    try {
      const snap = await getDocs(collection(db, 'slots'));
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Auto-compute runtime status based on dates (source of truth = stored status if 'cancelled')
      const today = todayISO();
      const normalised = all.map(s => {
        if (s.status === 'cancelled') return s;
        if (s.endDate && s.endDate <= today) return { ...s, status: 'matured' };
        if (s.startDate && s.startDate <= today) return { ...s, status: 'active' };
        return { ...s, status: 'booked' };
      });
      setSlots(normalised);
    } catch (e) { console.error('loadSlots error:', e); }
    setSlotsLoading(false);
  }

  // Filter my-slots for the currently viewed wallet
  useEffect(() => {
    const who = (isAdmin && selectedUsername) ? selectedUsername : myUsername;
    if (!who) { setMySlots([]); return; }
    setMySlots(slots.filter(s => s.username === who));
  }, [slots, isAdmin, selectedUsername, myUsername]);

  async function handleBookSlot() {
    const who = (isAdmin && selectedUsername) ? selectedUsername : myUsername;
    if (!who) { setSlotStatus('Login first'); return; }
    const principal = Number(slotForm.principal);
    if (!Number.isFinite(principal) || principal <= 0) { setSlotStatus('Enter a valid amount'); return; }
    if (principal > CLASS_B_CAP) { setSlotStatus('Amount exceeds Rs 5 Cr cap'); return; }
    const dur = Number(slotForm.duration);
    if (!DURATION_OPTIONS_MONTHS.includes(dur)) { setSlotStatus('Pick a valid duration'); return; }
    const start = slotForm.startDate;
    if (!start || start < todayISO()) { setSlotStatus('Start date must be today or later'); return; }
    const end = addMonthsISO(start, dur);

    // Check availability against active+booked (live slots)
    const available = availableForWindow(slots, start, end);
    if (principal > available) {
      setSlotStatus(`Only ${formatINR(available)} available in [${start} -> ${end}]`);
      return;
    }
    setSlotStatus('');
    setSlotSubmitting(true);
    const doc_ = {
      username: who,
      principal,
      startDate: start,
      endDate: end,
      durationMonths: dur,
      status: start <= todayISO() ? 'active' : 'booked',
      bookedAt: new Date().toISOString(),
    };
    try {
      await addDoc(collection(db, 'slots'), doc_);
      setSlotForm({ principal: '', duration: 3, startDate: todayISO() });
      setToast('Slot booked');
      setTimeout(() => setToast(''), 3000);
      await loadSlots();
    } catch (e) {
      console.error(e);
      setSlotStatus('Error booking slot');
    }
    setSlotSubmitting(false);
  }

  async function handleCancelSlot(slotId) {
    if (!window.confirm('Cancel this slot booking?')) return;
    try {
      await updateDoc(doc(db, 'slots', slotId), { status: 'cancelled', cancelledAt: new Date().toISOString() });
      await loadSlots();
      setToast('Slot cancelled');
      setTimeout(() => setToast(''), 3000);
    } catch (e) { console.error(e); }
  }

  async function loadCancel(username) {
    if (!username) return;
    try {
      setCancelSigned(null);
      const snap = await get(ref(rtdb, `users/${username}/cancelInvestment`));
      if (snap.exists()) {
        setCancelSigned(snap.val());
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function handleSignCancel() {
    if (!cancelName.trim()) { setCancelStatus('Enter your full name'); return; }
    if (!cancelAgreed) { setCancelStatus('You must agree to the terms'); return; }
    if (!cancelSigRef.current || cancelSigRef.current.isEmpty()) { setCancelStatus('Please sign above'); return; }

    try {
      setCancelStatus('Saving...');
      const signatureData = cancelSigRef.current.toDataURL('image/png');
      const cancelData = {
        name: cancelName.trim(),
        phone: user.phoneNumber,
        signatureImg: signatureData,
        signedAt: new Date().toISOString(),
        totalInvested: totalIn,
        totalWithdrawn: totalOut,
        interestAccrued: interest,
        netBalance: balance,
        agreed: true,
      };
      const docUsername = (isAdmin && selectedUsername) ? selectedUsername : myUsername;
      await rtdbSet(ref(rtdb, `users/${docUsername}/cancelInvestment`), cancelData);
      await loadCancel(docUsername);
      setCancelStatus('');
      setTab('transactions');
      setToast('Cancellation request submitted successfully');
      setTimeout(() => setToast(''), 3000);
    } catch (e) {
      setCancelStatus('Error saving');
    }
  }

  if (loginState === 'done' && user) {
    return (
      <div style={{ minHeight: '100vh', background: '#f5f5f7', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
        {/* Toast notification */}
        {toast && (
          <div style={{
            position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
            background: '#111', color: '#fff', padding: '12px 24px', borderRadius: 10,
            fontSize: 14, fontWeight: 600, zIndex: 1000,
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            animation: 'fadeIn 0.3s ease',
          }}>
            {toast}
          </div>
        )}
        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 16px', background: '#fff', borderBottom: '1px solid #eee', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, cursor: 'pointer' }} onClick={() => window.location.reload()}>
            <img src={logo} style={{ height: 24 }} alt="logo" />
          </div>

          {/* Admin search in header */}
          {isAdmin && (
            <div style={{ flex: 1, position: 'relative', maxWidth: 220 }}>
              <input
                type="text"
                placeholder={selectedUsername
                  ? selectedUsername.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                  : 'Search user...'}
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                style={{
                  padding: '8px 12px', borderRadius: 8, border: '1.5px solid #ddd', fontSize: 13,
                  width: '100%', boxSizing: 'border-box', fontWeight: 600, color: '#111',
                  background: selectedUsername ? '#f0f8ff' : '#fff',
                }}
              />
              {selectedUsername && !userSearch && (
                <span
                  onClick={() => { setSelectedUsername(null); }}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    fontSize: 14, color: '#999', cursor: 'pointer', lineHeight: 1 }}
                >
                  &times;
                </span>
              )}
              {userSearch && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 100,
                  background: '#fff', borderRadius: 10, overflow: 'hidden',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 200, overflowY: 'auto',
                }}>
                  {allUsers
                    .filter(u => u.key !== 'achintya' && u.key.toLowerCase().includes(userSearch.toLowerCase()))
                    .map(u => (
                      <div key={u.key}
                        onClick={() => {
                          setSelectedUsername(u.key);
                          setUserSearch('');
                        }}
                        style={{
                          padding: '10px 14px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f0f0f0',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          background: selectedUsername === u.key ? '#f0f0f0' : '#fff',
                        }}
                      >
                        <span style={{ fontWeight: 600, color: '#111' }}>{u.key.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</span>
                        <span style={{ fontSize: 11, color: '#888' }}>{u.phone}</span>
                      </div>
                    ))}
                  {allUsers.filter(u => u.key !== 'achintya' && u.key.toLowerCase().includes(userSearch.toLowerCase())).length === 0 && (
                    <div style={{ padding: 14, fontSize: 12, color: '#999', textAlign: 'center' }}>No users found</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* User dropdown */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <span
              onClick={() => setShowUserMenu(prev => !prev)}
              style={{
                cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#111',
                padding: '6px 12px', border: '1.5px solid #111', borderRadius: 8,
                display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              {myUsername ? myUsername.split('-')[0].charAt(0).toUpperCase() + myUsername.split('-')[0].slice(1) : ''}
              <span style={{ fontSize: 10 }}>&#x25BE;</span>
            </span>
            {showUserMenu && (
              <div style={{
                position: 'absolute', right: 0, top: '100%', marginTop: 6, zIndex: 100,
                background: '#fff', borderRadius: 10, overflow: 'hidden',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 180,
              }}>
                <div style={{ padding: '12px 16px', fontSize: 12, color: '#888', borderBottom: '1px solid #f0f0f0' }}>
                  {user.phoneNumber}
                </div>
                {isAdmin && !selectedUsername && (
                  <div
                    onClick={() => { setShowUserMenu(false); setTab('fundraiser'); }}
                    style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#111', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                  >
                    Fundraiser
                  </div>
                )}
                <div
                  onClick={() => { setShowUserMenu(false); setTab('transactions'); }}
                  style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#111', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                >
                  My Wallet
                </div>
                {isAdmin && userRole === 'anchor' && (
                  <div
                    onClick={() => { setShowUserMenu(false); setTab('anchor'); }}
                    style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#111', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                  >
                    Anchor Balance
                  </div>
                )}
                {isAdmin && userRole !== 'anchor' && (
                  <div
                    onClick={() => { setShowUserMenu(false); setTab('invest'); }}
                    style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#111', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                  >
                    Invest
                  </div>
                )}
                {isAdmin && (
                  <div
                    onClick={() => { setShowUserMenu(false); setTab('myslots'); }}
                    style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#111', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                  >
                    My Slots
                  </div>
                )}
                {isAdmin && (
                  <div
                    onClick={() => { setShowUserMenu(false); setTab('ask'); }}
                    style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#111', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                  >
                    Ask AI
                  </div>
                )}
                {isAdmin && !selectedUsername && (
                  <div
                    onClick={() => { setShowUserMenu(false); setTab('airev'); }}
                    style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#111', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <span>AI Review</span>
                    {pendingDrafts.length > 0 && (
                      <span style={{ background: '#eb4d4b', color: '#fff', fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 10 }}>
                        {pendingDrafts.length}
                      </span>
                    )}
                  </div>
                )}
                {isAdmin && (
                  <div
                    onClick={() => { setShowUserMenu(false); setTab('liability'); }}
                    style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#111', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                  >
                    Achintya Liability
                  </div>
                )}
                {isAdmin && process.env.NODE_ENV === 'development' && (
                  <div
                    onClick={() => { setShowUserMenu(false); setTab('bankstmts'); }}
                    style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#111', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <span>Bank Statements</span>
                    <span style={{ background: '#999', color: '#fff', fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 10 }}>LOCAL</span>
                  </div>
                )}
                {isAdmin && (
                  <div
                    onClick={() => { setShowUserMenu(false); setTab('recommenderstmts'); }}
                    style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#111', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <span>Recommender Statements</span>
                    {recRows && allTxnsCache && (() => {
                      const bookedIds = new Set(allTxnsCache.filter(t => t.rcommndrId).map(t => t.rcommndrId));
                      const txnKeySet = new Set(allTxnsCache.map(t => `${t.date}|${Number(t.amount)}|${t.sender === 'achintya' ? 'dr' : 'cr'}`));
                      const count = recRows.filter(r => {
                        const amt = Math.max(Number(r.withdrawal) || 0, Number(r.deposit) || 0);
                        if (amt <= RECOMMENDER_THRESHOLD || bookedIds.has(r.id)) return false;
                        const dir = Number(r.withdrawal) > 0 ? 'dr' : 'cr';
                        return !txnKeySet.has(`${r.date}|${amt}|${dir}`);
                      }).length;
                      return count > 0 ? (
                        <span style={{ background: '#e67e22', color: '#fff', fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 10 }}>{count}</span>
                      ) : null;
                    })()}
                  </div>
                )}
                {isAdmin && (
                  <div
                    onClick={() => { setShowUserMenu(false); setTab('statement'); }}
                    style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#111', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <span>Statement</span>
                    {stmtRows && (() => {
                      const count = stmtRows.filter(r => !r.username).length;
                      return count > 0 ? (
                        <span style={{ background: '#e67e22', color: '#fff', fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 10 }}>{count}</span>
                      ) : null;
                    })()}
                  </div>
                )}
                {isAdmin && (
                  <div
                    onClick={() => { setShowUserMenu(false); setTab('adminsettings'); }}
                    style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#111', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                  >
                    Admin Settings
                  </div>
                )}
                {isAdmin && !selectedUsername && (
                  <div
                    onClick={() => { setShowUserMenu(false); setTab('calendar'); }}
                    style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#111', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                  >
                    Slots Calendar
                  </div>
                )}
                {isAdmin && (
                  <div
                    onClick={() => { setShowUserMenu(false); setTab('visits'); }}
                    style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#111', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                  >
                    User Activity
                  </div>
                )}
                <div
                  onClick={() => { setShowUserMenu(false); setTab('submit'); }}
                  style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#111', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span>{isAdmin ? 'Pending Transactions' : 'Record a Transaction'}</span>
                  {isAdmin && transactions.filter(t => t.status === 'pending').length > 0 && (
                    <span style={{ background: '#e67e22', color: '#fff', fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 10 }}>
                      {transactions.filter(t => t.status === 'pending').length}
                    </span>
                  )}
                </div>
                <div
                  onClick={() => { setShowUserMenu(false); setTab('bank'); }}
                  style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#111', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                >
                  Bank Account
                </div>
                <div
                  onClick={() => { setShowUserMenu(false); setTab('settings'); }}
                  style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#111', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                >
                  Settings
                </div>
                <div
                  onClick={() => {
                    setShowUserMenu(false);
                    authListenerSkipRef.current = false;
                    // Explicit logout is the ONE way a secret/passkey session actually ends —
                    // clear the cached uid pointer so a reload doesn't restore it.
                    try { localStorage.removeItem(SECRET_SESSION_KEY); } catch (e) {}
                    auth.signOut();
                    setUser(null); setPhone(''); setOtp(''); setCountryCode('+91');
                    setLoginState('login'); setTransactions([]);
                    setBank({ name: '', accountNumber: '', ifsc: '', type: 'savings' });
                    setSecretMode(false); setSecretPhone('');
                  }}
                  style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#eb4d4b', cursor: 'pointer' }}
                >
                  Logout
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ maxWidth: 600, margin: '0 auto', padding: '20px 16px' }}>

          {/* ---- Fundraiser (homepage for all logged-in users) ---- */}
          {tab === 'fundraiser' && (() => {
            const FUNDRAISE_TARGET = TOTAL_LIABILITY_PRINCIPAL; // ₹10 Cr envelope (₹5 Cr Class A anchor + ₹5 Cr Class B cap)
            const daysFromEpoch = daysSinceEpoch();
            const currentMultiplier = cciMultiplier(daysFromEpoch);
            // Raised = locked ₹5 Cr foundational anchor (Class A) + aggregated Class B principal
            const totalPrincipalRupees = ANCHOR_PRINCIPAL + classBPrincipalRupees;
            // Current value: anchor accrues 1 PPD simple interest, Class B at 2% / 30d compound
            const currentCciValue = ANCHOR_PRINCIPAL * siMultiplier(daysFromEpoch) + classBPrincipalRupees * currentMultiplier;
            const maturityDays = 6300;
            const daysRemaining = Math.max(0, maturityDays - daysFromEpoch);
            const yearsRemaining = (daysRemaining / 365.25).toFixed(1);
            const progressPct = Math.min(100, (daysFromEpoch / maturityDays) * 100);

            return (
              <div>
                {/* Hero */}
                <div style={{
                  background: 'linear-gradient(135deg, #111 0%, #1a1a2e 50%, #16213e 100%)',
                  borderRadius: 20, padding: '32px 24px', color: '#fff', marginBottom: 20,
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.5, marginBottom: 8 }}>Active Fundraiser</div>
                  <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 4 }}>2% per 30 days</div>
                  <div style={{ fontSize: 13, opacity: 0.6, marginBottom: 20 }}>Compound interest, converging to 64{'\u00D7'} at maturity</div>

                  <div style={{ display: 'flex', justifyContent: 'center', gap: 32, marginBottom: 24 }}>
                    <div>
                      <div style={{ fontSize: 24, fontWeight: 700 }}>64{'\u00D7'}</div>
                      <div style={{ fontSize: 10, opacity: 0.5 }}>At Maturity</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 24, fontWeight: 700 }}>{currentMultiplier.toFixed(1)}{'\u00D7'}</div>
                      <div style={{ fontSize: 10, opacity: 0.5 }}>Current</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 24, fontWeight: 700 }}>{yearsRemaining}y</div>
                      <div style={{ fontSize: 10, opacity: 0.5 }}>Remaining</div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 6, height: 8, marginBottom: 8 }}>
                    <div style={{
                      width: progressPct + '%', height: '100%', borderRadius: 6,
                      background: 'linear-gradient(90deg, #ffd93d, #27ae60)',
                    }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, opacity: 0.4 }}>
                    <span>Deal start</span>
                    <span>2036 backstop</span>
                  </div>
                </div>

                {/* Amount raised vs target */}
                {(() => {
                  const raisedPct = Math.min(100, (totalPrincipalRupees / FUNDRAISE_TARGET) * 100);
                  return (
                    <div style={{
                      background: '#fff', borderRadius: 16, padding: '24px 20px', marginBottom: 16,
                      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                        <div style={{ fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: '#888' }}>Raised so far</div>
                        <div style={{ fontSize: 12, color: '#888' }}>{raisedPct.toFixed(1)}%</div>
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 800, color: '#111', letterSpacing: -1 }}>
                        {formatINR(totalPrincipalRupees)}
                      </div>
                      <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>
                        of {formatINR(FUNDRAISE_TARGET)} target
                      </div>
                      <div style={{ background: '#f0f0ee', borderRadius: 6, height: 10 }}>
                        <div style={{
                          width: raisedPct + '%', height: '100%', borderRadius: 6,
                          background: 'linear-gradient(90deg, #27ae60, #16213e)', minWidth: raisedPct > 0 ? 6 : 0,
                        }} />
                      </div>
                      <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #f5f5f5', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <div style={{ fontSize: 12, color: '#888' }}>Current value at {(currentCciValue / totalPrincipalRupees).toFixed(1)}{'×'}</div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#27ae60' }}>{formatINR(currentCciValue)}</div>
                      </div>
                    </div>
                  );
                })()}

                {/* The convergence story */}
                <div style={{
                  background: '#fff', borderRadius: 16, padding: '24px 20px', marginBottom: 16,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                }}>
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12, color: '#111' }}>How it works</div>
                  <div style={{ fontSize: 13, color: '#444', lineHeight: 1.8 }}>
                    Your investment compounds at <strong>2% every 30 days</strong>. This is equivalent to
                    earning <strong>1% per day</strong> simple interest over the full maturity period of 17.25 years.
                    Both rates converge at exactly <strong>64{'\u00D7'}</strong> your principal at maturity.
                  </div>
                  <div style={{ margin: '16px 0', padding: '16px', background: '#f9f9f6', borderRadius: 12 }}>
                    <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>The convergence</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: '#888' }}>Simple Interest</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#111' }}>1% / day</div>
                      </div>
                      <div style={{ fontSize: 20, color: '#ccc' }}>=</div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: '#888' }}>Compound Interest</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#111' }}>2% / 30 days</div>
                      </div>
                      <div style={{ fontSize: 20, color: '#ccc' }}>=</div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: '#888' }}>At Maturity</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#27ae60' }}>64{'\u00D7'}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Doubling timeline */}
                <div style={{
                  background: '#fff', borderRadius: 16, padding: '24px 20px', marginBottom: 16,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                }}>
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: '#111' }}>Doubling timeline</div>
                  {[1,2,3,4,5,6].map(i => {
                    const doublingDays = Math.round(1050 * i);
                    const doublingYears = (doublingDays / 365.25).toFixed(1);
                    const multiplier = Math.pow(2, i);
                    const reached = daysFromEpoch >= doublingDays;
                    return (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0',
                        borderBottom: i < 6 ? '1px solid #f5f5f5' : 'none',
                        opacity: reached ? 1 : 0.4,
                      }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: reached ? '#27ae60' : '#eee', color: reached ? '#fff' : '#aaa', fontSize: 11, fontWeight: 700,
                        }}>{reached ? '\u2713' : i}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>{multiplier}{'\u00D7'}</div>
                          <div style={{ fontSize: 11, color: '#888' }}>~{doublingYears} years</div>
                        </div>
                        <div style={{ fontSize: 12, color: '#888' }}>
                          {'\u20B9'}1L {'\u2192'} {'\u20B9'}{multiplier}L
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* CTA */}
                <div style={{
                  background: '#fff', borderRadius: 16, padding: '24px 20px', marginBottom: 16,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)', textAlign: 'center',
                }}>
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8, color: '#111' }}>Invest now</div>
                  <div style={{ fontSize: 13, color: '#666', marginBottom: 16, lineHeight: 1.6 }}>
                    Contact Achintya to participate in the active fundraiser at 2% per 30-day compound interest.
                  </div>
                  <button style={{
                    background: '#111', color: '#fff', border: 'none', borderRadius: 10,
                    padding: '14px 32px', fontSize: 14, fontWeight: 700, cursor: 'default',
                    width: '100%',
                  }}>
                    Get in touch
                  </button>
                </div>
              </div>
            );
          })()}

          {/* ---- Balance Card (transactions tab only) ----
                Same component for every viewer — no separate overlay card. When viewingWallets
                is true (declared above), the `transactions` it's built from is sourced from RTDB
                /wallets instead of Firestore, but the card itself is unmodified. ---- */}
          {tab === 'transactions' && (
          <div style={{
            background: 'linear-gradient(135deg, #111 0%, #333 100%)',
            borderRadius: 16, padding: '24px 20px', color: '#fff', marginBottom: 20,
          }}>
            {walletName && (
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: walletPhone ? 2 : 8, opacity: 0.9, display: 'flex', alignItems: 'center', gap: 8 }}>
                {walletName}
                {txnLoading && <span className="sync-spinner" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} />}
              </div>
            )}
            {walletPhone && (
              <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 8 }}>{walletPhone}</div>
            )}
            <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 4 }}>Total Balance</div>
            <div style={{ fontSize: 32, fontWeight: 700, letterSpacing: -1 }}>
              {siBalance < 0 ? '-' : ''}{'\u20B9'}{Math.abs(Math.round(siBalance)).toLocaleString('en-IN')}
            </div>
            <div style={{ fontSize: 11, opacity: 0.55, marginTop: 4 }}>at 1% per day simple interest</div>

            <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 11, opacity: 0.5 }}>Invested</div>
                <div style={{ fontSize: 15, fontWeight: 500, color: '#6bdb8a' }}>
                  +{'\u20B9'}{totalIn.toLocaleString('en-IN')}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, opacity: 0.5 }}>Withdrawn</div>
                <div style={{ fontSize: 15, fontWeight: 500, color: '#ff6b6b' }}>
                  -{'\u20B9'}{totalOut.toLocaleString('en-IN')}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, opacity: 0.5 }}>Principal</div>
                <div style={{ fontSize: 15, fontWeight: 500, color: '#7ec8ff' }}>
                  {'\u20B9'}{netPrincipal.toLocaleString('en-IN')}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, opacity: 0.5 }}>Interest (2 PPM CCI)</div>
                <div style={{ fontSize: 15, fontWeight: 500, color: '#ffd93d' }}>
                  +{'\u20B9'}{cciProfit.toLocaleString('en-IN')}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, opacity: 0.5 }}>Interest (1 PPD SI)</div>
                <div style={{ fontSize: 15, fontWeight: 500, color: '#aaa' }}>
                  +{'\u20B9'}{interest.toLocaleString('en-IN')}
                </div>
              </div>
              {isAdmin && (
              <div>
                <div style={{ fontSize: 11, opacity: 0.5 }}>Admin (1% CCI→SI)</div>
                <div style={{ fontSize: 15, fontWeight: 500, color: '#c4a35a' }}>
                  +{'\u20B9'}{adminProfit.toLocaleString('en-IN')}
                </div>
              </div>
              )}
            </div>
          </div>
          )}

          {/* ---- 3 Balance Sections (users only, on Wallet tab) ----
                Total Balance (SI) = Principal + CCI Interest + Buyback (SI excess)
                Principal: active withdraw
                CCI Interest: active withdraw
                Buyback (SI excess): greyed out — AppBru share buyback opens shortly; 2036 is the legal backstop */}
          {tab === 'transactions' && (() => {
            // Same wallet-withdraw cards for everyone — admin searching a user sees exactly
            // what that user would see on their own wallet screen, and can submit on their
            // behalf the same way the user could themselves (per "admin sees exactly what the
            // user sees of their main wallet screen"). Everything here keys off viewedUsername
            // (the searched user, or the logged-in user themselves when there's no selection —
            // see its declaration above), never myUsername directly, so a request submitted
            // while admin is viewing someone else's wallet is correctly attributed to THAT user,
            // not to the admin's own account.
            //
            // Group the viewed user's pending withdrawal requests by the wallet bucket they came
            // from, so each section can show "X pending — Achintya will review shortly".
            // profitLeverage's pending list comes straight from pendingWithdrawalRequests (RTDB —
            // see its declaration), not from `transactions`, since that request type no longer
            // lives in Firestore at all.
            const pendingBySource = { principal: [], cciInterest: [], buyback: [], profitLeverage: pendingWithdrawalRequests };
            transactions.forEach((t) => {
              if (t.status !== 'pending') return;
              if (t.sender !== 'achintya' || t.receiver !== viewedUsername) return;
              if (pendingBySource[t.source]) {
                pendingBySource[t.source].push(t);
              }
            });

            const renderSection = (opts) => {
              const { key, title, subtitle, amount, color, mode, disabled, disabledReason } = opts;
              const isThisFormOpen = showWalletWithdraw && walletWMode === mode;
              const pendingList = pendingBySource[mode] || [];
              const pendingTotal = pendingList.reduce((acc, t) => acc + (Number(t.amount) || 0), 0);
              const pendingPledgeTotal = pendingList.reduce((acc, t) => acc + (Number(t.pledgePrincipalAmount) || 0), 0);
              const hasPending = pendingList.length > 0;
              const remainingClaimable = Math.max(0, amount - pendingTotal);
              const canOpen = !disabled && remainingClaimable > 0 && !showWalletWithdraw;
              return (
                <div key={key} style={{
                  background: '#fff', borderRadius: 12, padding: 18, marginBottom: 14,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  border: `1px solid ${disabled ? '#eee' : '#f0e7c8'}`,
                  opacity: disabled ? 0.85 : 1,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: color, letterSpacing: 0.3, textTransform: 'uppercase' }}>
                        {title}
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 800, color: '#111', marginTop: 4, letterSpacing: -0.5 }}>
                        {'₹'}{Math.round(amount).toLocaleString('en-IN')}
                      </div>
                      {subtitle && (
                        <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>{subtitle}</div>
                      )}
                    </div>
                    <button
                      disabled={!canOpen}
                      onClick={() => {
                        if (!canOpen) return;
                        setWalletWMode(mode);
                        setShowWalletWithdraw(true);
                        setWalletWStatus(''); setWalletWAmount('');
                      }}
                      style={{
                        background: canOpen ? '#111' : '#e5e5e5',
                        color: canOpen ? '#fff' : '#999',
                        border: 'none', borderRadius: 8, padding: '10px 18px',
                        fontSize: 13, fontWeight: 700,
                        cursor: canOpen ? 'pointer' : 'not-allowed',
                        whiteSpace: 'nowrap',
                      }}
                    >{hasPending && canOpen ? 'Request more' : 'Withdraw'}</button>
                  </div>

                  {hasPending && (
                    <div style={{
                      marginTop: 12, background: '#fff8e6', border: '1px solid #f3dfa0', borderRadius: 8,
                      padding: '10px 12px', fontSize: 12, color: '#7a5a10', lineHeight: 1.5,
                    }}>
                      <div style={{ fontWeight: 700, marginBottom: 2 }}>
                        {pendingList.length === 1 ? '1 withdrawal request pending' : `${pendingList.length} withdrawal requests pending`}
                        {' · '}{formatINR(pendingTotal)}
                      </div>
                      {pendingPledgeTotal > 0 && (
                        <div style={{ fontWeight: 700, marginBottom: 2 }}>
                          {formatINR(pendingPledgeTotal)} of your principal withheld/pledged until reviewed
                        </div>
                      )}
                      <div style={{ opacity: 0.85 }}>
                        Achintya will review shortly. Track status in the Transactions list below.
                      </div>
                    </div>
                  )}

                  {disabled && disabledReason && (
                    <div style={{ marginTop: 12, background: '#f9f9f6', borderRadius: 8, padding: '10px 12px', fontSize: 11.5, color: '#666', lineHeight: 1.55 }}>
                      {disabledReason}
                    </div>
                  )}

                  {isThisFormOpen && (
                    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #f0f0f0' }}>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
                        Withdrawing from <strong>{title}</strong>. Max claimable now: <strong>{formatINR(remainingClaimable)}</strong>
                        {hasPending && ` (${formatINR(pendingTotal)} already pending)`}.
                      </div>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder={`Up to ${formatINR(remainingClaimable)}`}
                        value={walletWAmount}
                        onChange={(e) => {
                          const cleaned = e.target.value.replace(/[^0-9]/g, '');
                          // Hard-cap input at the remaining claimable so a user can never type more.
                          const capped = cleaned && Number(cleaned) > remainingClaimable
                            ? String(Math.floor(remainingClaimable))
                            : cleaned;
                          setWalletWAmount(capped);
                        }}
                        style={{
                          width: '100%', boxSizing: 'border-box',
                          padding: 12, borderRadius: 8, border: '1.5px solid #111',
                          fontSize: 15, fontWeight: 700, color: '#111', background: '#fff',
                        }}
                      />
                      {Number(walletWAmount) > 0 && (
                        <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                          {formatINR(Number(walletWAmount))}
                        </div>
                      )}
                      {/* Transparency: show the exact pledge amount this request will hold
                          against principal before they submit, same principle as the Admin
                          Settings preview — never a surprise what's about to be created. The
                          ₹5L minimum itself is only surfaced as a rejection message (via
                          walletWStatus, in handleWalletWithdraw) if they actually try to submit
                          less — not shown pre-emptively while they're still typing. */}
                      {mode === 'profitLeverage' && Number(walletWAmount) > 0 && (
                        <div style={{
                          marginTop: 8, background: '#fff3e0', border: '1px solid #f3dfa0', color: '#7a5a10',
                          borderRadius: 8, padding: '8px 12px', fontSize: 11.5, fontWeight: 600, lineHeight: 1.5,
                        }}>
                          {formatINR(Math.min(netPrincipal, Math.round(Number(walletWAmount) / 5)))} of your principal will be pledged/held until this request is approved or rejected.
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                        <button
                          onClick={() => { setShowWalletWithdraw(false); setWalletWAmount(''); setWalletWStatus(''); }}
                          style={{
                            flex: 1, background: '#fff', color: '#111',
                            border: '1.5px solid #eee', borderRadius: 8, padding: '11px',
                            fontSize: 13, fontWeight: 700, cursor: 'pointer',
                          }}
                        >Cancel</button>
                        <button
                          onClick={handleWalletWithdraw}
                          style={{
                            flex: 2, background: '#111', color: '#fff',
                            border: 'none', borderRadius: 8, padding: '11px',
                            fontSize: 13, fontWeight: 700, cursor: 'pointer',
                          }}
                        >Request withdrawal</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            };
            return (
              <div style={{ marginBottom: 20 }}>
                {renderSection({
                  key: 'principal',
                  title: 'Principal',
                  subtitle: 'Your remaining invested principal — accrues 1% per day simple interest',
                  // Excludes any currently-held pledge (heldPledgeAmount) — see its declaration.
                  amount: Math.max(0, netPrincipal - heldPledgeAmount),
                  color: '#27ae60',
                  mode: 'principal',
                  disabled: true,
                  disabledReason: 'Direct principal withdrawal is disabled — Profit Withdrawal (up to 5× Net Principal) below is the only enabled withdrawal method for now.',
                })}
                {renderSection({
                  key: 'cci',
                  title: 'Net 2% per 30-day CCI Interest',
                  subtitle: 'Legally payable interest accrued on principal',
                  amount: cciProfit,
                  color: '#a07c1c',
                  mode: 'cciInterest',
                  disabled: true,
                  disabledReason: 'CCI interest withdrawal is disabled — Profit Withdrawal (up to 5× Net Principal) below is the only enabled withdrawal method for now.',
                })}
                {renderSection({
                  key: 'buyback',
                  title: 'Buyback (SI − CCI gap)',
                  subtitle: 'Notional SI excess over CCI — AppBru share buyback opens shortly',
                  amount: siExcess,
                  color: '#888',
                  mode: 'buyback',
                  disabled: true,
                  disabledReason: "Achintya can't legally pay more than 2% per 30-day CCI in cash. This excess is settled via a buyback from the principal shares of AppBru Pvt Ltd, which opens shortly — your SI balance will begin to be claimable then.",
                })}
                {/* Self-service leveraged profit withdrawal — always shown (like Buyback above),
                    greyed out with an explanation below the ₹1,00,000 net-principal threshold
                    instead of disappearing entirely, so it's visible but explained why it isn't
                    usable yet rather than looking like a missing feature. */}
                {renderSection({
                  key: 'profitLeverage',
                  title: 'Profit Withdrawal (up to 5× Net Principal)',
                  subtitle: 'A leveraged withdrawal against future profit — a portion of your principal is pledged/held until Achintya approves or rejects it.',
                  amount: netPrincipal * 5,
                  color: '#6c5ce7',
                  mode: 'profitLeverage',
                  disabled: netPrincipal < PROFIT_WITHDRAWAL_MIN_PRINCIPAL,
                  disabledReason: `Minimum withdrawal is ${formatINR(PROFIT_WITHDRAWAL_MIN_AMOUNT)}.`,
                })}

                {walletWStatus && (
                  <div style={{
                    marginTop: 6, padding: '10px 12px', borderRadius: 8, fontSize: 12,
                    background: walletWStatus.toLowerCase().includes('submitted') ? '#e8f8ee' : '#fde8e8',
                    color: walletWStatus.toLowerCase().includes('submitted') ? '#27ae60' : '#eb4d4b',
                    fontWeight: 600,
                  }}>
                    {walletWStatus}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ---- Transactions Tab ----
                Same form/list for every viewer, whether the underlying `transactions` came from
                Firestore or (when viewingWallets) RTDB /wallets — see handleAddTransaction and
                the Delete button below for the write-side branch on each row's `origin`. */}
          {tab === 'transactions' && (
            <div>
              {/* Transaction form — admin only, edit/delete an EXISTING row only (click one below
                  to open it here). Adding a brand-new transaction from this screen is disabled —
                  this wallet view stays a pure mirror of what the user themselves sees; a fresh
                  ledger correction is added from the Pending Transactions tab instead (admin-
                  only "Add Transaction" section there), not mixed into this shared view. Gated on
                  editingTxnId so there's no path to a blank/new form here; handleAddTransaction
                  (still used for Save here) already resolves targetUsername = selectedUsername ||
                  myUsername, and writes to RTDB /wallets/<username> instead of Firestore when
                  viewingWallets. */}
              {isAdmin && editingTxnId && (<div style={{
                background: '#fff', borderRadius: 12, padding: 16, marginBottom: 16,
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  <input type="text" inputMode="numeric" placeholder="Amount" value={txnAmount}
                    onChange={(e) => { const v = e.target.value; if (/^\d*$/.test(v)) setTxnAmount(v); }}
                    style={{ ...inputStyle, width: '50%' }} />
                  <div style={{
                    display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1.5px solid #111',
                  }}>
                    <div
                      onClick={() => setTxnType('invest')}
                      style={{
                        padding: '10px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600,
                        background: txnType === 'invest' ? '#e8f8ee' : '#fafafa',
                        color: txnType === 'invest' ? '#27ae60' : '#aaa',
                      }}
                    >Invest</div>
                    <div
                      onClick={() => setTxnType('withdraw')}
                      style={{
                        padding: '10px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600,
                        background: txnType === 'withdraw' ? '#fde8e8' : '#fafafa',
                        color: txnType === 'withdraw' ? '#eb4d4b' : '#aaa',
                        borderLeft: '1.5px solid #111',
                      }}
                    >Withdraw</div>
                    {/* Reversal isn't a concept in the /wallets schema (credit/debit only) — hide
                        it while editing/adding a wallets-sourced row. */}
                    {!viewingWallets && (
                      <div
                        onClick={() => setTxnType('reversal')}
                        style={{
                          padding: '10px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600,
                          background: txnType === 'reversal' ? '#fff3e0' : '#fafafa',
                          color: txnType === 'reversal' ? '#e67e22' : '#aaa',
                          borderLeft: '1.5px solid #111',
                        }}
                      >Reversed</div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                  <DatePicker
                    selected={txnDate}
                    onChange={(date) => setTxnDate(date)}
                    dateFormat="dd MMM yyyy"
                    maxDate={new Date()}
                    showYearDropdown
                    showMonthDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={10}
                    customInput={
                      <input style={{ ...inputStyle, width: 140, cursor: 'pointer' }} readOnly />
                    }
                  />
                  {/* /wallets rows have no status concept — every row there is an already-settled
                      credit/debit (handleAddTransaction ignores txnFormStatus for them). Show a
                      static note instead of the pending/successful/rejected toggle. */}
                  {viewingWallets ? (
                    <div style={{
                      flex: 1, textAlign: 'center', padding: '10px 0', borderRadius: 8, fontSize: 11, fontWeight: 600,
                      background: '#e8f8ee', color: '#27ae60', border: '1.5px solid #111',
                    }}>Wallets entry — always Successful</div>
                  ) : (
                    <div style={{
                      display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1.5px solid #111', flex: 1,
                    }}>
                      {['pending', 'successful', 'rejected'].map((s, idx) => (
                        <div key={s}
                          onClick={() => setTxnFormStatus(s)}
                          style={{
                            flex: 1, textAlign: 'center', padding: '10px 0', fontSize: 11, cursor: 'pointer', fontWeight: 600,
                            background: txnFormStatus === s
                              ? (s === 'successful' ? '#e8f8ee' : s === 'rejected' ? '#fde8e8' : '#fff3e0')
                              : '#fafafa',
                            color: txnFormStatus === s
                              ? (s === 'successful' ? '#27ae60' : s === 'rejected' ? '#eb4d4b' : '#e67e22')
                              : '#aaa',
                            borderLeft: idx > 0 ? '1.5px solid #111' : 'none',
                          }}
                        >{s.charAt(0).toUpperCase() + s.slice(1)}</div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button onClick={handleAddTransaction} style={{ ...btnStyle, flex: 1 }}>
                    {editingTxnId ? 'Save' : 'Add'}
                  </button>
                  {editingTxnId && (
                    <>
                    <button onClick={() => { setEditingTxnId(null); setTxnAmount(''); setTxnType('invest'); setTxnDate(new Date()); setTxnFormStatus('pending'); setTxnStatus(''); }}
                      style={{ padding: '10px 14px', borderRadius: 8, border: '1.5px solid #111', background: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                      Cancel
                    </button>
                    <button onClick={() => {
                      if (window.confirm('Delete this transaction?')) {
                        const existing = transactions.find(t => t.id === editingTxnId);
                        if (existing && existing.origin === 'wallets') {
                          const existingUsername = existing.sender !== 'achintya' ? existing.sender : existing.receiver;
                          runTransaction(ref(rtdb, `wallets/${existingUsername}`), (current) => {
                            if (!current) return current;
                            const txns = { ...(current.txns || {}) };
                            delete txns[existing.walletKey];
                            return { ...current, txns, summary: recomputeRtdbWalletSummary(txns) };
                          }).catch(e => { console.error(e); setTxnStatus('Error deleting'); });
                        } else {
                          deleteDoc(doc(db, 'transactions', editingTxnId)).catch(e => { console.error(e); setTxnStatus('Error deleting'); });
                        }
                        setEditingTxnId(null); setTxnAmount(''); setTxnType('invest'); setTxnDate(new Date()); setTxnFormStatus('pending'); setTxnStatus('');
                      }
                    }}
                      style={{ padding: '10px 14px', borderRadius: 8, border: '1.5px solid #eb4d4b', background: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600, color: '#eb4d4b' }}>
                      Delete
                    </button>
                    </>
                  )}
                </div>
                {txnStatus && (
                  <div style={{ fontSize: 12, marginTop: 8, color: txnStatus === 'Error saving' ? '#eb4d4b' : '#27ae60' }}>
                    {txnStatus}
                  </div>
                )}
              </div>)}

              {/* Transaction list \u2014 merges the settled/legacy-pending `transactions` with RTDB
                  withdrawalRequests (visibleWithdrawalRequests: pending or processing), so a
                  Profit Withdrawal request OR a processing refund shows up in this same list, not
                  just in the card's pending banner above. Serial numbers (#N) only apply to the
                  original `transactions` rows (matches the existing wallet serial scheme); a
                  withdrawal-request row shows its status badge instead. */}
              {(() => {
                const requestRows = visibleWithdrawalRequests.map(r => ({
                  id: `wr-${r.id}`,
                  sender: 'achintya',
                  receiver: viewedUsername,
                  amount: r.amount,
                  date: r.date,
                  status: r.status,
                  pledgeStatus: r.pledgeStatus,
                  pledgePrincipalAmount: r.pledgePrincipalAmount,
                  ledgerRecordedSeparately: r.ledgerRecordedSeparately,
                  origin: 'withdrawalRequest',
                }));
                const serialById = new Map(transactions.map((t, i) => [t.id, transactions.length - i]));
                const displayTransactions = [...transactions, ...requestRows].sort((a, b) => b.date.localeCompare(a.date));
                return (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111', margin: '4px 4px 10px', letterSpacing: 0.3, textTransform: 'uppercase' }}>
                      Transactions
                    </div>
                    <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                      {txnLoading && displayTransactions.length === 0 && (
                        <div style={{ padding: 24, textAlign: 'center', color: '#bbb', fontSize: 13 }}>Loading...</div>
                      )}
                      {!txnLoading && displayTransactions.length === 0 && (
                        <div style={{ padding: 24, textAlign: 'center', color: '#bbb', fontSize: 13 }}>No transactions yet</div>
                      )}
                      {displayTransactions.map((t, i) => {
                        const isWithdrawalRequest = t.origin === 'withdrawalRequest';
                        const isWithdraw = t.sender === 'achintya';
                        const isReversal = t.type === 'reversal' || t.remarks === 'Reversed';
                        const txnIndex = serialById.get(t.id);
                        const txnColor = isReversal ? '#e67e22' : isWithdraw ? '#eb4d4b' : '#27ae60';
                        return (
                          <div key={t.id} onClick={() => {
                            if (isAdmin && !t.syncing && !isWithdrawalRequest) {
                              setEditingTxnId(t.id);
                              setTxnAmount(String(t.amount));
                              setTxnType(t.type === 'reversal' || t.remarks === 'Reversed' ? 'reversal' : t.sender === 'achintya' ? 'withdraw' : 'invest');
                              setTxnDate(new Date(t.date));
                              setTxnFormStatus(t.status || 'pending');
                              setTxnStatus('');
                            }
                          }} style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            padding: '14px 16px',
                            borderBottom: i < displayTransactions.length - 1 ? '1px solid #f0f0f0' : 'none',
                            opacity: t.syncing ? 0.5 : 1,
                            cursor: isAdmin && !isWithdrawalRequest ? 'pointer' : 'default',
                            background: editingTxnId === t.id ? '#f8f8ff' : 'transparent',
                            borderLeft: `3px solid ${txnColor}`,
                          }}>
                            {t.syncing && <span className="sync-spinner" />}
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: 16, fontWeight: 700, color: '#111' }}>
                                  {txnIndex != null && <span style={{ fontSize: 11, color: '#bbb', fontWeight: 500, marginRight: 6 }}>#{txnIndex}</span>}
                                  {isWithdraw ? '-' : '+'}{'\u20B9'}{Number(t.amount).toLocaleString('en-IN')}
                                </span>
                                <span style={{ fontSize: 11, color: '#999' }}>
                                  {new Date(t.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                                <span style={{ fontSize: 11, color: txnColor, fontWeight: 600 }}>
                                  {isWithdrawalRequest ? (t.ledgerRecordedSeparately ? 'Refund' : 'Profit Withdrawal') : isReversal ? 'Reversed' : isWithdraw ? 'Withdrawn' : 'Invested'}
                                </span>
                                {t.status === 'successful' ? (
                                  <span style={{ fontSize: 10, color: '#bbb' }}>
                                    {Math.max(0, Math.floor((new Date() - new Date(t.date)) / (1000 * 60 * 60 * 24)))}d ago
                                  </span>
                                ) : t.status === 'processing' ? (
                                  <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: '#e3f0fd', color: '#1565c0' }}>
                                    Processing
                                  </span>
                                ) : (
                                  <span style={{
                                    fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                                    background: (t.status === 'rejected' || t.status === 'failed') ? '#fde8e8' : '#fff3e0',
                                    color: (t.status === 'rejected' || t.status === 'failed') ? '#eb4d4b' : '#e67e22',
                                  }}>
                                    {t.status === 'rejected' ? 'Rejected' : t.status === 'failed' ? 'Failed' : 'Pending'}
                                  </span>
                                )}
                                {t.pledgeStatus === 'held' && (
                                  <span style={{
                                    fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                                    background: '#f3e8d8', color: '#7a5a10',
                                  }} title={`${formatINR(t.pledgePrincipalAmount)} pledged from principal, held until this resolves`}>
                                    {formatINR(t.pledgePrincipalAmount)} pledged
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* ---- Bank Account (fireact-main onboarding UI) ---- */}
          {tab === 'bank' && (() => {
            const STEPS = ['Agreement', 'Your info', 'Bank details', 'Linked'];
            return (
              <div className="ob-page" style={{ padding: 0, minHeight: 'auto' }}>
                <div className="ob-container">
                  <div
                    onClick={() => setTab('transactions')}
                    style={{ fontSize: 13, cursor: 'pointer', color: '#888', marginBottom: 16 }}
                  >
                    &larr; Back
                  </div>

                  <h1 className="ob-title">Link your account</h1>
                  <p className="ob-subtitle">Set up your bank account for payouts in a few simple steps.</p>

                  <div className="ob-steps">
                    {STEPS.map((label, i) => (
                      <div key={i} className={`ob-step ${i < linkStep ? 'ob-step-done' : ''} ${i === linkStep ? 'ob-step-active' : ''}`}>
                        <div className="ob-step-dot">{i < linkStep ? '\u2713' : i + 1}</div>
                        <span className="ob-step-label">{label}</span>
                      </div>
                    ))}
                  </div>

                  {/* Step 0: NDA Agreement */}
                  {linkStep === 0 && (
                    <div className="ob-card">
                      <h3>Review and accept</h3>
                      <p className="ob-card-desc">Before linking your account, please review and accept our agreement.</p>

                      <div style={{ border: '1px solid #e8e8e2', borderRadius: 12, padding: 18, marginBottom: 20, maxHeight: 400, overflowY: 'auto' }}>
                        <div style={{ textAlign: 'center', marginBottom: 16, borderBottom: '2px solid #111', paddingBottom: 12 }}>
                          <div style={{ fontSize: 11, color: '#888', letterSpacing: 2, textTransform: 'uppercase' }}>Confidential</div>
                          <div style={{ fontSize: 16, fontWeight: 800, color: '#111', marginTop: 4 }}>Non-Disclosure Agreement</div>
                        </div>
                        <div className="nda-body">
                          <p>This Non-Disclosure Agreement ("<strong>Agreement</strong>") is entered into by and between:</p>
                          <p><strong>Disclosing Party:</strong> Achintya Organisation ("Achintya"), a private entity engaged in investment management, financial technology, and related services.</p>
                          <p><strong>Receiving Party:</strong> The undersigned individual ("<strong>User</strong>"), identified by their registered phone number and name provided below.</p>
                          <p>Collectively referred to as the "<strong>Parties</strong>".</p>
                          <h4>1. Purpose</h4>
                          <p>The User acknowledges that in connection with their use of the Achintya platform and participation in investment activities, they may receive or have access to Confidential Information belonging to Achintya. This Agreement is intended to protect such Confidential Information from unauthorized disclosure.</p>
                          <h4>2. Definition of Confidential Information</h4>
                          <p>"<strong>Confidential Information</strong>" shall include, but is not limited to:</p>
                          <ul>
                            <li>The User's own transaction records, account balances, and banking details on the platform;</li>
                            <li>Investment terms, interest rates, and settlement mechanics disclosed to the User;</li>
                            <li>Any materials, communications, reports, or analyses shared with the User through the platform;</li>
                            <li>Any information marked or reasonably understood to be confidential.</li>
                          </ul>
                          <h4>2A. Interest Rate Disclosure</h4>
                          <p>The User acknowledges and agrees that any <strong>1% per day simple interest ("1 PPD SI")</strong> reflected on the platform in respect of the User's remaining principal shall be maintained and honoured until <strong>after the year 2036</strong>, being the legal backstop date (epoch + 6,300 days) under the original 17.25-year deal on which the foundational investor's <strong>2% per 30-day compounded interest rate ("2% CCI")</strong> curve converges with their 1 PPD SI at 64&times; principal. The User's full SI Total Balance is realisable through a buyback of <strong>AppBru Pvt Ltd</strong> shares at the currently accepted company valuation, which opens shortly &mdash; the 2036 backstop is the security floor, not the expected payout date.</p>
                          <p>The User shall not disclose the terms, rates, or settlement mechanics of their engagement with Achintya to any third party without prior written consent.</p>
                          <h4>3. Obligations of the Receiving Party</h4>
                          <p>The User agrees to:</p>
                          <ul>
                            <li>Hold all Confidential Information in strict confidence;</li>
                            <li>Not disclose, publish, or disseminate Confidential Information to any third party without prior written consent from Achintya;</li>
                            <li>Not use Confidential Information for any purpose other than their engagement with the Achintya platform;</li>
                            <li>Take all reasonable measures to prevent unauthorized access to or disclosure of Confidential Information;</li>
                            <li>Immediately notify Achintya of any unauthorized disclosure or breach of this Agreement.</li>
                          </ul>
                          <h4>4. Exclusions</h4>
                          <p>Confidential Information does not include information that:</p>
                          <ul>
                            <li>Is or becomes publicly available through no fault of the User;</li>
                            <li>Was already known to the User prior to disclosure, as evidenced by written records;</li>
                            <li>Is independently developed by the User without reference to Confidential Information;</li>
                            <li>Is required to be disclosed by law, regulation, or court order, provided that the User gives Achintya prompt written notice and cooperates to minimize the scope of disclosure.</li>
                          </ul>
                          <h4>5. Intellectual Property</h4>
                          <p>All Confidential Information remains the exclusive property of Achintya.</p>
                          <h4>6. Term and Termination</h4>
                          <p>This Agreement shall be effective from the date of acceptance and shall remain in force for a period of <strong>five (5) years</strong> from the date of last disclosure of Confidential Information, or until terminated by mutual written agreement.</p>
                          <h4>7. Remedies</h4>
                          <p>The User acknowledges that any breach of this Agreement may cause irreparable harm to Achintya. Achintya shall be entitled to seek injunctive relief, specific performance, and any other remedies available at law or in equity.</p>
                          <h4>8. Governing Law</h4>
                          <p>This Agreement shall be governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts located in New Delhi, India.</p>
                        </div>
                      </div>

                      <label className="ob-check" style={{ marginBottom: 16 }}>
                        <input type="checkbox" checked={ndaAgreed} onChange={(e) => setNdaAgreed(e.target.checked)} />
                        <span>I have read and agree to the Non-Disclosure Agreement</span>
                      </label>
                      <button className="ob-next-btn" onClick={() => { if (ndaAgreed) setLinkStep(1); }} disabled={!ndaAgreed}>Continue</button>
                    </div>
                  )}

                  {/* Step 1: Your info */}
                  {linkStep === 1 && (
                    <div className="ob-card">
                      <h3>Your information</h3>
                      <p className="ob-card-desc">We need a few details to set up your account.</p>
                      <div className="ob-field">
                        <label>Full Name</label>
                        <input className="ob-input" placeholder="As per government ID" value={bank.name}
                          onChange={(e) => setBank({ ...bank, name: e.target.value })} />
                      </div>
                      <div className="ob-field">
                        <label>Phone Number</label>
                        <div className="ob-input" style={{ background: '#f9f9f6', color: '#888' }}>{user?.phoneNumber}</div>
                      </div>
                      <div className="ob-btn-row">
                        <button className="ob-back-btn" onClick={() => setLinkStep(0)}>Back</button>
                        <button className="ob-next-btn" onClick={() => {
                          if (!bank.name.trim()) { setBankStatus('Enter your full name'); return; }
                          setBankStatus('');
                          setLinkStep(2);
                        }}>Continue</button>
                      </div>
                      {bankStatus && <div style={{ fontSize: 12, color: '#eb4d4b', marginTop: 8 }}>{bankStatus}</div>}
                    </div>
                  )}

                  {/* Step 2: Bank details */}
                  {linkStep === 2 && (
                    <div className="ob-card">
                      <h3>Link your bank account</h3>
                      <p className="ob-card-desc">Your bank details are used for payouts. They are stored securely.</p>
                      <div className="ob-field">
                        <label>Account number</label>
                        <input className="ob-input" placeholder="Enter account number" value={bank.accountNumber}
                          onChange={(e) => setBank({ ...bank, accountNumber: e.target.value })} inputMode="numeric" />
                      </div>
                      <div className="ob-field">
                        <label>Confirm account number</label>
                        <input className="ob-input" placeholder="Re-enter account number" value={confirmAccountNumber}
                          onChange={(e) => setConfirmAccountNumber(e.target.value)} inputMode="numeric" />
                      </div>
                      <div className="ob-field">
                        <label>IFSC code</label>
                        <input className="ob-input" placeholder="e.g. SBIN0001234" value={bank.ifsc}
                          onChange={(e) => setBank({ ...bank, ifsc: e.target.value })} style={{ textTransform: 'uppercase' }} maxLength={11} />
                      </div>
                      <div className="ob-field">
                        <label>Account type</label>
                        <div style={{ display: 'flex', gap: 8 }}>
                          {['savings', 'current'].map((t) => (
                            <button key={t} onClick={() => setBank({ ...bank, type: t })}
                              className={`ob-next-btn`}
                              style={{
                                background: bank.type === t ? '#0a0a0a' : '#fff',
                                color: bank.type === t ? '#fff' : '#888',
                                border: '1px solid #e8e8e2', padding: '10px 0',
                              }}>
                              {t.charAt(0).toUpperCase() + t.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="ob-security-note">
                        <span className="ob-lock">&#128274;</span>
                        Bank details are encrypted and stored securely. Your full account number is never exposed.
                      </div>
                      <div className="ob-btn-row">
                        <button className="ob-back-btn" onClick={() => setLinkStep(1)}>Back</button>
                        <button className="ob-next-btn" disabled={linkSubmitting} onClick={async () => {
                          if (!bank.accountNumber.trim()) { setBankStatus('Account number is required'); return; }
                          if (bank.accountNumber !== confirmAccountNumber) { setBankStatus("Account numbers don't match"); return; }
                          if (!bank.ifsc.trim() || bank.ifsc.trim().length !== 11) { setBankStatus('IFSC code must be 11 characters'); return; }
                          setBankStatus('');
                          setLinkSubmitting(true);
                          try {
                            const docUsername = (isAdmin && selectedUsername) ? selectedUsername : myUsername;
                            await rtdbSet(ref(rtdb, `users/${docUsername}/bank`), bank);
                            setLinkStep(3);
                            setToast('Bank account linked successfully');
                            setTimeout(() => setToast(''), 3000);
                          } catch (e) {
                            setBankStatus('Error linking account');
                          }
                          setLinkSubmitting(false);
                        }}>
                          {linkSubmitting ? 'Linking...' : 'Link account'}
                        </button>
                      </div>
                      {bankStatus && <div style={{ fontSize: 12, color: '#eb4d4b', marginTop: 8 }}>{bankStatus}</div>}
                    </div>
                  )}

                  {/* Step 3: Done */}
                  {linkStep === 3 && (
                    <div className="ob-card ob-done">
                      <div className="ob-done-icon">&#9989;</div>
                      <h3>Account linked</h3>
                      <p>Your bank account is linked and ready for payouts.</p>
                      <div className="ob-summary">
                        {[
                          { label: 'Account Holder', value: bank.name },
                          { label: 'Account Number', value: bank.accountNumber ? '****' + bank.accountNumber.slice(-4) : '' },
                          { label: 'IFSC Code', value: bank.ifsc?.toUpperCase() },
                          { label: 'Account Type', value: bank.type ? bank.type.charAt(0).toUpperCase() + bank.type.slice(1) : '' },
                        ].map((row, i) => (
                          <div key={i} className="ob-summary-row">
                            <span>{row.label}</span>
                            <span style={{ fontWeight: 600 }}>{row.value}</span>
                          </div>
                        ))}
                      </div>
                      <button className="ob-back-btn" onClick={() => setLinkStep(2)} style={{ display: 'block', width: '100%', marginBottom: 12, textAlign: 'center' }}>Edit details</button>
                      <button className="ob-next-btn" onClick={() => setTab('transactions')} style={{ display: 'block', width: '100%', textAlign: 'center' }}>Back to wallet</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ---- Settings ---- */}
          {tab === 'settings' && (
            <div>
              <div
                onClick={() => setTab('transactions')}
                style={{ fontSize: 13, cursor: 'pointer', color: '#888', marginBottom: 16 }}
              >
                &larr; Back
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: '#111' }}>Settings</div>

              {/* Profile */}
              <div style={{
                background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16,
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, color: '#111' }}>Profile</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0', fontSize: 14 }}>
                  <span style={{ color: '#555' }}>Name</span>
                  <span style={{ fontWeight: 700, color: '#111' }}>{walletName}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontSize: 14 }}>
                  <span style={{ color: '#555' }}>Phone</span>
                  <span style={{ fontWeight: 700, color: '#111' }}>{user.phoneNumber}</span>
                </div>
              </div>

              {/* FAQ — Understanding your returns */}
              <div style={{
                background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16,
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, color: '#111' }}>Understanding your returns</div>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>Tap any question to expand.</div>

                {[
                  {
                    q: 'What does 1% per day interest on my balance mean?',
                    a: (
                      <>
                        <p>Your remaining principal is credited with <strong>1% per day simple interest (1 PPD SI)</strong>, accruing daily on the principal that is still in the account. This is the notional value shown on your wallet as the Total Balance &mdash; a tracker for what you are owed at maturity.</p>
                        <p>SI freezes the moment your principal hits zero (i.e. once you have withdrawn your full principal). What has already accrued stays with you; nothing further is added.</p>
                      </>
                    ),
                  },
                  {
                    q: 'Then what is the 2% per 30-day continuously compounded interest (CCI) rate?',
                    a: (
                      <>
                        <p>Under Indian law, the maximum cash-payable interest rate on unsecured lending of this nature is <strong>continuously compounded interest</strong> at <strong>2% per 30 days</strong> &mdash; abbreviated <strong>2% CCI</strong>. This is the rate at which Achintya can legally pay you in cash, today.</p>
                        <p>Your CCI Claimable balance behaves like a savings account &mdash; it starts at your first invest, grows at 2%/30d, and you can withdraw any amount from it at any time. Withdrawals reduce the CCI balance directly. Once it hits zero, it stops growing.</p>
                        <p>The portion of your Total Balance that is <strong>above</strong> the CCI Claimable is the <strong>SI&minus;CCI Gap</strong> &mdash; settled via the buyback mechanism below.</p>
                      </>
                    ),
                  },
                  {
                    q: 'Why do the two curves converge after exactly 6300 days?',
                    a: (
                      <>
                        <p>The 1 PPD SI line (linear) and the 2% CCI curve (compounding) start far apart but mathematically meet at <strong>6,300 days &mdash; about 17.25 years</strong>.</p>
                        <p>At that point both measures yield the same multiple on principal: <strong>64&times;</strong>. The original deal was structured around this 17.25-year convergence, with <strong>2036</strong> as the legal backstop date for the entire program. Buyback availability for the SI balance runs ahead of that backstop and opens shortly.</p>
                      </>
                    ),
                  },
                  {
                    q: 'How can I get the full 1 PPD SI value if I invested late?',
                    a: (
                      <>
                        <p>Your SI Total Balance becomes claimable as the <strong>AppBru Pvt Ltd share buyback</strong> opens &mdash; which is happening <strong>shortly</strong>. You don't have to wait until 2036 to begin realising it.</p>
                        <p>2036 is the legal backstop date built into the original 17.25-year deal &mdash; the security floor that guarantees the foundational investor's 2%/30d CCI matures to ~64&times; (~&#8377;320 Cr on &#8377;5 Cr), which underwrites every other investor's SI realisation. Buyback availability runs ahead of that backstop.</p>
                      </>
                    ),
                  },
                  {
                    q: 'How much can I withdraw, and when?',
                    a: (
                      <>
                        <p>You can freely withdraw from <strong>two buckets</strong> at any time, on request, subject to Achintya's approval:</p>
                        <p>&middot; <strong>Principal</strong> &mdash; the remaining portion of what you put in. Withdrawing it reduces the SI base going forward.<br/>
                        &middot; <strong>Net 2% per 30-day CCI Interest</strong> &mdash; the interest that has compounded on your principal. Withdrawing it does not touch the principal base.</p>
                        <p>The <strong>SI&minus;CCI Gap (Buyback)</strong> sits above the CCI Claimable and is the part that <em>cannot</em> be paid in cash under Indian unsecured-lending limits. It is settled as a buyback of <strong>shares in AppBru Pvt Ltd</strong> at the currently accepted company valuation. The buyback <strong>opens shortly</strong>, so your SI balance will begin to be claimable then. <strong>2036</strong> is the legal backstop under the original 17.25-year deal &mdash; the security floor, not the expected payout date.</p>
                      </>
                    ),
                  },
                  {
                    q: 'What backs the buyback?',
                    a: (
                      <>
                        <p>The buyback is honoured by Achintya through <strong>AppBru Pvt Ltd</strong> shares at the company's currently accepted valuation. Your SI&minus;CCI Gap is the rupee size of the buyback claim, and it becomes claimable as the buyback opens shortly.</p>
                        <p>The security floor underneath this is the foundational investor's 2%/30d CCI, which matures to ~&#8377;320 Cr on a &#8377;5 Cr principal at the 2036 backstop &mdash; the legally-enforceable liquidity envelope from which every investor's gap is cash-equivalent settled at the latest, well after the buyback has already opened.</p>
                      </>
                    ),
                  },
                ].map((item, i) => {
                  const open = faqOpen === i;
                  return (
                    <div key={i} style={{ borderTop: i === 0 ? 'none' : '1px solid #f0f0f0' }}>
                      <div
                        onClick={() => setFaqOpen(open ? -1 : i)}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', cursor: 'pointer' }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#111', paddingRight: 12 }}>{item.q}</span>
                        <span style={{ fontSize: 16, color: '#888', transform: open ? 'rotate(45deg)' : 'none', transition: 'transform 0.2s' }}>+</span>
                      </div>
                      {open && (
                        <div style={{ fontSize: 13, color: '#444', lineHeight: 1.6, padding: '0 0 14px' }}>
                          {item.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* NDA */}
              <div style={{
                background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16,
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#111' }}>Non-Disclosure Agreement</div>
                  {ndaSigned && (
                    <span style={{
                      fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 6,
                      background: '#e8f8ee', color: '#27ae60',
                    }}>
                      Accepted
                    </span>
                  )}
                </div>

                <div style={{ border: '1px solid #e8e8e2', borderRadius: 12, padding: 18, marginBottom: 16, maxHeight: 300, overflowY: 'auto' }}>
                  <div className="nda-body">
                    <p>This Non-Disclosure Agreement ("<strong>Agreement</strong>") is entered into by and between:</p>
                    <p><strong>Disclosing Party:</strong> Achintya Organisation ("Achintya"), a private entity engaged in investment management, financial technology, and related services.</p>
                    <p><strong>Receiving Party:</strong> The undersigned individual ("<strong>User</strong>"), identified by their registered phone number and name provided below.</p>
                    <h4>1. Purpose</h4>
                    <p>The User acknowledges that they may receive or have access to Confidential Information belonging to Achintya. This Agreement is intended to protect such information from unauthorized disclosure.</p>
                    <h4>2. Confidential Information</h4>
                    <p>Includes the User's own transaction records, account balances, banking details, and the terms, rates, and settlement mechanics of the User's engagement with Achintya, and any information marked or reasonably understood to be confidential.</p>
                    <h4>2A. Interest Rate Disclosure</h4>
                    <p>The User acknowledges that any <strong>1% per day simple interest ("1 PPD SI")</strong> reflected on the platform on the User's remaining principal shall be maintained and honoured until <strong>after the year 2036</strong>, being the legal backstop date (epoch + 6,300 days) under the original 17.25-year deal on which the foundational investor's <strong>2% per 30-day compounded interest rate ("2% CCI")</strong> curve converges with their 1 PPD SI at 64&times; principal. The User's full SI Total Balance is realisable through a buyback of <strong>AppBru Pvt Ltd</strong> shares at the currently accepted company valuation, which opens shortly &mdash; the 2036 backstop is the security floor, not the expected payout date. The User shall hold all such information in strict confidence.</p>
                    <h4>3. Obligations</h4>
                    <p>The User agrees to hold all Confidential Information in strict confidence, not disclose it to any third party, and not use it for any purpose other than their engagement with the Achintya platform.</p>
                    <h4>4. Term</h4>
                    <p>This Agreement shall remain in force for <strong>five (5) years</strong> from the date of last disclosure of Confidential Information.</p>
                    <h4>5. Governing Law</h4>
                    <p>This Agreement shall be governed by the laws of India. Disputes shall be subject to the courts in New Delhi, India.</p>
                  </div>
                </div>

                {ndaSigned ? (
                  <div style={{ fontSize: 12, color: '#888' }}>
                    Accepted on {new Date(ndaSigned.signedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                    {isAdmin && !selectedUsername && (
                      <span
                        onClick={async () => {
                          const viewedUsername = selectedUsername || myUsername;
                          if (!viewedUsername) return;
                          await rtdbSet(ref(rtdb, `users/${viewedUsername}/nda`), null);
                          await loadNda(viewedUsername);
                          setToast('NDA reset successfully');
                          setTimeout(() => setToast(''), 3000);
                        }}
                        style={{ color: '#eb4d4b', cursor: 'pointer', marginLeft: 12, fontWeight: 600 }}
                      >
                        Reset
                      </span>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="ob-check" style={{ marginBottom: 12 }}>
                      <input type="checkbox" checked={ndaAgreed} onChange={(e) => setNdaAgreed(e.target.checked)} />
                      <span>I have read and agree to the Non-Disclosure Agreement</span>
                    </label>
                    <button className="ob-next-btn" style={{ width: '100%', background: '#27ae60', opacity: ndaAgreed ? 1 : 0.4 }} disabled={!ndaAgreed} onClick={handleSignNda}>Accept</button>
                    {ndaStatus && <div style={{ fontSize: 12, marginTop: 8, color: '#eb4d4b' }}>{ndaStatus}</div>}
                  </div>
                )}
              </div>

              {/* Loan Agreement — Secured by Collateral */}
              <div style={{
                background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16,
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#111' }}>Loan Agreement &mdash; SI Gap Settled by AppBru Share Buyback</div>
                  {loanSigned && (
                    <span style={{
                      fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 6,
                      background: '#e8f8ee', color: '#27ae60',
                    }}>
                      Accepted
                    </span>
                  )}
                </div>

                <div style={{ border: '1px solid #e8e8e2', borderRadius: 12, padding: 18, marginBottom: 16, maxHeight: 300, overflowY: 'auto' }}>
                  <div className="nda-body">
                    <p>This Loan Agreement ("<strong>Agreement</strong>") is entered into on the date of acceptance, by and between:</p>
                    <p><strong>Borrower:</strong> Achintya Organisation ("<strong>Achintya</strong>"), having its principal place of business in India (hereinafter referred to as the "<strong>Borrower</strong>").</p>
                    <p><strong>Lender:</strong> <strong>{walletName || 'The User'}</strong>, identified by their registered phone number on the Achintya platform (hereinafter referred to as the "<strong>Lender</strong>").</p>

                    <h4>Recitals</h4>
                    <p><strong>A.</strong> The Lender has registered on the Achintya platform and may, from time to time, advance funds to the Borrower through the platform's transaction ledger. Each such advance is made from a date in or after <strong>2020</strong>.</p>
                    <p><strong>B.</strong> The Borrower has agreed to credit the Lender with a notional <strong>1% per day simple interest ("1 PPD SI")</strong> on the Lender's own running principal balance, computed from the Lender's own transaction dates, and to maintain and honour such rate until <strong>after the year 2036</strong>.</p>
                    <p><strong>C.</strong> Under applicable Indian law, the maximum contractual interest rate which the Borrower may legally pay in cash on unsecured lending of this nature is <strong>2% per 30 days, compounded continuously</strong> ("<strong>2% CCI</strong>"), which is the legally payable rate set forth under Clause 3 below. The 1 PPD SI curve and the 2% CCI curve converge, for any given disbursement, <strong>after the year 2036</strong>, at which point both measures yield the same multiple on the Lender's running principal balance.</p>

                    <h4>1. Loan &amp; Disbursement</h4>
                    <p>The Lender agrees to advance funds to the Borrower as recorded in the Achintya platform transaction ledger. Each investment transaction made by the Lender constitutes a disbursement under this Agreement. The aggregate outstanding principal of the Lender shall be the net of all investments less withdrawals as reflected on the platform.</p>

                    <h4>2. Notional Interest Entitlement (1 PPD SI)</h4>
                    <p>The Lender shall be <em>credited with a notional interest entitlement</em> at the rate of <strong>1% per day, simple interest</strong>, calculated on the Lender's daily running principal balance, accruing from the date of each disbursement made by the Lender and continuing until repayment in full. This notional entitlement shall be displayed on the Achintya platform as the Lender's "wallet" or "target" value.</p>

                    <h4>3. Legally Payable Interest (2% CCI Cap)</h4>
                    <p>Notwithstanding Clause 2, the <em>cash-payable</em> interest which the Borrower shall be contractually obliged to discharge to the Lender at any time shall be capped at <strong>2% per 30 days, compounded continuously</strong>, calculated on the Lender's daily running principal balance from the Lender's own transaction dates. Any shortfall between the notional 1 PPD SI entitlement under Clause 2 and the legally payable 2% CCI amount under this Clause 3 (the "<strong>SI-CCI Gap</strong>") shall be satisfied only through the mechanisms set forth in Clause 5.</p>

                    <h4>4. Withdrawal &amp; Repayment</h4>
                    <p>The Lender may request a withdrawal at any time through the Achintya platform. On each such withdrawal, the Borrower shall pay to the Lender an amount equal to the lesser of (i) the withdrawal request, or (ii) the Lender's then-outstanding principal plus accrued interest computed under Clause 3 (2% CCI). Partial withdrawals shall be applied first toward accrued 2% CCI interest and then toward principal reduction. The Lender acknowledges that until the convergence of the two curves (which shall occur <strong>after the year 2036</strong> for any given disbursement), the legally payable 2% CCI amount shall be less than the notional 1 PPD SI entitlement credited under Clause 2.</p>

                    <h4>5. Satisfaction of the SI-CCI Gap</h4>
                    <p>The SI-CCI Gap, if any, shall be satisfied <em>only on or after the convergence of the two curves for the relevant disbursement</em>, and exclusively by one or more of the following: (a) voluntary top-up payments made by the Borrower from lawful sources; (b) enforcement of the <strong>Share Pledge Agreement</strong> (as defined below) against the collateral held in favour of the Lender; or (c) any other lawful settlement mutually agreed in writing. The Lender expressly acknowledges that receipt of the notional 1 PPD SI entitlement is contingent on the realisation of the pledged collateral and is not a guaranteed cash-denominated obligation of the Borrower prior to convergence.</p>

                    <h4>6. Security / Collateral</h4>
                    <p>As security for the due and punctual repayment of all amounts due under this Agreement (including the legally payable 2% CCI amount and any SI-CCI Gap realised through enforcement), the Borrower has caused a separate <strong>Share Pledge Agreement</strong> to be executed in favour of the Lender. The Share Pledge Agreement forms an integral part of the security arrangement under this Loan Agreement.</p>

                    <h4>7. Representations &amp; Warranties</h4>
                    <ul>
                      <li>The Borrower has full authority to enter into this Agreement.</li>
                      <li>The execution and performance of this Agreement does not conflict with any law, regulation, or other obligation binding on the Borrower.</li>
                      <li>All information furnished to the Lender is true, accurate, and complete in all material respects.</li>
                      <li>The collateral securing this Agreement is in full force and effect and free of third-party encumbrance save as disclosed.</li>
                    </ul>

                    <h4>8. Events of Default</h4>
                    <p>The following shall constitute Events of Default:</p>
                    <ul>
                      <li>Failure by the Borrower to pay principal or 2% CCI interest (as per Clause 3) when duly demanded in accordance with Clause 4.</li>
                      <li>Any representation or warranty made by the Borrower proves to be materially incorrect.</li>
                      <li>The Borrower becomes insolvent, files for bankruptcy, or is unable to pay its debts as they fall due.</li>
                      <li>Any material adverse change in the financial condition of the Borrower or any material impairment of the collateral.</li>
                    </ul>
                    <p>Upon the occurrence of an Event of Default, the Lender may declare all outstanding amounts immediately due and payable, and may enforce the Share Pledge Agreement in accordance with its terms.</p>

                    <h4>9. Governing Law &amp; Jurisdiction</h4>
                    <p>This Agreement shall be governed by and construed in accordance with the laws of India, including the Indian Contract Act, 1872, and shall be read together with the NDA and the Share Pledge Agreement as a single integrated arrangement. Any disputes arising hereunder shall be subject to the exclusive jurisdiction of the courts in <strong>New Delhi, India</strong>.</p>
                  </div>
                </div>

                {loanSigned ? (
                  <div style={{ fontSize: 12, color: '#888' }}>
                    Accepted on {new Date(loanSigned.signedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                    {isAdmin && !selectedUsername && (
                      <span
                        onClick={async () => {
                          const viewedUsername = selectedUsername || myUsername;
                          if (!viewedUsername) return;
                          await rtdbSet(ref(rtdb, `users/${viewedUsername}/loanAgreement`), null);
                          await loadLoan(viewedUsername);
                          setToast('Loan Agreement reset');
                          setTimeout(() => setToast(''), 3000);
                        }}
                        style={{ color: '#eb4d4b', cursor: 'pointer', marginLeft: 12, fontWeight: 600 }}
                      >
                        Reset
                      </span>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="ob-check" style={{ marginBottom: 12 }}>
                      <input type="checkbox" checked={loanAgreed} onChange={(e) => setLoanAgreed(e.target.checked)} />
                      <span>I have read and agree to the Loan Agreement — Secured by Collateral</span>
                    </label>
                    <button className="ob-next-btn" style={{ width: '100%', background: '#27ae60', opacity: loanAgreed ? 1 : 0.4 }} disabled={!loanAgreed} onClick={handleSignLoan}>Accept</button>
                    {loanStatus && <div style={{ fontSize: 12, marginTop: 8, color: '#eb4d4b' }}>{loanStatus}</div>}
                  </div>
                )}
              </div>

              {/* Share Pledge Agreement */}
              <div style={{
                background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16,
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#111' }}>Share Pledge Agreement</div>
                  {pledgeSigned && (
                    <span style={{
                      fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 6,
                      background: '#e8f8ee', color: '#27ae60',
                    }}>
                      Accepted
                    </span>
                  )}
                </div>

                <div style={{ border: '1px solid #e8e8e2', borderRadius: 12, padding: 18, marginBottom: 16, maxHeight: 300, overflowY: 'auto' }}>
                  <div className="nda-body">
                    <p>This Share Pledge Agreement ("<strong>Agreement</strong>") is entered into on the date of acceptance, by and between:</p>
                    <p><strong>Pledgor:</strong> The party designated by Achintya Organisation as the provider of collateral under the Loan Agreement (hereinafter referred to as the "<strong>Pledgor</strong>").</p>
                    <p><strong>Pledgee:</strong> <strong>{walletName || 'The User'}</strong>, identified by their registered phone number on the Achintya platform, in their capacity as Lender under the Loan Agreement (hereinafter referred to as the "<strong>Pledgee</strong>").</p>

                    <h4>1. Background &amp; Recitals</h4>
                    <p><strong>A.</strong> Pursuant to the Loan Agreement between the Borrower and the Pledgee, the Pledgor has agreed to pledge equity shares as collateral security for the due repayment of all amounts owed under the said Loan Agreement, including the satisfaction of any <strong>SI-CCI Gap</strong> (as defined in the Loan Agreement) through enforcement of this Agreement on the convergence date applicable to each of the Pledgee's disbursements (being <strong>after the year 2036</strong> for disbursements made in or after 2020).</p>
                    <p><strong>B.</strong> The Pledgor's collateral retains intrinsic long-term value sufficient to support the Secured Obligations until <strong>after the year 2036</strong>, and the continued confidentiality of the collateral arrangement is a material consideration in sustaining such value.</p>

                    <h4>2. Pledged Shares (Collateral)</h4>
                    <p>The Pledgor hereby pledges in favour of the Pledgee the equity shares designated under this Agreement as collateral (the "<strong>Pledged Shares</strong>"). The Pledgor shall deliver or cause to be delivered all share certificates, duly endorsed transfer forms, and such other documents as may be required to perfect this pledge. The Pledged Shares shall extend rateably to secure all lenders holding parallel instruments, pro rata to their respective outstanding principal balances.</p>

                    <h4>3. Secured Obligations</h4>
                    <p>This pledge secures the repayment of:</p>
                    <ul>
                      <li>All principal amounts disbursed by the Pledgee to the Borrower under the Loan Agreement;</li>
                      <li>All accrued and unpaid interest thereon, computed at the legally payable <strong>2% per 30 days, compounded continuously</strong> rate;</li>
                      <li>Any <strong>SI-CCI Gap</strong> due to the Pledgee as and when realised in accordance with Clause 5 of the Loan Agreement;</li>
                      <li>All costs, charges, and expenses incurred by the Pledgee in enforcing this Agreement or the Loan Agreement.</li>
                    </ul>

                    <h4>4. Pledgor&rsquo;s Covenants</h4>
                    <ul>
                      <li>The Pledgor is the legal and beneficial owner of the Pledged Shares, free from any encumbrance, lien, or third-party claim save for the rateable pledges in favour of other lenders under parallel instruments.</li>
                      <li>The Pledgor shall not sell, transfer, assign, or create any further encumbrance over the Pledged Shares without the prior written consent of the Pledgee, except in favour of other lenders on a pari passu basis.</li>
                      <li>The Pledgor shall preserve and not wilfully impair the Pledged Shares during the subsistence of this pledge, and shall promptly inform the Pledgee of any event that may adversely affect the value of the Pledged Shares.</li>
                      <li>Any dividends, bonus shares, rights issues, or other entitlements arising from the Pledged Shares during the pledge period shall also form part of the collateral.</li>
                    </ul>

                    <h4>5. Valuation</h4>
                    <p>For the purposes of enforcement under Clause 6 and for the satisfaction of any SI-CCI Gap, the Pledged Shares shall be valued by a <strong>registered valuer</strong> appointed in accordance with Rule 11UA of the Income-tax Rules, 1962 and Section 247 of the Companies Act, 2013.</p>

                    <h4>6. Enforcement</h4>
                    <p>Upon the occurrence of an Event of Default (as defined in the Loan Agreement), or upon the arrival of the convergence date applicable to the Pledgee's disbursements (being <strong>after the year 2036</strong>) with any SI-CCI Gap then outstanding, the Pledgee shall be entitled to:</p>
                    <ul>
                      <li>Appropriate and sell the Pledged Shares (or a rateable portion thereof), either privately or through a public sale, after providing <strong>fifteen (15) days&rsquo;</strong> prior written notice to the Pledgor;</li>
                      <li>Apply the net proceeds of such sale toward the satisfaction of the Secured Obligations;</li>
                      <li>Exercise all rights of a shareholder in respect of the Pledged Shares, including voting rights, until the Secured Obligations are discharged in full.</li>
                    </ul>

                    <h4>7. Release of Pledge</h4>
                    <p>Upon full and final repayment of all Secured Obligations &mdash; including any SI-CCI Gap &mdash; the Pledgee shall release the Pledged Shares and return all share certificates and related documents to the Pledgor within <strong>fifteen (15) business days</strong>.</p>

                    <h4>8. Governing Law &amp; Jurisdiction</h4>
                    <p>This Agreement shall be governed by and construed in accordance with the laws of India, including the Indian Contract Act, 1872 and the Companies Act, 2013, and shall be read together with the NDA and the Loan Agreement as a single integrated arrangement. Any disputes shall be subject to the exclusive jurisdiction of the courts in <strong>New Delhi, India</strong>.</p>
                  </div>
                </div>

                {pledgeSigned ? (
                  <div style={{ fontSize: 12, color: '#888' }}>
                    Accepted on {new Date(pledgeSigned.signedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                    {isAdmin && !selectedUsername && (
                      <span
                        onClick={async () => {
                          const viewedUsername = selectedUsername || myUsername;
                          if (!viewedUsername) return;
                          await rtdbSet(ref(rtdb, `users/${viewedUsername}/sharePledge`), null);
                          await loadPledge(viewedUsername);
                          setToast('Share Pledge Agreement reset');
                          setTimeout(() => setToast(''), 3000);
                        }}
                        style={{ color: '#eb4d4b', cursor: 'pointer', marginLeft: 12, fontWeight: 600 }}
                      >
                        Reset
                      </span>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="ob-check" style={{ marginBottom: 12 }}>
                      <input type="checkbox" checked={pledgeAgreed} onChange={(e) => setPledgeAgreed(e.target.checked)} />
                      <span>I have read and agree to the Share Pledge Agreement</span>
                    </label>
                    <button className="ob-next-btn" style={{ width: '100%', background: '#27ae60', opacity: pledgeAgreed ? 1 : 0.4 }} disabled={!pledgeAgreed} onClick={handleSignPledge}>Accept</button>
                    {pledgeStatus && <div style={{ fontSize: 12, marginTop: 8, color: '#eb4d4b' }}>{pledgeStatus}</div>}
                  </div>
                )}
              </div>

              {/* Cancel Investment */}
              <div style={{
                background: cancelSigned ? '#fde8e8' : '#fff', borderRadius: 12, padding: 20, marginBottom: 16,
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer',
                border: '1.5px solid #eb4d4b',
              }} onClick={() => setTab('cancel')}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#eb4d4b' }}>
                    Cancel Investment
                  </div>
                  {cancelSigned && (
                    <span style={{
                      fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 6,
                      background: '#fde8e8', color: '#eb4d4b',
                    }}>
                      Filed
                    </span>
                  )}
                </div>
                {cancelSigned && (
                  <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                    Filed on {new Date(cancelSigned.signedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ---- Anchor (Class A) Live Balance ---- */}
          {tab === 'anchor' && (() => {
            const days = daysSinceEpoch(nowMs);
            const mult = siMultiplier(days);
            const bal = ANCHOR_PRINCIPAL * mult;
            const maturityDays = daysBetween(ACHINTYA_EPOCH, MATURITY_DATE);
            const maturityBal = ANCHOR_PRINCIPAL * siMultiplier(maturityDays);
            const remaining = Math.max(0, maturityDays - days);
            const progress = Math.min(100, (days / maturityDays) * 100);
            return (
              <div>
                <div onClick={() => setTab('transactions')} style={{ fontSize: 13, cursor: 'pointer', color: '#888', marginBottom: 16 }}>
                  &larr; Back
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: '#111' }}>Foundational Investor Balance</div>
                <div style={{
                  background: 'linear-gradient(135deg, #111 0%, #333 100%)',
                  borderRadius: 16, padding: '28px 24px', color: '#fff', marginBottom: 20,
                }}>
                  <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 4 }}>Live balance (1 PPD simple interest)</div>
                  <div style={{ fontSize: 36, fontWeight: 700, letterSpacing: -1 }}>{formatINR(bal)}</div>
                  <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                    Principal {'\u20B9'}5,00,00,000 &middot; growing at 1% per day since deal start
                  </div>
                  <div style={{ marginTop: 18, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: 10, opacity: 0.5 }}>Days elapsed</div>
                      <div style={{ fontSize: 18, fontWeight: 700 }}>{Math.floor(days).toLocaleString('en-IN')}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, opacity: 0.5 }}>Days to maturity</div>
                      <div style={{ fontSize: 18, fontWeight: 700 }}>{Math.floor(remaining).toLocaleString('en-IN')}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, opacity: 0.5 }}>Multiplier</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#ffd93d' }}>{mult.toFixed(2)}&times;</div>
                    </div>
                  </div>
                  <div style={{ marginTop: 20 }}>
                    <div style={{ height: 6, background: 'rgba(255,255,255,0.15)', borderRadius: 6, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: progress + '%', background: '#ffd93d', transition: 'width 0.3s' }} />
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.7, marginTop: 6, display: 'flex', justifyContent: 'space-between' }}>
                      <span>Deal start</span>
                      <span>{progress.toFixed(2)}%</span>
                      <span>2036 backstop</span>
                    </div>
                  </div>
                </div>
                <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: 13, color: '#888', marginBottom: 6 }}>Maturity value (2036 backstop)</div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: '#111' }}>{formatINR(maturityBal)}</div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                    = 64&times; principal &middot; 1 PPD SI and 2% / 30d CCI converge at this backstop
                  </div>
                </div>
                <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: '#111' }}>How this works</div>
                  <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6 }}>
                    Your foundational subscription is locked under the original Innernet agreement.
                    The balance shown above is your notional 1 PPD simple interest entitlement.
                    The AppBru Pvt Ltd share buyback opens shortly &mdash; realisation begins then.
                    2036 is the legal backstop (day 6,300) under the original 17.25-year deal at which
                    the 1 PPD SI and 2% / 30d CCI curves converge at {formatINR(maturityBal)} (64&times; principal).
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ---- Achintya Max Liability ---- */}
          {tab === 'liability' && (() => {
            const days = daysSinceEpoch(nowMs);
            const mult = siMultiplier(days);
            const maxLiab = TOTAL_LIABILITY_PRINCIPAL * mult;
            const anchorLiab = ANCHOR_PRINCIPAL * mult;
            const classBCeiling = CLASS_B_CAP * mult;
            const today = todayISO();
            // Sum live Class B notional from active slots (using each slot's own elapsed days)
            const activeClassBNotional = slots
              .filter(s => s.status !== 'cancelled' && s.status !== 'matured' && s.startDate <= today)
              .reduce((acc, s) => {
                const elapsed = Math.min(daysBetween(s.startDate, today), daysBetween(s.startDate, s.endDate));
                return acc + s.principal * siMultiplier(Math.max(0, elapsed));
              }, 0);
            const activePrincipal = slots
              .filter(s => s.status !== 'cancelled' && s.status !== 'matured' && s.startDate <= today && s.endDate > today)
              .reduce((acc, s) => acc + Number(s.principal), 0);
            const classBUtilisation = (activePrincipal / CLASS_B_CAP) * 100;
            const maturityCap = TOTAL_LIABILITY_PRINCIPAL * MATURITY_MULTIPLIER;
            return (
              <div>
                <div onClick={() => setTab('transactions')} style={{ fontSize: 13, cursor: 'pointer', color: '#888', marginBottom: 16 }}>
                  &larr; Back
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6, color: '#111' }}>Achintya Max Liability</div>
                <div style={{ fontSize: 13, color: '#666', marginBottom: 20 }}>
                  Worst-case envelope assuming both Rs 5 Cr books fully utilized since deal start.
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #2c1810 0%, #5a2c1e 100%)',
                  borderRadius: 16, padding: '28px 24px', color: '#fff', marginBottom: 20,
                }}>
                  <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 4 }}>Envelope at 1 PPD SI (Rs 10 Cr principal)</div>
                  <div style={{ fontSize: 36, fontWeight: 700, letterSpacing: -1 }}>{formatINR(maxLiab)}</div>
                  <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
                    Target {formatINR(maturityCap)} at the 2036 backstop (640 Cr worst case)
                  </div>
                  <div style={{ marginTop: 18, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: 10, opacity: 0.5 }}>Days since epoch</div>
                      <div style={{ fontSize: 18, fontWeight: 700 }}>{Math.floor(days).toLocaleString('en-IN')}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, opacity: 0.5 }}>Multiplier</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#ffd93d' }}>{mult.toFixed(2)}&times;</div>
                    </div>
                  </div>
                </div>
                <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: '#111' }}>Breakdown</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
                    <span style={{ color: '#555' }}>Anchor liability (Rs 5 Cr locked)</span>
                    <span style={{ fontWeight: 700, color: '#111' }}>{formatINR(anchorLiab)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
                    <span style={{ color: '#555' }}>Class B ceiling (Rs 5 Cr full)</span>
                    <span style={{ fontWeight: 700, color: '#111' }}>{formatINR(classBCeiling)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
                    <span style={{ color: '#555' }}>Class B active notional (actual)</span>
                    <span style={{ fontWeight: 700, color: '#27ae60' }}>{formatINR(activeClassBNotional)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontSize: 13 }}>
                    <span style={{ color: '#555' }}>Class B principal utilisation</span>
                    <span style={{ fontWeight: 700, color: '#111' }}>{formatINR(activePrincipal)} / {formatINR(CLASS_B_CAP)} ({classBUtilisation.toFixed(1)}%)</span>
                  </div>
                </div>
                <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: '#111' }}>Appbru / Innernet valuation required</div>
                  <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6 }}>
                    The pledged shares in Appbru Pvt Ltd (backed by the Innernet asset) must be worth at least this envelope at any time during the pledge period. Maturity target for the worst case: {formatINR(maturityCap)} at the 2036 backstop.
                  </div>
                </div>

                {/* RTDB legacy principal aggregate (Class B) */}
                <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, color: '#111' }}>Class B principal (from existing users)</div>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 14 }}>
                    Aggregated from <code>rtdb/users/*/principal</code> (values in lakhs). Excludes anchor, admin, and system entries.
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
                    <span style={{ color: '#555' }}>Total Class B principal</span>
                    <span style={{ fontWeight: 700, color: '#111' }}>{classBPrincipalLakhs.toLocaleString('en-IN', { maximumFractionDigits: 2 })} L &middot; {formatINR(classBPrincipalRupees)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
                    <span style={{ color: '#555' }}>Utilisation vs Rs 5 Cr cap</span>
                    <span style={{ fontWeight: 700, color: classBRtdbUtilPct > 100 ? '#eb4d4b' : classBRtdbUtilPct > 80 ? '#e67e22' : '#27ae60' }}>{classBRtdbUtilPct.toFixed(1)}%</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 13 }}>
                    <span style={{ color: '#555' }}>Remaining capacity</span>
                    <span style={{ fontWeight: 700, color: '#27ae60' }}>{formatINR(Math.max(0, CLASS_B_CAP - classBPrincipalRupees))}</span>
                  </div>
                  {/* Occupancy bar */}
                  <div style={{ marginTop: 12, height: 10, background: '#fafaf6', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: Math.min(100, classBRtdbUtilPct) + '%',
                      background: classBRtdbUtilPct > 100 ? '#eb4d4b' : classBRtdbUtilPct > 80 ? '#e67e22' : '#27ae60',
                    }} />
                  </div>

                  {/* Per-user breakdown — top holders first, zeros collapsed */}
                  {(() => {
                    const nonZero = classBRtdb.filter(u => u.principal > 0).sort((a, b) => b.principal - a.principal);
                    const zero = classBRtdb.filter(u => u.principal <= 0);
                    return (
                      <div style={{ marginTop: 16 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                          Active holders ({nonZero.length})
                        </div>
                        {nonZero.map((u) => {
                          const pct = (u.principal * 100000 / CLASS_B_CAP) * 100;
                          const nameDisplay = u.name.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                          return (
                            <div key={u.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f5f5f5', fontSize: 12 }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, color: '#111' }}>{nameDisplay}</div>
                                <div style={{ color: '#888', fontSize: 10 }}>{pct.toFixed(2)}% of cap</div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontWeight: 700 }}>{u.principal.toLocaleString('en-IN', { maximumFractionDigits: 2 })} L</div>
                                <div style={{ color: '#888', fontSize: 10 }}>{formatINR(u.principal * 100000)}</div>
                              </div>
                            </div>
                          );
                        })}
                        {zero.length > 0 && (
                          <div style={{ fontSize: 11, color: '#aaa', marginTop: 10, textAlign: 'center' }}>
                            + {zero.length} zero-principal users
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            );
          })()}

          {/* ---- User Activity (admin only) ---- */}
          {tab === 'visits' && isAdmin && (() => {
            const fmtTime = (ts) => {
              const d = ts?.toDate?.() || (ts ? new Date(ts) : null);
              if (!d) return '—';
              return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
            };
            const fmtDur = (sec) => {
              if (!Number.isFinite(sec) || sec < 0) return '—';
              if (sec < 60) return `${Math.round(sec)}s`;
              if (sec < 3600) return `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`;
              const h = Math.floor(sec / 3600);
              const m = Math.floor((sec % 3600) / 60);
              return `${h}h ${m}m`;
            };
            const now = Date.now();
            const rows = visits.map((v) => {
              const start = v.startedAt?.toDate?.()?.getTime?.() || null;
              const last = v.lastSeenAt?.toDate?.()?.getTime?.() || start;
              const ended = v.endedAt?.toDate?.()?.getTime?.() || null;
              const endRef = ended || last || start || now;
              const live = !ended && last && (now - last) < 120000;
              return { ...v, _start: start, _last: last, _ended: ended, _live: live, _durSec: start ? Math.max(0, (endRef - start) / 1000) : 0 };
            });
            const activeNow = rows.filter(r => r._live);
            return (
              <div>
                <div onClick={() => setTab('transactions')} style={{ fontSize: 13, cursor: 'pointer', color: '#888', marginBottom: 16 }}>
                  &larr; Back
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6, color: '#111' }}>User Activity</div>
                <div style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
                  Every session from login to leaving. Heartbeat every 60s; "Active" = seen in the last 2 minutes.
                </div>

                <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                  <div style={{ flex: 1, background: '#fff', borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Active now</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#27ae60' }}>{activeNow.length}</div>
                  </div>
                  <div style={{ flex: 1, background: '#fff', borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Sessions shown</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#111' }}>{rows.length}</div>
                  </div>
                </div>

                <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  {visitsLoading && rows.length === 0 && (
                    <div style={{ padding: 24, textAlign: 'center', color: '#bbb', fontSize: 13 }}>Loading…</div>
                  )}
                  {!visitsLoading && rows.length === 0 && (
                    <div style={{ padding: 24, textAlign: 'center', color: '#bbb', fontSize: 13 }}>No sessions recorded yet.</div>
                  )}
                  {rows.map((v, i) => (
                    <div key={v.id} style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '14px 16px',
                      borderBottom: i < rows.length - 1 ? '1px solid #f0f0f0' : 'none',
                      borderLeft: `3px solid ${v._live ? '#27ae60' : v._ended ? '#bbb' : '#e67e22'}`,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: '#111' }}>
                            {v.username}{v.isAdmin ? ' · admin' : ''}
                          </span>
                          <span style={{
                            fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
                            background: v._live ? '#e8f8ee' : v._ended ? '#f0f0f0' : '#fff3e0',
                            color: v._live ? '#27ae60' : v._ended ? '#888' : '#e67e22',
                          }}>{v._live ? 'Active' : v._ended ? 'Ended' : 'Stalled'}</span>
                        </div>
                        <div style={{ fontSize: 11, color: '#666', marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                          <span>Start: <strong style={{ color: '#111' }}>{fmtTime(v.startedAt)}</strong></span>
                          <span>{v._ended ? 'End' : 'Last seen'}: <strong style={{ color: '#111' }}>{fmtTime(v.endedAt || v.lastSeenAt)}</strong></span>
                          <span>Duration: <strong style={{ color: '#111' }}>{fmtDur(v._durSec)}</strong></span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* ---- Record a Transaction (user request, pending admin approval) ---- */}
          {tab === 'submit' && (() => {
            // Firestore-pending (legacy other withdrawal types, being wound down) + RTDB
            // pending/processing requests (visibleWithdrawalRequests — see its declaration),
            // reshaped to the same fields the row below reads, tagged origin so the click
            // handler and Approve/Reject controls know which write path to use.
            const pendingTxns = [
              ...transactions.filter(t => t.status === 'pending'),
              ...visibleWithdrawalRequests.map(r => ({
                id: r.id,
                sender: 'achintya',
                receiver: viewedUsername,
                amount: r.amount,
                date: r.date,
                status: r.status,
                pledgeStatus: r.pledgeStatus,
                pledgePrincipalAmount: r.pledgePrincipalAmount,
                ledgerRecordedSeparately: r.ledgerRecordedSeparately,
                origin: 'withdrawalRequest',
              })),
            ];
            const editingTxn = editingPendingId ? transactions.find(t => t.id === editingPendingId) : null;
            const editingCounterparty = editingTxn
              ? (editingTxn.sender !== 'achintya' ? editingTxn.sender : editingTxn.receiver)
              : '';
            return (
            <div>
              <div onClick={() => { setEditingPendingId(null); setPendingEditMsg(''); setTab('transactions'); }} style={{ fontSize: 13, cursor: 'pointer', color: '#888', marginBottom: 16 }}>
                &larr; Back
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6, color: '#111' }}>
                {isAdmin ? 'Pending Transactions' : 'Record a Transaction'}
              </div>
              <div style={{ fontSize: 13, color: '#666', marginBottom: 20 }}>
                {isAdmin
                  ? 'All transactions awaiting your approval. Tap any row to review and approve or reject.'
                  : 'For recording an existing successful transaction only — an invest or a withdrawal that has already cleared in the bank. Achintya will verify against the bank statement and approve.'}
              </div>

              {/* Add Transaction — admin only, direct ledger correction for ANY user (own
                  search/picker, independent of the header search above). Always records a
                  settled entry, e.g. a backdated correction like "this was actually settled in
                  2021 through someone else's account". */}
              {isAdmin && (() => {
                const filteredAddTxnUsers = (allUsers || [])
                  .filter(u => u.key && u.key !== 'achintya' && u.key !== 'ayana' && u.key !== 'undefined')
                  .filter(u => u.key.toLowerCase().includes(addTxnSearch.toLowerCase()));
                return (
                  <div style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111', marginBottom: 10 }}>
                      Add Transaction
                    </div>
                    <div style={{ position: 'relative', marginBottom: 10 }}>
                      <input
                        type="text"
                        placeholder={addTxnUsername
                          ? addTxnUsername.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                          : 'Search user…'}
                        value={addTxnSearch}
                        onChange={(e) => setAddTxnSearch(e.target.value)}
                        style={{
                          padding: '10px 12px', borderRadius: 8, border: '1.5px solid #ddd', fontSize: 13,
                          width: '100%', boxSizing: 'border-box', fontWeight: 600, color: '#111',
                          background: addTxnUsername ? '#f0f8ff' : '#fff',
                        }}
                      />
                      {addTxnUsername && !addTxnSearch && (
                        <span
                          onClick={() => setAddTxnUsername(null)}
                          style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: '#999', cursor: 'pointer', lineHeight: 1 }}
                        >&times;</span>
                      )}
                      {addTxnSearch && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 100,
                          background: '#fff', borderRadius: 10, overflow: 'hidden',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto',
                        }}>
                          {filteredAddTxnUsers.map(u => (
                            <div key={u.key}
                              onClick={() => { setAddTxnUsername(u.key); setAddTxnSearch(''); }}
                              style={{
                                padding: '10px 14px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f0f0f0',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                background: addTxnUsername === u.key ? '#f0f0f0' : '#fff',
                              }}
                            >
                              <span style={{ fontWeight: 600, color: '#111' }}>{u.key.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</span>
                              <span style={{ fontSize: 11, color: '#888' }}>{u.phone}</span>
                            </div>
                          ))}
                          {filteredAddTxnUsers.length === 0 && (
                            <div style={{ padding: 14, fontSize: 12, color: '#999', textAlign: 'center' }}>No users found</div>
                          )}
                        </div>
                      )}
                    </div>
                    {addTxnUsername && (
                      <>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                          <input type="text" inputMode="numeric" placeholder="Amount" value={addTxnAmount}
                            onChange={(e) => { const v = e.target.value; if (/^\d*$/.test(v)) setAddTxnAmount(v); }}
                            style={{ ...inputStyle, width: '50%' }} />
                          <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1.5px solid #111' }}>
                            <div
                              onClick={() => setAddTxnType('invest')}
                              style={{
                                padding: '10px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600,
                                background: addTxnType === 'invest' ? '#e8f8ee' : '#fafafa',
                                color: addTxnType === 'invest' ? '#27ae60' : '#aaa',
                              }}
                            >Invest</div>
                            <div
                              onClick={() => setAddTxnType('withdraw')}
                              style={{
                                padding: '10px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600,
                                background: addTxnType === 'withdraw' ? '#fde8e8' : '#fafafa',
                                color: addTxnType === 'withdraw' ? '#eb4d4b' : '#aaa',
                                borderLeft: '1.5px solid #111',
                              }}
                            >Withdraw</div>
                          </div>
                        </div>
                        <div style={{ marginBottom: 10 }}>
                          <DatePicker
                            selected={addTxnDate}
                            onChange={(date) => setAddTxnDate(date)}
                            dateFormat="dd MMM yyyy"
                            maxDate={new Date()}
                            showYearDropdown
                            showMonthDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={10}
                            customInput={
                              <input style={{ ...inputStyle, width: 140, cursor: 'pointer' }} readOnly />
                            }
                          />
                        </div>
                        {addTxnType === 'withdraw' && (
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 12, color: '#666', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={addTxnMarkProcessing}
                              onChange={(e) => setAddTxnMarkProcessing(e.target.checked)}
                            />
                            Also flag as a refund in progress (shows "Processing" in Transactions until marked complete)
                          </label>
                        )}
                        <button
                          onClick={handleAddTransactionForUser}
                          disabled={addTxnBusy}
                          style={{ ...btnStyle, width: '100%', opacity: addTxnBusy ? 0.6 : 1 }}
                        >
                          {addTxnBusy ? 'Saving…' : 'Add'}
                        </button>
                        {addTxnStatus && (
                          <div style={{ fontSize: 12, marginTop: 8, fontWeight: 600, color: addTxnStatus.startsWith('Error') ? '#eb4d4b' : '#27ae60' }}>
                            {addTxnStatus}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Admin inline edit card (replaces nothing — sits above the list) */}
              {isAdmin && editingTxn && (
                <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1.5px solid #111' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111', marginBottom: 4 }}>
                    Review pending transaction
                  </div>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
                    Submitted by <strong style={{ color: '#444' }}>{editingCounterparty}</strong>
                  </div>
                  {editingTxn.pledgeStatus === 'held' && (
                    <div style={{
                      background: '#fff3e0', border: '1px solid #f3dfa0', color: '#7a5a10',
                      borderRadius: 8, padding: '8px 12px', fontSize: 12, fontWeight: 600, marginBottom: 16,
                    }}>
                      {formatINR(editingTxn.pledgePrincipalAmount)} pledged from principal (held) — releases automatically once this is approved or rejected.
                    </div>
                  )}
                  <div className="ob-field">
                    <label>Type</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <div
                        onClick={() => setPendingEditType('invest')}
                        style={{
                          flex: 1, textAlign: 'center', padding: '12px 0', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                          background: pendingEditType === 'invest' ? '#e8f8ee' : '#fafafa',
                          color: pendingEditType === 'invest' ? '#27ae60' : '#aaa',
                          border: `1.5px solid ${pendingEditType === 'invest' ? '#27ae60' : '#eee'}`,
                        }}
                      >Invest</div>
                      <div
                        onClick={() => setPendingEditType('withdraw')}
                        style={{
                          flex: 1, textAlign: 'center', padding: '12px 0', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                          background: pendingEditType === 'withdraw' ? '#fde8e8' : '#fafafa',
                          color: pendingEditType === 'withdraw' ? '#eb4d4b' : '#aaa',
                          border: `1.5px solid ${pendingEditType === 'withdraw' ? '#eb4d4b' : '#eee'}`,
                        }}
                      >Withdraw</div>
                    </div>
                  </div>
                  <div className="ob-field">
                    <label>Amount ({'\u20B9'})</label>
                    <input
                      className="ob-input"
                      placeholder="Enter amount in rupees"
                      value={pendingEditAmount}
                      onChange={(e) => setPendingEditAmount(e.target.value.replace(/[^0-9]/g, ''))}
                      inputMode="numeric"
                    />
                    {Number(pendingEditAmount) > 0 && (
                      <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>{formatINR(Number(pendingEditAmount))}</div>
                    )}
                  </div>
                  <div className="ob-field">
                    <label>Date</label>
                    <div style={{ display: 'flex' }}>
                      <DatePicker
                        selected={pendingEditDate}
                        onChange={(d) => setPendingEditDate(d)}
                        dateFormat="dd MMM yyyy"
                        maxDate={new Date()}
                        showYearDropdown
                        showMonthDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={10}
                        popperPlacement="bottom-start"
                        customInput={<input className="ob-input" readOnly style={{ cursor: 'pointer' }} />}
                      />
                    </div>
                  </div>
                  <div className="ob-field">
                    <label>Status</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {[
                        { key: 'pending', label: 'Pending', bg: '#fff3e0', fg: '#e67e22' },
                        { key: 'successful', label: 'Approve', bg: '#e8f8ee', fg: '#27ae60' },
                        { key: 'rejected', label: 'Reject', bg: '#fde8e8', fg: '#eb4d4b' },
                      ].map((s) => (
                        <div
                          key={s.key}
                          onClick={() => setPendingEditStatus(s.key)}
                          style={{
                            flex: 1, textAlign: 'center', padding: '12px 0', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                            background: pendingEditStatus === s.key ? s.bg : '#fafafa',
                            color: pendingEditStatus === s.key ? s.fg : '#aaa',
                            border: `1.5px solid ${pendingEditStatus === s.key ? s.fg : '#eee'}`,
                          }}
                        >{s.label}</div>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                    <button
                      className="ob-back-btn"
                      style={{ flex: 1 }}
                      onClick={() => { setEditingPendingId(null); setPendingEditMsg(''); }}
                    >Cancel</button>
                    <button
                      className="ob-next-btn"
                      style={{ flex: 1 }}
                      onClick={handleSavePendingEdit}
                    >Save</button>
                  </div>
                  <button
                    onClick={() => {
                      if (!editingPendingId) return;
                      if (!window.confirm('Permanently delete this transaction? This cannot be undone.')) return;
                      const id = editingPendingId;
                      setEditingPendingId(null);
                      setPendingEditMsg('');
                      deleteDoc(doc(db, 'transactions', id))
                        .catch((e) => { console.error(e); setPendingEditMsg('Error deleting'); });
                    }}
                    style={{
                      width: '100%', marginTop: 10,
                      background: '#fff', color: '#eb4d4b',
                      border: '1.5px solid #eb4d4b', borderRadius: 8, padding: '11px',
                      fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    }}
                  >Delete transaction</button>
                  {pendingEditMsg && (
                    <div style={{ fontSize: 12, marginTop: 10, color: '#eb4d4b' }}>
                      {pendingEditMsg}
                    </div>
                  )}
                </div>
              )}

              {/* Free-form "Record a Transaction" self-submit form removed — Profit Withdrawal
                  (on the wallet card) is now the only way to create a new pending request; the
                  form's handler (handleSubmitRequest) and its state are left in place, unused,
                  in case this needs to be restored. */}
              {!isAdmin && (
                <div style={{ background: '#f9f9f6', borderRadius: 12, padding: '16px 18px', marginBottom: 16, fontSize: 12.5, color: '#666', lineHeight: 1.6 }}>
                  To request a withdrawal, use the Profit Withdrawal option on your Wallet screen.
                </div>
              )}

              {/* Pending list */}
              <div style={{ background: '#fff', borderRadius: 12, padding: 0, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                <div style={{ padding: '14px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>
                    {isAdmin ? 'All pending' : 'Your pending'}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#e67e22', background: '#fff3e0', padding: '2px 8px', borderRadius: 10 }}>
                    {pendingTxns.length}
                  </span>
                </div>
                {pendingTxns.length === 0 && (
                  <div style={{ padding: 24, textAlign: 'center', color: '#bbb', fontSize: 13 }}>
                    No pending transactions.
                  </div>
                )}
                {pendingTxns.map((t, i) => {
                  const isWithdrawalRequest = t.origin === 'withdrawalRequest';
                  const isWithdraw = t.sender === 'achintya';
                  const isReversal = t.type === 'reversal' || t.remarks === 'Reversed';
                  const counterparty = t.sender === 'achintya' ? t.receiver : t.sender;
                  const handleRowClick = () => {
                    if (!isAdmin || t.syncing || isWithdrawalRequest) return;
                    // Open inline editor in this same tab; admin picks Approve/Reject/Pending.
                    // withdrawalRequest rows use the Approve/Reject buttons below instead \u2014 no
                    // type/status to edit, they're always a pending withdrawal at a fixed amount.
                    setEditingPendingId(t.id);
                    setPendingEditType(isWithdraw ? 'withdraw' : 'invest');
                    setPendingEditAmount(String(t.amount));
                    setPendingEditDate(new Date(t.date));
                    setPendingEditStatus(t.status || 'pending');
                    setPendingEditMsg('');
                  };
                  return (
                    <div key={t.id} onClick={handleRowClick} style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '14px 16px',
                      borderBottom: i < pendingTxns.length - 1 ? '1px solid #f0f0f0' : 'none',
                      borderLeft: `3px solid ${isReversal ? '#e67e22' : isWithdraw ? '#eb4d4b' : '#27ae60'}`,
                      background: editingPendingId === t.id ? '#f8f8ff' : 'transparent',
                      cursor: isAdmin && !t.syncing && !isWithdrawalRequest ? 'pointer' : 'default',
                      opacity: t.syncing ? 0.5 : 1,
                    }}>
                      {t.syncing && <span className="sync-spinner" />}
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 16, fontWeight: 700, color: '#111' }}>
                            {isWithdraw ? '-' : '+'}{'\u20B9'}{Number(t.amount).toLocaleString('en-IN')}
                          </span>
                          <span style={{ fontSize: 11, color: '#999' }}>
                            {new Date(t.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                          <span style={{ fontSize: 11, color: isReversal ? '#e67e22' : isWithdraw ? '#eb4d4b' : '#27ae60', fontWeight: 600 }}>
                            {isWithdrawalRequest ? (t.ledgerRecordedSeparately ? 'Refund' : 'Profit Withdrawal') : isReversal ? 'Reversed' : isWithdraw ? 'Withdrawal' : 'Investment'}
                          </span>
                          {isAdmin && (
                            <span style={{ fontSize: 11, color: '#666' }}>
                              &middot; {counterparty}
                            </span>
                          )}
                          {t.status === 'processing' ? (
                            <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: '#e3f0fd', color: '#1565c0' }}>
                              Processing
                            </span>
                          ) : (
                            <span style={{
                              fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                              background: '#fff3e0', color: '#e67e22',
                            }}>
                              Pending
                            </span>
                          )}
                          {t.pledgeStatus === 'held' && (
                            <span style={{
                              fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                              background: '#f3e8d8', color: '#7a5a10',
                            }} title={`${formatINR(t.pledgePrincipalAmount)} pledged from principal, held until this resolves`}>
                              {formatINR(t.pledgePrincipalAmount)} pledged
                            </span>
                          )}
                        </div>
                        {isAdmin && isWithdrawalRequest && (
                          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const msg = t.ledgerRecordedSeparately
                                  ? `Cancel this ${formatINR(t.amount)} refund for ${counterparty}? This only clears the "Processing" flag — it does not touch their wallet ledger.`
                                  : `Reject this ${formatINR(t.amount)} profit withdrawal request for ${counterparty}? Their pledged ${formatINR(t.pledgePrincipalAmount)} will be released.`;
                                if (window.confirm(msg)) {
                                  handleResolveWithdrawalRequest(t.id, false);
                                }
                              }}
                              style={{
                                flex: 1, padding: '8px 0', borderRadius: 8, border: '1.5px solid #eb4d4b',
                                background: '#fff', color: '#eb4d4b', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                              }}
                            >{t.ledgerRecordedSeparately ? 'Cancel' : 'Reject'}</button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const msg = t.ledgerRecordedSeparately
                                  ? `Mark this ${formatINR(t.amount)} refund for ${counterparty} as complete? Their wallet was already debited when this was created — this only clears the "Processing" flag.`
                                  : `Approve this ${formatINR(t.amount)} profit withdrawal request for ${counterparty}? This settles it as a debit on their wallet today.`;
                                if (window.confirm(msg)) {
                                  handleResolveWithdrawalRequest(t.id, true);
                                }
                              }}
                              style={{
                                flex: 1, padding: '8px 0', borderRadius: 8, border: 'none',
                                background: '#27ae60', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                              }}
                            >{t.ledgerRecordedSeparately ? 'Mark Complete' : 'Approve'}</button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })()}

          {/* ---- Bank Statements (LOCAL DEV ONLY) ---- */}
          {tab === 'bankstmts' && process.env.NODE_ENV === 'development' && isAdmin && (() => {
            // Confident username matcher: every hyphen-token of the username must appear
            // as a standalone word in the remarks (case-insensitive). For single-word
            // usernames, the token must be ≥4 chars to avoid noise. Pick the username
            // with the most matched tokens (most specific).
            const usernames = (allUsers || []).map(u => u.key).filter(k => k && k !== 'achintya' && k !== 'ayana' && k !== 'undefined');
            const matchUser = (remarks) => {
              if (!remarks) return null;
              const r = remarks.toLowerCase();
              let best = null;
              let bestScore = 0;
              for (const u of usernames) {
                const tokens = u.split('-').filter(t => t.length >= 4);
                if (tokens.length === 0) continue;
                const allMatch = tokens.every(t => new RegExp(`\\b${t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(r));
                if (allMatch && tokens.length > bestScore) {
                  best = u;
                  bestScore = tokens.length;
                }
              }
              return best;
            };

            // Build a lookup over every booked Firestore txn, keyed by (date|amount|direction).
            // direction 'cr' = money into achintya (sender != achintya), 'dr' = money out of achintya.
            // Multiple txns on the same key are stacked in the same slot.
            const txnIndex = new Map();
            for (const t of (allTxnsCache || [])) {
              const dir = t.sender === 'achintya' ? 'dr' : 'cr';
              const counterparty = dir === 'dr' ? t.receiver : t.sender;
              const key = `${t.date}|${Number(t.amount)}|${dir}`;
              if (!txnIndex.has(key)) txnIndex.set(key, []);
              txnIndex.get(key).push({ id: t.id, user: counterparty, status: t.status || '' });
            }
            const findTxns = (r) => {
              const dir = (r.debit && Number(r.debit) > 0) ? 'dr' : 'cr';
              const amt = Number(r.debit || r.credit || 0);
              if (!amt) return [];
              return txnIndex.get(`${r.date}|${amt}|${dir}`) || [];
            };

            const rows = bankStmts || [];
            const enriched = rows.map(r => {
              const txnMatches = findTxns(r);
              return { ...r, matched: matchUser(r.remarks), txnMatches };
            });
            const filtered = enriched.filter(r => {
              if (bankStmtsFilter === 'matched' && !r.matched) return false;
              if (bankStmtsFilter === 'unmatched' && r.matched) return false;
              if (bankStmtsSearch) {
                const q = bankStmtsSearch.toLowerCase();
                const txnUsers = (r.txnMatches || []).map(m => m.user).join(' ').toLowerCase();
                if (!r.remarks.toLowerCase().includes(q) && !(r.matched || '').includes(q) && !r.date.includes(q) && !txnUsers.includes(q)) return false;
              }
              return true;
            });
            const matchedCount = enriched.filter(r => r.matched).length;
            const bookedCount = enriched.filter(r => r.txnMatches && r.txnMatches.length > 0).length;
            const totalDr = filtered.reduce((s, r) => s + (r.debit || 0), 0);
            const totalCr = filtered.reduce((s, r) => s + (r.credit || 0), 0);
            return (
              <div>
                <div onClick={() => setTab('transactions')} style={{ fontSize: 13, cursor: 'pointer', color: '#888', marginBottom: 16 }}>
                  &larr; Back
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6, color: '#111', display: 'flex', alignItems: 'center', gap: 8 }}>
                  Bank Statements
                  <span style={{ background: '#999', color: '#fff', fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 10 }}>LOCAL ONLY</span>
                </div>
                <div style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
                  {bankStmtsErr && <span style={{ color: '#eb4d4b' }}>{bankStmtsErr}</span>}
                  {!bankStmtsErr && bankStmts === null && 'Loading…'}
                  {!bankStmtsErr && bankStmts !== null && (
                    <>
                      {rows.length} rows parsed from ICICI statements (2019–2026).
                      {' '}<strong>{matchedCount}</strong> name-matched in remarks;
                      {' '}<strong>{bookedCount}</strong> linked to a booked /transactions doc;
                      {' '}<strong>{rows.length - bookedCount}</strong> unbooked.
                    </>
                  )}
                </div>
                <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <button
                    onClick={handleUploadStmtsToFirestore}
                    style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #111', background: '#111', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    Upload to Firestore (bankStatements/data)
                  </button>
                  {bankStmtsUploadStatus && <span style={{ fontSize: 12, color: bankStmtsUploadStatus.startsWith('Upload failed') ? '#eb4d4b' : '#2e8b57' }}>{bankStmtsUploadStatus}</span>}
                </div>

                {bankStmts !== null && (
                  <>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                      {[
                        { k: 'all', l: `All (${rows.length})` },
                        { k: 'matched', l: `Matched (${matchedCount})` },
                        { k: 'unmatched', l: `Unmatched (${rows.length - matchedCount})` },
                      ].map(o => (
                        <div key={o.k} onClick={() => setBankStmtsFilter(o.k)}
                          style={{
                            padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            background: bankStmtsFilter === o.k ? '#111' : '#fafafa',
                            color: bankStmtsFilter === o.k ? '#fff' : '#666',
                            border: '1px solid ' + (bankStmtsFilter === o.k ? '#111' : '#eee'),
                          }}>{o.l}</div>
                      ))}
                      <input
                        placeholder="Search remarks / user / date…"
                        value={bankStmtsSearch}
                        onChange={(e) => setBankStmtsSearch(e.target.value)}
                        style={{ flex: 1, minWidth: 180, padding: '6px 12px', borderRadius: 8, border: '1px solid #eee', fontSize: 12, outline: 'none' }}
                      />
                    </div>

                    <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>
                      Showing <strong>{filtered.length}</strong> rows · Dr {'\u20B9'}{totalDr.toLocaleString('en-IN')} · Cr {'\u20B9'}{totalCr.toLocaleString('en-IN')}
                    </div>

                    <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '88px 1fr 90px 90px 120px 140px 80px', gap: 8, padding: '10px 12px', fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
                        <div>Date</div><div>Remarks</div><div style={{ textAlign: 'right' }}>Debit</div><div style={{ textAlign: 'right' }}>Credit</div><div>Name in remarks</div><div>Booked to user</div><div>Status</div>
                      </div>
                      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        {filtered.map((r, i) => {
                          const booked = r.txnMatches || [];
                          const hasBooking = booked.length > 0;
                          // Colour cue: green = booked successfully · red = booked but failed ·
                          // yellow = name matches but nothing booked · grey = nothing at all
                          const anyFailed = booked.some(m => m.status === 'failed');
                          const allSuccessful = hasBooking && booked.every(m => m.status === 'successful');
                          let bg = '#fffaf3';
                          if (allSuccessful) bg = '#f5fcf7';
                          else if (anyFailed) bg = '#fdf1f0';
                          else if (hasBooking) bg = '#fff8e6';
                          else if (r.matched) bg = '#fffaf3';
                          else bg = '#fafafa';
                          return (
                          <div key={`${r.source}-${r.sl}`} style={{
                            display: 'grid', gridTemplateColumns: '88px 1fr 90px 90px 120px 140px 80px', gap: 8,
                            alignItems: 'start',
                            padding: '10px 12px', fontSize: 12, color: '#222',
                            borderBottom: i < filtered.length - 1 ? '1px solid #f5f5f5' : 'none',
                            background: bg,
                          }}>
                            <div style={{ color: '#666', fontVariantNumeric: 'tabular-nums' }}>{r.date}</div>
                            <div style={{ wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: 1.4 }}>{r.remarks}</div>
                            <div style={{ textAlign: 'right', color: r.debit ? '#eb4d4b' : '#ccc', fontVariantNumeric: 'tabular-nums', fontWeight: r.debit ? 600 : 400 }}>
                              {r.debit ? `${'\u20B9'}${r.debit.toLocaleString('en-IN')}` : '—'}
                            </div>
                            <div style={{ textAlign: 'right', color: r.credit ? '#27ae60' : '#ccc', fontVariantNumeric: 'tabular-nums', fontWeight: r.credit ? 600 : 400 }}>
                              {r.credit ? `${'\u20B9'}${r.credit.toLocaleString('en-IN')}` : '—'}
                            </div>
                            <div style={{ fontSize: 11 }}>
                              {r.matched
                                ? <span style={{ background: '#e8f8ee', color: '#1f8e4a', padding: '2px 8px', borderRadius: 6, fontWeight: 600 }}>{r.matched}</span>
                                : <span style={{ color: '#bbb' }}>—</span>}
                            </div>
                            <div style={{ fontSize: 11, display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {hasBooking
                                ? booked.map(m => (
                                    <span key={m.id} title={m.id}
                                      style={{
                                        background: m.user === r.matched ? '#e8f8ee' : '#fef2e6',
                                        color: m.user === r.matched ? '#1f8e4a' : '#b4722a',
                                        padding: '2px 8px', borderRadius: 6, fontWeight: 600,
                                        wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: 1.3,
                                      }}>{m.user}</span>
                                  ))
                                : <span style={{ color: '#bbb' }}>—</span>}
                            </div>
                            <div style={{ fontSize: 11, display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {hasBooking
                                ? booked.map(m => {
                                    const c = m.status === 'successful' ? { bg: '#e8f8ee', fg: '#1f8e4a' }
                                            : m.status === 'failed' ? { bg: '#fbe3e1', fg: '#b53a36' }
                                            : m.status === 'pending' ? { bg: '#fff3cd', fg: '#8a6d1f' }
                                            : { bg: '#eee', fg: '#666' };
                                    return (
                                      <span key={m.id} style={{
                                        background: c.bg, color: c.fg, padding: '2px 8px',
                                        borderRadius: 6, fontWeight: 600, textAlign: 'center',
                                      }}>{m.status || '—'}</span>
                                    );
                                  })
                                : <span style={{ color: '#bbb' }}>—</span>}
                            </div>
                          </div>
                          );
                        })}
                        {filtered.length === 0 && (
                          <div style={{ padding: 24, textAlign: 'center', color: '#bbb', fontSize: 13 }}>No rows match the filter.</div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {/* ---- Recommender Statements: reads the existing `rcommndr` Firestore collection
               (Achintya's own ICICI current account, already parsed by a past session) —
               assign + approve unbooked rows above the ₹50,000 threshold. ---- */}
          {tab === 'recommenderstmts' && isAdmin && (() => {
            const usernames = (allUsers || []).map(u => u.key).filter(k => k && k !== 'achintya' && k !== 'ayana' && k !== 'undefined');
            const rows = recRows || [];
            const txns = allTxnsCache || [];

            // Two ways a row can already be booked: (1) a transactions doc carries this row's
            // Firestore doc id in rcommndrId (the linkage this feature itself writes, and the
            // convention ledger/verify_investment.py already used), or (2) a legacy match by
            // (date, amount, direction) predating that field — same pattern as the Bank
            // Statements tab's txnIndex above.
            const byRcommndrId = new Map();
            const txnIndex = new Map();
            for (const t of txns) {
              if (t.rcommndrId) byRcommndrId.set(t.rcommndrId, t);
              const dir = t.sender === 'achintya' ? 'dr' : 'cr'; // dr = paid out by achintya (withdrawal); cr = paid in (deposit/invest)
              const counterparty = dir === 'dr' ? t.receiver : t.sender;
              const key = `${t.date}|${Number(t.amount)}|${dir}`;
              if (!txnIndex.has(key)) txnIndex.set(key, []);
              txnIndex.get(key).push({ id: t.id, user: counterparty, status: t.status || '' });
            }

            const rowAmount = (r) => Math.max(Number(r.withdrawal) || 0, Number(r.deposit) || 0);
            const inferDirection = (r) => (Number(r.deposit) > 0 ? 'invest' : 'withdraw');
            const bookingFor = (r) => {
              const direct = byRcommndrId.get(r.id);
              if (direct) {
                const counterparty = direct.sender === 'achintya' ? direct.receiver : direct.sender;
                return [{ id: direct.id, user: counterparty, status: direct.status || '' }];
              }
              const amt = rowAmount(r);
              if (!amt) return [];
              const dir = Number(r.withdrawal) > 0 ? 'dr' : 'cr';
              return txnIndex.get(`${r.date}|${amt}|${dir}`) || [];
            };

            const enriched = rows.map(r => {
              const booked = bookingFor(r);
              const amt = rowAmount(r);
              const dismissed = !!r.ignoredAt;
              return {
                ...r,
                amt,
                booked,
                dismissed,
                actionable: booked.length === 0 && !dismissed && amt > RECOMMENDER_THRESHOLD,
                belowThreshold: booked.length === 0 && !dismissed && amt <= RECOMMENDER_THRESHOLD,
              };
            });
            const filtered = enriched.filter(r => {
              if (recStmtsFilter === 'actionable' && !r.actionable) return false;
              if (recStmtsFilter === 'processed' && r.booked.length === 0) return false;
              if (recStmtsFilter === 'dismissed' && !r.dismissed) return false;
              if (recStmtsFilter === 'belowThreshold' && !r.belowThreshold) return false;
              if (recStmtsSearch) {
                const q = recStmtsSearch.toLowerCase();
                const bookedUsers = r.booked.map(b => b.user).join(' ').toLowerCase();
                if (!(r.remarks || '').toLowerCase().includes(q) && !r.date.includes(q) && !bookedUsers.includes(q)) return false;
              }
              return true;
            });
            const actionableCount = enriched.filter(r => r.actionable).length;
            const processedCount = enriched.filter(r => r.booked.length > 0).length;
            const dismissedCount = enriched.filter(r => r.dismissed).length;
            const belowThresholdCount = enriched.filter(r => r.belowThreshold).length;
            const loaded = recRows !== null && allTxnsCache !== null;
            return (
              <div>
                <div onClick={() => setTab('transactions')} style={{ fontSize: 13, cursor: 'pointer', color: '#888', marginBottom: 16 }}>
                  &larr; Back
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6, color: '#111' }}>
                  Recommender Statements
                </div>
                <div style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
                  {recRowsErr && <span style={{ color: '#eb4d4b' }}>{recRowsErr}</span>}
                  {!recRowsErr && !loaded && 'Loading…'}
                  {!recRowsErr && loaded && (
                    <>
                      {rows.length} row(s) from the <code>rcommndr</code> statement &middot; <strong>{actionableCount}</strong> actionable
                      (&gt;{'₹'}{RECOMMENDER_THRESHOLD.toLocaleString('en-IN')}) &middot;{' '}
                      <strong>{processedCount}</strong> already booked &middot; <strong>{dismissedCount}</strong> not investor txns &middot;{' '}
                      <strong>{belowThresholdCount}</strong> below threshold.
                    </>
                  )}
                </div>

                {loaded && (
                  <>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                      {[
                        { k: 'actionable', l: `Actionable (${actionableCount})` },
                        { k: 'processed', l: `Processed (${processedCount})` },
                        { k: 'dismissed', l: `Not investor txns (${dismissedCount})` },
                        { k: 'belowThreshold', l: `Below threshold (${belowThresholdCount})` },
                        { k: 'all', l: `All (${rows.length})` },
                      ].map(o => (
                        <div key={o.k} onClick={() => setRecStmtsFilter(o.k)}
                          style={{
                            padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            background: recStmtsFilter === o.k ? '#111' : '#fafafa',
                            color: recStmtsFilter === o.k ? '#fff' : '#666',
                            border: '1px solid ' + (recStmtsFilter === o.k ? '#111' : '#eee'),
                          }}>{o.l}</div>
                      ))}
                      <input
                        placeholder="Search remarks / user / date…"
                        value={recStmtsSearch}
                        onChange={(e) => setRecStmtsSearch(e.target.value)}
                        style={{ flex: 1, minWidth: 180, padding: '6px 12px', borderRadius: 8, border: '1px solid #eee', fontSize: 12, outline: 'none' }}
                      />
                    </div>

                    <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '84px 1fr 84px 84px 260px', gap: 8, padding: '10px 12px', fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
                        <div>Date</div><div>Remarks</div><div style={{ textAlign: 'right' }}>Withdrawal</div><div style={{ textAlign: 'right' }}>Deposit</div><div>Assign / Status</div>
                      </div>
                      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        {filtered.map((r) => {
                          const action = recStmtsRowAction[r.id] || { username: '', direction: inferDirection(r) };
                          const setAction = (patch) => setRecStmtsRowAction(prev => ({ ...prev, [r.id]: { ...action, ...patch } }));
                          let bg = '#fafafa';
                          if (r.booked.length > 0) bg = '#f5fcf7';
                          else if (r.dismissed) bg = '#f2f2f2';
                          else if (r.actionable) bg = '#fff8e6';
                          return (
                            <div key={r.id} style={{
                              display: 'grid', gridTemplateColumns: '84px 1fr 84px 84px 260px', gap: 8,
                              alignItems: 'start', padding: '10px 12px', fontSize: 12, color: '#222',
                              borderBottom: '1px solid #f5f5f5', background: bg,
                            }}>
                              <div style={{ color: '#666', fontVariantNumeric: 'tabular-nums' }}>{r.date}</div>
                              <div style={{ wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: 1.4 }}>{r.remarks}</div>
                              <div style={{ textAlign: 'right', color: r.withdrawal ? '#eb4d4b' : '#ccc', fontVariantNumeric: 'tabular-nums', fontWeight: r.withdrawal ? 600 : 400 }}>
                                {r.withdrawal ? `${'₹'}${Number(r.withdrawal).toLocaleString('en-IN')}` : '—'}
                              </div>
                              <div style={{ textAlign: 'right', color: r.deposit ? '#27ae60' : '#ccc', fontVariantNumeric: 'tabular-nums', fontWeight: r.deposit ? 600 : 400 }}>
                                {r.deposit ? `${'₹'}${Number(r.deposit).toLocaleString('en-IN')}` : '—'}
                              </div>
                              <div>
                                {r.booked.length > 0 && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {r.booked.map(b => (
                                      <span key={b.id} title={b.id} style={{ background: '#e8f8ee', color: '#1f8e4a', padding: '2px 8px', borderRadius: 6, fontWeight: 600, fontSize: 11, display: 'inline-block', width: 'fit-content' }}>
                                        {b.user} &middot; {b.status || 'successful'}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {r.dismissed && r.booked.length === 0 && (
                                  <span style={{ color: '#999', fontSize: 11 }} title={r.ignoredAt}>Not an investor txn (by {r.ignoredBy})</span>
                                )}
                                {r.belowThreshold && (
                                  <span style={{ color: '#bbb', fontSize: 11 }}>Below {'₹'}{RECOMMENDER_THRESHOLD.toLocaleString('en-IN')} threshold</span>
                                )}
                                {r.actionable && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                      {['invest', 'withdraw'].map(d => (
                                        <div key={d} onClick={() => setAction({ direction: d })}
                                          style={{
                                            padding: '3px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                                            background: action.direction === d ? '#111' : '#fff',
                                            color: action.direction === d ? '#fff' : '#666',
                                            border: '1px solid ' + (action.direction === d ? '#111' : '#ddd'),
                                            textTransform: 'uppercase',
                                          }}>{d}</div>
                                      ))}
                                    </div>
                                    <select
                                      value={action.username}
                                      onChange={(e) => setAction({ username: e.target.value })}
                                      style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 11, color: '#111' }}
                                    >
                                      <option value="">Pick user…</option>
                                      {usernames.map(u => <option key={u} value={u}>{u}</option>)}
                                    </select>
                                    <div style={{ display: 'flex', gap: 6 }}>
                                      <button
                                        onClick={() => handleApproveRecommenderRow(r, action.username, action.direction)}
                                        disabled={!action.username || recStmtsBusyId === r.id}
                                        style={{
                                          padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: action.username ? 'pointer' : 'default',
                                          background: action.username ? '#2e8b57' : '#ccc', color: '#fff', border: 'none',
                                        }}
                                      >
                                        {recStmtsBusyId === r.id ? 'Approving…' : 'Approve'}
                                      </button>
                                      <button
                                        onClick={() => handleDismissRecommenderRow(r)}
                                        disabled={recStmtsBusyId === r.id}
                                        title="Mark as not an investor transaction (e.g. own-name transfer, bank interest, unrelated business income)"
                                        style={{
                                          padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                          background: '#fff', color: '#888', border: '1px solid #ddd',
                                        }}
                                      >
                                        Not investor
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {filtered.length === 0 && (
                          <div style={{ padding: 24, textAlign: 'center', color: '#bbb', fontSize: 13 }}>No rows match the filter.</div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {/* ---- Statement: browse + classify raw RTDB /statement rows ----
                Pure tag-a-row-with-a-username feature — writes ONLY `username` onto the exact
                RTDB row (update(), preserving every other field), nothing else. Does not create
                anything in Firestore `transactions` or RTDB `/wallets` — turning a classified
                row into an actual booked transaction/wallet entry would be a natural next step
                but is explicitly out of scope for this feature. No amount threshold — every row
                is visible/searchable/classifiable. ---- */}
          {tab === 'statement' && isAdmin && (() => {
            const usernames = (allUsers || []).map(u => u.key).filter(k => k && k !== 'achintya' && k !== 'ayana' && k !== 'undefined');
            const rows = stmtRows || [];
            const enriched = rows.map(r => ({
              ...r,
              debitNum: Number(r.debit) || 0,
              creditNum: Number(r.credit) || 0,
              classified: !!r.username,
            }));
            const filtered = enriched.filter(r => {
              if (stmtFilter === 'unclassified' && r.classified) return false;
              if (stmtFilter === 'classified' && !r.classified) return false;
              if (stmtSearch) {
                const q = stmtSearch.toLowerCase();
                if (!(r.remarks || '').toLowerCase().includes(q) && !(r.date || '').includes(q) && !(r.username || '').toLowerCase().includes(q)) return false;
              }
              return true;
            });
            const classifiedCount = enriched.filter(r => r.classified).length;
            const unclassifiedCount = enriched.length - classifiedCount;
            const loaded = stmtRows !== null;
            return (
              <div>
                <div onClick={() => setTab('transactions')} style={{ fontSize: 13, cursor: 'pointer', color: '#888', marginBottom: 16 }}>
                  &larr; Back
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6, color: '#111' }}>
                  Statement
                </div>
                <div style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
                  {stmtErr && <span style={{ color: '#eb4d4b' }}>{stmtErr}</span>}
                  {!stmtErr && !loaded && 'Loading…'}
                  {!stmtErr && loaded && (
                    <>
                      {rows.length} row(s) from <code>/statement</code> &middot; <strong>{classifiedCount}</strong> classified &middot;{' '}
                      <strong>{unclassifiedCount}</strong> unclassified.
                    </>
                  )}
                </div>

                {loaded && (
                  <>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                      {[
                        { k: 'unclassified', l: `Unclassified (${unclassifiedCount})` },
                        { k: 'classified', l: `Classified (${classifiedCount})` },
                        { k: 'all', l: `All (${rows.length})` },
                      ].map(o => (
                        <div key={o.k} onClick={() => setStmtFilter(o.k)}
                          style={{
                            padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            background: stmtFilter === o.k ? '#111' : '#fafafa',
                            color: stmtFilter === o.k ? '#fff' : '#666',
                            border: '1px solid ' + (stmtFilter === o.k ? '#111' : '#eee'),
                          }}>{o.l}</div>
                      ))}
                      <input
                        placeholder="Search remarks / user / date…"
                        value={stmtSearch}
                        onChange={(e) => setStmtSearch(e.target.value)}
                        style={{ flex: 1, minWidth: 180, padding: '6px 12px', borderRadius: 8, border: '1px solid #eee', fontSize: 12, outline: 'none' }}
                      />
                    </div>

                    <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>
                      Showing <strong>{filtered.length}</strong> of {rows.length} rows
                    </div>

                    <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '84px 1fr 84px 84px 200px', gap: 8, padding: '10px 12px', fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
                        <div>Date</div><div>Remarks</div><div style={{ textAlign: 'right' }}>Debit</div><div style={{ textAlign: 'right' }}>Credit</div><div>Classify</div>
                      </div>
                      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        {filtered.map((r) => {
                          const pickedUsername = stmtRowAction[r._key] || '';
                          let bg = '#fafafa';
                          if (r.classified) bg = '#f5fcf7';
                          return (
                            <div key={r._key} style={{
                              display: 'grid', gridTemplateColumns: '84px 1fr 84px 84px 200px', gap: 8,
                              alignItems: 'start', padding: '10px 12px', fontSize: 12, color: '#222',
                              borderBottom: '1px solid #f5f5f5', background: bg,
                            }}>
                              <div style={{ color: '#666', fontVariantNumeric: 'tabular-nums' }}>{r.date}</div>
                              <div style={{ wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: 1.4 }}>{r.remarks}</div>
                              <div style={{ textAlign: 'right', color: r.debitNum ? '#eb4d4b' : '#ccc', fontVariantNumeric: 'tabular-nums', fontWeight: r.debitNum ? 600 : 400 }}>
                                {r.debitNum ? `${'₹'}${r.debitNum.toLocaleString('en-IN')}` : '—'}
                              </div>
                              <div style={{ textAlign: 'right', color: r.creditNum ? '#27ae60' : '#ccc', fontVariantNumeric: 'tabular-nums', fontWeight: r.creditNum ? 600 : 400 }}>
                                {r.creditNum ? `${'₹'}${r.creditNum.toLocaleString('en-IN')}` : '—'}
                              </div>
                              <div>
                                {r.classified ? (
                                  <span style={{ background: '#e8f8ee', color: '#1f8e4a', padding: '2px 8px', borderRadius: 6, fontWeight: 600, fontSize: 11, display: 'inline-block', width: 'fit-content' }}>
                                    {r.username}
                                  </span>
                                ) : (
                                  <div style={{ display: 'flex', gap: 6 }}>
                                    <select
                                      value={pickedUsername}
                                      onChange={(e) => setStmtRowAction(prev => ({ ...prev, [r._key]: e.target.value }))}
                                      style={{ flex: 1, padding: '5px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 11, color: '#111' }}
                                    >
                                      <option value="">Pick user…</option>
                                      {usernames.map(u => <option key={u} value={u}>{u}</option>)}
                                    </select>
                                    <button
                                      onClick={() => handleClassifyStmtRow(r._key, pickedUsername)}
                                      disabled={!pickedUsername || stmtBusyKey === r._key}
                                      style={{
                                        padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: pickedUsername ? 'pointer' : 'default',
                                        background: pickedUsername ? '#2e8b57' : '#ccc', color: '#fff', border: 'none',
                                      }}
                                    >
                                      {stmtBusyKey === r._key ? '…' : 'Assign'}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {filtered.length === 0 && (
                          <div style={{ padding: 24, textAlign: 'center', color: '#bbb', fontSize: 13 }}>No rows match the filter.</div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {/* ---- Admin Settings: user-management actions, decoupled from the header search /
                searched-wallet view (that view must stay a pure mirror of what the user
                themselves sees — no admin controls mixed in). Has its own user picker (same
                allUsers search/select pattern as the header search box) so picking someone here
                never touches selectedUsername or navigates admin "into" that user's wallet. ---- */}
          {tab === 'adminsettings' && isAdmin && (() => {
            const filteredUsers = (allUsers || [])
              .filter(u => u.key && u.key !== 'achintya' && u.key !== 'ayana' && u.key !== 'undefined')
              .filter(u => u.key.toLowerCase().includes(adminSettingsSearch.toLowerCase()));
            const eligible = adminSettingsPrincipal !== null && adminSettingsPrincipal >= PROFIT_WITHDRAWAL_MIN_PRINCIPAL;
            // Multiplier/pledge% are configurable per-action (not hardcoded to 1×/10%) — see
            // handleCreateProfitWithdrawal. Both derived amounts are computed from principal, so
            // the preview below always matches exactly what the button will create.
            const multiplierNum = Number(adminSettingsMultiplier);
            const pledgePercentNum = Number(adminSettingsPledgePercent);
            const validMultiplier = adminSettingsMultiplier !== '' && multiplierNum >= 1;
            const validPledgePercent = adminSettingsPledgePercent !== '' && pledgePercentNum >= 1 && pledgePercentNum <= 100;
            const previewAmount = eligible && validMultiplier ? Math.round(adminSettingsPrincipal * multiplierNum) : null;
            const previewPledge = eligible && validPledgePercent ? Math.round(adminSettingsPrincipal * pledgePercentNum / 100) : null;
            return (
              <div>
                <div onClick={() => setTab('transactions')} style={{ fontSize: 13, cursor: 'pointer', color: '#888', marginBottom: 16 }}>
                  &larr; Back
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6, color: '#111' }}>
                  Admin Settings
                </div>
                <div style={{ fontSize: 13, color: '#666', marginBottom: 20 }}>
                  User-management actions. Pick a user below — independent of the header search box above.
                </div>

                <div style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#111', marginBottom: 8 }}>User</div>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      placeholder={adminSettingsUsername
                        ? adminSettingsUsername.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                        : 'Search user…'}
                      value={adminSettingsSearch}
                      onChange={(e) => setAdminSettingsSearch(e.target.value)}
                      style={{
                        padding: '10px 12px', borderRadius: 8, border: '1.5px solid #ddd', fontSize: 13,
                        width: '100%', boxSizing: 'border-box', fontWeight: 600, color: '#111',
                        background: adminSettingsUsername ? '#f0f8ff' : '#fff',
                      }}
                    />
                    {adminSettingsUsername && !adminSettingsSearch && (
                      <span
                        onClick={() => { setAdminSettingsUsername(null); }}
                        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: '#999', cursor: 'pointer', lineHeight: 1 }}
                      >&times;</span>
                    )}
                    {adminSettingsSearch && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 100,
                        background: '#fff', borderRadius: 10, overflow: 'hidden',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto',
                      }}>
                        {filteredUsers.map(u => (
                          <div key={u.key}
                            onClick={() => { setAdminSettingsUsername(u.key); setAdminSettingsSearch(''); }}
                            style={{
                              padding: '10px 14px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f0f0f0',
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              background: adminSettingsUsername === u.key ? '#f0f0f0' : '#fff',
                            }}
                          >
                            <span style={{ fontWeight: 600, color: '#111' }}>{u.key.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</span>
                            <span style={{ fontSize: 11, color: '#888' }}>{u.phone}</span>
                          </div>
                        ))}
                        {filteredUsers.length === 0 && (
                          <div style={{ padding: 14, fontSize: 12, color: '#999', textAlign: 'center' }}>No users found</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {adminSettingsUsername && (
                  <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111', marginBottom: 4 }}>
                      {adminSettingsUsername.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </div>
                    <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
                      {adminSettingsLoading || adminSettingsPrincipal === null
                        ? 'Loading net principal…'
                        : `Net principal: ${formatINR(adminSettingsPrincipal)}`}
                    </div>

                    {!adminSettingsLoading && adminSettingsPrincipal !== null && (
                      <div>
                        {eligible && (
                          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                            <div style={{ flex: 1 }}>
                              <label style={{ fontSize: 11, fontWeight: 700, color: '#888', display: 'block', marginBottom: 4 }}>
                                Withdrawal multiplier
                              </label>
                              <input
                                type="text" inputMode="decimal"
                                value={adminSettingsMultiplier}
                                onChange={(e) => setAdminSettingsMultiplier(e.target.value.replace(/[^0-9.]/g, ''))}
                                style={{
                                  width: '100%', boxSizing: 'border-box', padding: '9px 10px', borderRadius: 8,
                                  border: `1.5px solid ${validMultiplier ? '#ddd' : '#eb4d4b'}`, fontSize: 13, fontWeight: 700, color: '#111',
                                }}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={{ fontSize: 11, fontWeight: 700, color: '#888', display: 'block', marginBottom: 4 }}>
                                Pledge %
                              </label>
                              <input
                                type="text" inputMode="decimal"
                                value={adminSettingsPledgePercent}
                                onChange={(e) => setAdminSettingsPledgePercent(e.target.value.replace(/[^0-9.]/g, ''))}
                                style={{
                                  width: '100%', boxSizing: 'border-box', padding: '9px 10px', borderRadius: 8,
                                  border: `1.5px solid ${validPledgePercent ? '#ddd' : '#eb4d4b'}`, fontSize: 13, fontWeight: 700, color: '#111',
                                }}
                              />
                            </div>
                          </div>
                        )}
                        {eligible && (!validMultiplier || !validPledgePercent) && (
                          <div style={{ fontSize: 11, color: '#eb4d4b', marginBottom: 10 }}>
                            {!validMultiplier && 'Multiplier must be a number ≥ 1. '}
                            {!validPledgePercent && 'Pledge % must be between 1 and 100.'}
                          </div>
                        )}
                        <button
                          onClick={() => handleCreateProfitWithdrawal(adminSettingsUsername, adminSettingsPrincipal, multiplierNum, pledgePercentNum)}
                          disabled={!eligible || !validMultiplier || !validPledgePercent || profitWithdrawBusy}
                          style={{
                            width: '100%', padding: '11px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                            cursor: eligible && validMultiplier && validPledgePercent && !profitWithdrawBusy ? 'pointer' : 'default',
                            background: eligible && validMultiplier && validPledgePercent ? '#111' : '#eee',
                            color: eligible && validMultiplier && validPledgePercent ? '#fff' : '#999',
                            border: 'none',
                          }}
                        >
                          {profitWithdrawBusy ? 'Working…' : 'Reject Pending & Create Profit Withdrawal'}
                        </button>
                        <div style={{ fontSize: 11, color: '#888', marginTop: 6, lineHeight: 1.5 }}>
                          {eligible
                            ? (previewAmount !== null && previewPledge !== null
                                ? `Rejects any currently-pending withdrawals for this user, then creates one new pending withdrawal of ${formatINR(previewAmount)} (${adminSettingsMultiplier}× principal), pledging ${formatINR(previewPledge)} (${adminSettingsPledgePercent}% of principal) until it resolves.`
                                : 'Enter a valid multiplier and pledge % to preview the withdrawal.')
                            : `Requires ≥${formatINR(PROFIT_WITHDRAWAL_MIN_PRINCIPAL)} net principal — currently ${formatINR(adminSettingsPrincipal)}.`}
                        </div>
                        {profitWithdrawStatus && (
                          <div style={{
                            fontSize: 12, marginTop: 8, fontWeight: 600,
                            color: profitWithdrawStatus.startsWith('Error') ? '#eb4d4b' : '#27ae60',
                          }}>
                            {profitWithdrawStatus}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* System-wide cleanup, independent of the per-user picker above — reject every
                    currently-pending withdrawal for every user, one time, as the switch to
                    Profit Withdrawal being the only enabled withdrawal method. */}
                <div style={{
                  background: '#fff', borderRadius: 12, padding: 18, marginTop: 20,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1.5px solid #eb4d4b',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#eb4d4b', marginBottom: 4 }}>
                    Reject All Pending Withdrawals (system-wide)
                  </div>
                  <div style={{ fontSize: 11.5, color: '#888', marginBottom: 12, lineHeight: 1.5 }}>
                    One-time cleanup: rejects every currently-pending withdrawal request, for every
                    user, releasing any held pledge — both legacy Firestore ones and RTDB
                    withdrawalRequests. Does not touch investments or already-settled transactions.
                  </div>
                  <button
                    onClick={handleRejectAllPendingWithdrawals}
                    disabled={bulkRejectBusy}
                    style={{
                      width: '100%', padding: '11px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                      cursor: bulkRejectBusy ? 'default' : 'pointer',
                      background: '#eb4d4b', color: '#fff', border: 'none',
                    }}
                  >
                    {bulkRejectBusy ? 'Working…' : 'Reject all pending withdrawals'}
                  </button>
                  {bulkRejectStatus && (
                    <div style={{
                      fontSize: 12, marginTop: 8, fontWeight: 600,
                      color: bulkRejectStatus.startsWith('Error') ? '#eb4d4b' : '#27ae60',
                    }}>
                      {bulkRejectStatus}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ---- Invest (Class B booking) ---- */}
          {tab === 'invest' && (() => {
            const start = slotForm.startDate || todayISO();
            const dur = Number(slotForm.duration) || 3;
            const end = addMonthsISO(start, dur);
            const principal = Number(slotForm.principal) || 0;
            const durationDays = daysBetween(start, end);
            const cashPayout = principal * cciMultiplier(durationDays);
            const notionalSI = principal * siMultiplier(durationDays);
            const gap = notionalSI - cashPayout;
            const available = availableForWindow(slots, start, end);
            const cciRate = ((cciMultiplier(durationDays) - 1) * 100);
            return (
              <div>
                <div onClick={() => setTab('transactions')} style={{ fontSize: 13, cursor: 'pointer', color: '#888', marginBottom: 16 }}>
                  &larr; Back
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6, color: '#111' }}>Invest</div>
                <div style={{ fontSize: 13, color: '#666', marginBottom: 20 }}>
                  Pre-commit to a fixed term. Cash payout at maturity is locked at 2% / 30d continuous compound interest.
                </div>
                <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div className="ob-field">
                    <label>Amount (Rs)</label>
                    <input className="ob-input" placeholder="e.g. 10000000" value={slotForm.principal}
                      onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setSlotForm({ ...slotForm, principal: v }); }}
                      inputMode="numeric"
                    />
                    {principal > 0 && (
                      <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>{formatINR(principal)}</div>
                    )}
                  </div>
                  <div className="ob-field">
                    <label>Duration</label>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {DURATION_OPTIONS_MONTHS.map((m) => (
                        <div key={m}
                          onClick={() => setSlotForm({ ...slotForm, duration: m })}
                          style={{
                            padding: '8px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            background: slotForm.duration === m ? '#111' : '#fff',
                            color: slotForm.duration === m ? '#fff' : '#111',
                            border: '1.5px solid #111',
                          }}
                        >{m} mo</div>
                      ))}
                    </div>
                  </div>
                  <div className="ob-field">
                    <label>Start date</label>
                    <DatePicker
                      selected={new Date(start)}
                      onChange={(d) => setSlotForm({ ...slotForm, startDate: d.toISOString().slice(0, 10) })}
                      dateFormat="dd MMM yyyy"
                      minDate={new Date()}
                      customInput={<input className="ob-input" readOnly />}
                    />
                  </div>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 16 }}>
                    Window: <strong>{start}</strong> &rarr; <strong>{end}</strong> &middot; {durationDays} days &middot; 2% CCI return: <strong>{cciRate.toFixed(2)}%</strong>
                  </div>
                  <div style={{ background: '#f9f9f6', borderRadius: 10, padding: 14, fontSize: 13, marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                      <span>Available in this window</span>
                      <span style={{ fontWeight: 700, color: available >= principal ? '#27ae60' : '#eb4d4b' }}>{formatINR(available)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                      <span>Cash at maturity (2% CCI)</span>
                      <span style={{ fontWeight: 700, color: '#27ae60' }}>{formatINR(cashPayout)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                      <span>Notional wallet at maturity (1 PPD SI)</span>
                      <span style={{ fontWeight: 700 }}>{formatINR(notionalSI)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                      <span>SI&ndash;CCI gap (AppBru share buyback, opens shortly)</span>
                      <span style={{ fontWeight: 700, color: '#e67e22' }}>{formatINR(gap)}</span>
                    </div>
                  </div>
                  <button className="ob-next-btn" style={{ width: '100%' }}
                    disabled={slotSubmitting || !principal || principal > available}
                    onClick={handleBookSlot}
                  >
                    {slotSubmitting ? 'Booking...' : 'Book slot'}
                  </button>
                  {slotStatus && <div style={{ fontSize: 12, marginTop: 8, color: '#eb4d4b' }}>{slotStatus}</div>}
                </div>
              </div>
            );
          })()}

          {/* ---- My Slots ---- */}
          {tab === 'myslots' && (() => {
            const today = todayISO();
            return (
              <div>
                <div onClick={() => setTab('transactions')} style={{ fontSize: 13, cursor: 'pointer', color: '#888', marginBottom: 16 }}>
                  &larr; Back
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: '#111' }}>My Slots</div>
                {slotsLoading && mySlots.length === 0 && (
                  <div style={{ padding: 24, textAlign: 'center', color: '#bbb', fontSize: 13, background: '#fff', borderRadius: 12 }}>Loading...</div>
                )}
                {!slotsLoading && mySlots.length === 0 && (
                  <div style={{ padding: 24, textAlign: 'center', color: '#bbb', fontSize: 13, background: '#fff', borderRadius: 12 }}>No slots booked yet.</div>
                )}
                {mySlots
                  .slice()
                  .sort((a, b) => a.startDate.localeCompare(b.startDate))
                  .map((s) => {
                    const duration = daysBetween(s.startDate, s.endDate);
                    const elapsed = Math.max(0, Math.min(duration, daysBetween(s.startDate, today)));
                    const cashAtMaturity = s.principal * cciMultiplier(duration);
                    const notionalNow = s.principal * siMultiplier(elapsed);
                    const cashNow = s.principal * cciMultiplier(elapsed);
                    const gapAtMaturity = s.principal * (siMultiplier(duration) - cciMultiplier(duration));
                    const statusColor = { active: '#27ae60', booked: '#e67e22', matured: '#888', cancelled: '#eb4d4b' }[s.status] || '#888';
                    return (
                      <div key={s.id} style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderLeft: `4px solid ${statusColor}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                          <span style={{ fontSize: 17, fontWeight: 700, color: '#111' }}>{formatINR(s.principal)}</span>
                          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: statusColor }}>{s.status}</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                          {s.durationMonths} mo &middot; {s.startDate} &rarr; {s.endDate}
                        </div>
                        <div style={{ marginTop: 12, fontSize: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                            <span style={{ color: '#888' }}>Notional now (SI)</span>
                            <span style={{ fontWeight: 700 }}>{formatINR(notionalNow)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                            <span style={{ color: '#888' }}>Legal value now (CCI)</span>
                            <span style={{ fontWeight: 700 }}>{formatINR(cashNow)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                            <span style={{ color: '#888' }}>Cash at maturity</span>
                            <span style={{ fontWeight: 700, color: '#27ae60' }}>{formatINR(cashAtMaturity)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                            <span style={{ color: '#888' }}>Gap settled via AppBru buyback (opens shortly)</span>
                            <span style={{ fontWeight: 700, color: '#e67e22' }}>{formatINR(gapAtMaturity)}</span>
                          </div>
                        </div>
                        {s.status === 'booked' && s.startDate > today && (
                          <button
                            onClick={() => handleCancelSlot(s.id)}
                            style={{ marginTop: 10, background: 'transparent', border: '1px solid #eb4d4b', color: '#eb4d4b', fontSize: 11, fontWeight: 600, padding: '6px 12px', borderRadius: 6, cursor: 'pointer' }}
                          >Cancel booking</button>
                        )}
                      </div>
                    );
                  })}
              </div>
            );
          })()}

          {/* ---- Admin Slots Calendar ---- */}
          {tab === 'calendar' && isAdmin && !selectedUsername && (() => {
            const sorted = [...slots].sort((a, b) => a.startDate.localeCompare(b.startDate));
            const months = 36;
            const base = new Date(); base.setDate(1); base.setHours(0,0,0,0);
            const bars = [];
            for (let i = 0; i < months; i++) {
              const mStart = new Date(base); mStart.setMonth(mStart.getMonth() + i);
              const mEnd = new Date(mStart); mEnd.setMonth(mEnd.getMonth() + 1);
              const occ = maxActivePrincipal(slots, mStart.toISOString().slice(0,10), mEnd.toISOString().slice(0,10));
              bars.push({ label: mStart.toISOString().slice(0,7), occ, pct: (occ / CLASS_B_CAP) * 100 });
            }
            return (
              <div>
                <div onClick={() => setTab('transactions')} style={{ fontSize: 13, cursor: 'pointer', color: '#888', marginBottom: 16 }}>
                  &larr; Back
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: '#111' }}>Slots Calendar</div>
                <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Occupancy next 36 months</div>
                  <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 120, background: '#fafaf6', padding: 6, borderRadius: 8 }}>
                    {bars.map((b, i) => (
                      <div key={i} title={`${b.label}: ${formatINR(b.occ)} / ${formatINR(CLASS_B_CAP)} (${b.pct.toFixed(0)}%)`}
                        style={{ flex: 1, height: Math.max(2, b.pct) + '%', background: b.pct > 80 ? '#eb4d4b' : b.pct > 50 ? '#e67e22' : '#27ae60', borderRadius: 2 }}
                      />
                    ))}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#888', marginTop: 6 }}>
                    <span>{bars[0]?.label}</span>
                    <span>{bars[bars.length - 1]?.label}</span>
                  </div>
                </div>
                <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>All slots ({sorted.length})</div>
                  {sorted.length === 0 && <div style={{ fontSize: 12, color: '#bbb', textAlign: 'center', padding: 12 }}>No slots yet.</div>}
                  {sorted.map((s) => {
                    const statusColor = { active: '#27ae60', booked: '#e67e22', matured: '#888', cancelled: '#eb4d4b' }[s.status] || '#888';
                    return (
                      <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0', fontSize: 12 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, color: '#111' }}>{s.username}</div>
                          <div style={{ color: '#888', fontSize: 11 }}>{s.startDate} &rarr; {s.endDate} ({s.durationMonths} mo)</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 700 }}>{formatINR(s.principal)}</div>
                          <div style={{ fontSize: 10, color: statusColor, textTransform: 'uppercase', fontWeight: 700 }}>{s.status}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ---- Ask AI (all users, admin-moderated) ---- */}
          {tab === 'ask' && (
            <div>
              <div onClick={() => setTab('transactions')} style={{ fontSize: 13, cursor: 'pointer', color: '#888', marginBottom: 16 }}>
                &larr; Back
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6, color: '#111' }}>Ask Achintya</div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 16, lineHeight: 1.5 }}>
                Ask anything about your position, agreements, or Achintya's investment structure. Replies are drafted by our assistant and reviewed by Achintya before being sent to you.
              </div>

              <div style={{
                background: '#fff', borderRadius: 12, padding: 16, marginBottom: 12,
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                maxHeight: 480, overflowY: 'auto',
                display: 'flex', flexDirection: 'column', gap: 10,
              }}>
                {chatMessages.length === 0 && (
                  <div style={{ fontSize: 12, color: '#bbb', textAlign: 'center', padding: 30 }}>
                    No messages yet. Start a conversation below.
                  </div>
                )}
                {chatMessages.map((m) => {
                  // When admin impersonates a user, mirror that user's view — hide unapproved drafts
                  const seeAsUser = !isAdmin || !!selectedUsername;
                  if (m.role === 'assistant' && m.status !== 'approved' && seeAsUser) {
                    return (
                      <div key={m.id} style={{ alignSelf: 'flex-start', maxWidth: '80%', background: '#faf9f5', border: '1px dashed #e8e8e2', borderRadius: 12, padding: '10px 14px', fontSize: 12, color: '#888', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="sync-spinner" />
                        Pending Achintya's review&hellip;
                      </div>
                    );
                  }
                  if (m.role === 'assistant' && m.status === 'rejected' && seeAsUser) return null;
                  const isUser = m.role === 'user';
                  return (
                    <div key={m.id} style={{
                      alignSelf: isUser ? 'flex-end' : 'flex-start',
                      maxWidth: '82%',
                      background: isUser ? '#111' : '#faf9f5',
                      color: isUser ? '#fff' : '#111',
                      borderRadius: 12,
                      padding: '10px 14px',
                      fontSize: 13,
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      border: isUser ? 'none' : '1px solid #e8e8e2',
                    }}>
                      {m.content || m.draftContent || ''}
                      {!seeAsUser && m.role === 'assistant' && m.status && m.status !== 'approved' && (
                        <div style={{ fontSize: 10, color: '#eb4d4b', marginTop: 4, fontWeight: 700, textTransform: 'uppercase' }}>
                          {m.status}
                        </div>
                      )}
                    </div>
                  );
                })}
                <div ref={chatEndRef} />
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  placeholder="Ask a question&hellip;"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !chatSending) handleSendChat(); }}
                  disabled={chatSending}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button
                  onClick={handleSendChat}
                  disabled={chatSending || !chatInput.trim()}
                  style={{ ...btnStyle, opacity: (chatSending || !chatInput.trim()) ? 0.4 : 1 }}
                >
                  {chatSending ? '...' : 'Send'}
                </button>
              </div>
              {chatError && <div style={{ fontSize: 12, color: '#eb4d4b', marginTop: 8 }}>{chatError}</div>}
            </div>
          )}

          {/* ---- AI Review (admin-only) ---- */}
          {tab === 'airev' && isAdmin && !selectedUsername && (
            <div>
              <div onClick={() => setTab('transactions')} style={{ fontSize: 13, cursor: 'pointer', color: '#888', marginBottom: 16 }}>
                &larr; Back
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6, color: '#111' }}>AI Review Queue</div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
                Drafts awaiting your approval. Edits apply before sending.
              </div>

              {pendingDrafts.length === 0 && (
                <div style={{ background: '#fff', borderRadius: 12, padding: 30, textAlign: 'center', fontSize: 13, color: '#bbb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  No pending drafts.
                </div>
              )}

              {pendingDrafts.map((d) => {
                const key = `${d.username}/${d.id}`;
                const edited = draftEdits[key] ?? (d.content || d.draftContent || '');
                return (
                  <div key={key} style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>{d.username}</div>
                      <div style={{ fontSize: 10, color: '#888' }}>
                        {d.createdAt?.toDate?.().toLocaleString?.('en-IN') || ''}
                      </div>
                    </div>
                    {d.userQuestion && (
                      <div style={{ fontSize: 12, color: '#666', fontStyle: 'italic', marginBottom: 10, padding: '8px 10px', background: '#faf9f5', borderRadius: 8, borderLeft: '3px solid #111' }}>
                        Q: {d.userQuestion}
                      </div>
                    )}
                    <textarea
                      value={edited}
                      onChange={(e) => setDraftEdits(prev => ({ ...prev, [key]: e.target.value }))}
                      rows={6}
                      style={{
                        width: '100%', boxSizing: 'border-box', padding: 10, fontSize: 13,
                        borderRadius: 8, border: '1px solid #e8e8e2', fontFamily: 'inherit',
                        lineHeight: 1.5, resize: 'vertical',
                      }}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button
                        onClick={() => handleApproveDraft(d, edited)}
                        style={{ ...btnStyle, background: '#27ae60', flex: 1 }}
                      >
                        Approve & Send
                      </button>
                      <button
                        onClick={() => handleRejectDraft(d)}
                        style={{ ...btnStyle, background: '#fff', color: '#eb4d4b', border: '1.5px solid #eb4d4b' }}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ---- Cancel Investment Document ---- */}
          {tab === 'cancel' && (
            <div style={{
              background: '#fff', borderRadius: 12, padding: 20,
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}>
              <div
                onClick={() => setTab('settings')}
                style={{ fontSize: 13, cursor: 'pointer', color: '#888', marginBottom: 12 }}
              >
                &larr; Back to Settings
              </div>

              {cancelSigned && (
                <div style={{ textAlign: 'center', marginBottom: 16, padding: '10px 0', background: '#fde8e8', borderRadius: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#eb4d4b' }}>Filed on {new Date(cancelSigned.signedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                  {isAdmin && !selectedUsername && (
                    <div
                      onClick={async () => {
                        const viewedUsername = selectedUsername || myUsername;
                        if (!viewedUsername) return;
                        await rtdbSet(ref(rtdb, `users/${viewedUsername}/cancelInvestment`), null);
                        await loadCancel(viewedUsername);
                        setToast('Cancellation reset successfully');
                        setTimeout(() => setToast(''), 3000);
                      }}
                      style={{ fontSize: 11, color: '#eb4d4b', cursor: 'pointer', marginTop: 6, fontWeight: 600 }}
                    >
                      Reset Cancellation
                    </div>
                  )}
                </div>
              )}

              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: '#eb4d4b' }}>Cancellation of Investment</div>

              <div style={{ border: '1px solid #e8e8e2', borderRadius: 12, padding: 18, marginBottom: 20, maxHeight: 400, overflowY: 'auto' }}>
                <div className="nda-body">
                  <p>This Investment Cancellation Request ("<strong>Request</strong>") is submitted by the undersigned investor ("<strong>User</strong>") to Achintya Organisation ("<strong>Achintya</strong>") for the termination and settlement of all outstanding investment positions held on the platform.</p>

                  <h4>1. Declaration of Intent</h4>
                  <p>The User hereby formally requests the cancellation and termination of all active investments held with Achintya. This Request constitutes an irrevocable notice of the User's intent to withdraw from all investment activities on the platform.</p>

                  <h4>2. Account Summary</h4>
                  <p>As of the date of this Request, the User's account reflects the following:</p>
                  <ul>
                    <li><strong>Total Invested:</strong> {'\u20B9'}{totalIn.toLocaleString('en-IN')}</li>
                    <li><strong>Total Withdrawn:</strong> {'\u20B9'}{totalOut.toLocaleString('en-IN')}</li>
                    <li><strong>Net Principal:</strong> {'\u20B9'}{netPrincipal.toLocaleString('en-IN')}</li>
                    <li><strong>Accrued Profit (1 PPD SI):</strong> {'\u20B9'}{interest.toLocaleString('en-IN')}</li>
                    <li><strong>Total Balance Due:</strong> {'\u20B9'}{Math.round(balance).toLocaleString('en-IN')}</li>
                  </ul>

                  <div style={{ background: '#fde8e8', border: '1.5px solid #eb4d4b', borderRadius: 10, padding: '14px 16px', margin: '16px 0', color: '#b71c1c' }}>
                    <p style={{ fontWeight: 800, fontSize: 13, margin: '0 0 8px', color: '#b71c1c' }}>Important Notice</p>
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      <li>Upon the User's consent for cancellation, the investment shall be cancelled by reversing the original invest transactions within <strong>two (2) working days</strong>, after which the amount will be routed back to the User's bank account.</li>
                      <li>Any pending withdrawal transactions shall also stand <strong>cancelled</strong>.</li>
                      <li>The User's official balance, including any accrued profit, shall be <strong>nullified</strong>. The User shall have <strong>no legal ground</strong> to claim profit, capital gains, returns, or any other form of interest arising from these investments.</li>
                    </ul>
                  </div>

                  <h4>3. Settlement Terms</h4>
                  <ul>
                    <li>All pending withdrawal transactions shall stand cancelled;</li>
                    <li>The User's account balance, including all accrued profit, shall be nullified;</li>
                    <li>Any applicable taxes, fees, or deductions shall be withheld as required by law.</li>
                  </ul>

                  <h4>4. Obligations Upon Cancellation</h4>
                  <p>The User acknowledges and agrees that:</p>
                  <ul>
                    <li>All existing Non-Disclosure Agreements and confidentiality obligations shall remain in full force and effect;</li>
                    <li>The User shall not make any claims against Achintya for capital gains, returns, interest, or profits of any kind;</li>
                    <li>Access to the platform shall be terminated upon completion of settlement;</li>
                    <li>The User shall cooperate with Achintya in completing any necessary documentation.</li>
                  </ul>

                  <h4>5. Release of Claims</h4>
                  <p>Upon reversal of the original transactions, the User agrees to release and discharge Achintya from any and all claims, demands, liabilities, and causes of action arising out of or related to the User's investment activities on the platform.</p>

                  <h4>6. Governing Law</h4>
                  <p>This Request shall be governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts located in New Delhi, India.</p>

                  <h4>7. Irrevocability</h4>
                  <p>Once submitted, this Request is <strong>irrevocable</strong> and cannot be withdrawn or modified without written consent from Achintya.</p>
                </div>
              </div>

              {/* Signature section */}
              <div style={{ borderTop: '2px solid #eb4d4b', marginTop: 20, paddingTop: 20 }}>
                {cancelSigned ? (
                  <div>
                    {[
                      { label: 'Full Name', value: cancelSigned.name },
                      { label: 'Phone', value: cancelSigned.phone },
                    ].map((row, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{ fontSize: 12, color: '#888' }}>{row.label}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#111', textAlign: 'right', maxWidth: '60%' }}>{row.value}</span>
                      </div>
                    ))}
                    <div style={{ marginTop: 16, textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Signature</div>
                      <img src={cancelSigned.signatureImg} alt="Signature" style={{ maxWidth: '100%', height: 80, objectFit: 'contain', border: '1px solid #eee', borderRadius: 8 }} />
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 11, color: '#888', marginBottom: 4, display: 'block' }}>Full Legal Name</label>
                      <input placeholder="As per government ID" value={cancelName} onChange={(e) => setCancelName(e.target.value)} style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 11, color: '#888', marginBottom: 4, display: 'block' }}>Phone Number</label>
                      <div style={{ ...inputStyle, background: '#f5f5f5', color: '#555' }}>{user.phoneNumber}</div>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 11, color: '#888', marginBottom: 6, display: 'block' }}>Signature</label>
                      <div style={{ border: '1.5px solid #eb4d4b', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                        <SignatureCanvas ref={cancelSigRef} penColor="#eb4d4b" canvasProps={{ width: 340, height: 120, style: { width: '100%', height: 120 } }} />
                      </div>
                      <div onClick={() => cancelSigRef.current && cancelSigRef.current.clear()} style={{ fontSize: 11, color: '#888', cursor: 'pointer', marginTop: 4, textAlign: 'right' }}>Clear signature</div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', marginBottom: 16 }}>
                      <input type="checkbox" checked={cancelAgreed} onChange={(e) => setCancelAgreed(e.target.checked)} style={{ marginTop: 3, accentColor: '#eb4d4b', width: 16, height: 16 }} />
                      <span style={{ fontSize: 12, color: '#333', lineHeight: 1.5 }}>I understand that this cancellation is irrevocable. I have read and agree to all terms above and acknowledge that my digital signature is legally binding.</span>
                    </label>
                    <button onClick={() => {
                      if (!cancelAgreed) return;
                      if (window.confirm('Are you sure? This cancellation is irrevocable and cannot be undone.')) {
                        handleSignCancel();
                      }
                    }} style={{ ...btnStyle, width: '100%', padding: 14, fontSize: 15, fontWeight: 700, background: '#eb4d4b', opacity: cancelAgreed ? 1 : 0.4 }}>Sign & Submit Cancellation</button>
                    {cancelStatus && <div style={{ fontSize: 12, marginTop: 8, textAlign: 'center', color: '#eb4d4b' }}>{cancelStatus}</div>}
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="App">
        <header className="App-header">
          <img src={logo} style={{ marginTop: -40 }} className="App-logo" alt="logo" />
        </header>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">

        <img src={logo} style={{ marginTop: -40 }} className="App-logo" alt="logo" />
        <p style={{ fontWeight: 500, fontSize: 36, color: '#000' }}>Achintya</p>
        <p style={{ fontWeight: 200, color: '#000', marginTop: -10 }}>The silent revolution to take the world from Internet to Innernet!</p>

        <div style={{ minHeight: 180, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start' }}>

        {/* ---- FIRST SCREEN ---- */}
        {loginState === 'login' && (
          <button
            onClick={() => { setLoginState('phone'); setErrorState(''); }}
            style={{ background: 'black', color: 'white', border: 'none', fontSize: 16, cursor: 'pointer', padding: 16 }}
          >
            Enter
          </button>
        )}

        {/* ---- PHONE NUMBER SCREEN ---- */}
        {loginState === 'phone' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 0, border: '1px solid #000', overflow: 'hidden', background: '#fff', minWidth: 260 }}>
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                style={{
                  padding: '10px 8px', border: 'none', borderRight: '1px solid #000',
                  fontSize: 14, background: '#fff', cursor: 'pointer', color: '#000',
                  outline: 'none',
                }}
              >
                {countryCodes.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
              <input
                type="tel"
                placeholder="xxxxx xxxxx"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                style={{
                  padding: '10px 12px', border: 'none',
                  fontSize: 14, flex: 1, outline: 'none', color: '#000',
                }}
              />
            </div>

            {(() => {
              const busy = statusMsg === 'Sending OTP...';
              return (
                <>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={handleSendOtp}
                      disabled={busy}
                      style={{
                        background: busy ? '#888' : 'black', color: 'white', border: 'none',
                        fontSize: 16, cursor: busy ? 'not-allowed' : 'pointer', padding: 16,
                      }}
                    >
                      {busy ? statusMsg : 'Send OTP'}
                    </button>
                    <button
                      onClick={() => { setLoginState('login'); setErrorState(''); }}
                      style={{ background: '#fff', color: '#000', border: '1px solid #000', fontSize: 16, cursor: 'pointer', padding: 16 }}
                    >
                      Back
                    </button>
                  </div>
                  <p style={{ marginTop: 6, minHeight: 18, fontSize: 13, color: errorState ? 'red' : '#000' }}>
                    {errorState || (busy ? '' : statusMsg) || '\u00A0'}
                  </p>
                </>
              );
            })()}
          </div>
        )}

        {/* ---- OTP SCREEN ---- */}
        {loginState === 'otp' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="------"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              maxLength={6}
              style={{
                padding: '10px 12px', border: '1px solid #000',
                fontSize: 18, width: 260, textAlign: 'center',
                letterSpacing: 10, fontWeight: 700, boxSizing: 'border-box',
                background: '#fff', color: '#000', outline: 'none',
              }}
            />

            {(() => {
              const busy = statusMsg === 'Verifying...' || statusMsg === 'Checking registration...';
              return (
                <>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={handleVerifyOtp}
                      disabled={busy}
                      style={{
                        background: busy ? '#888' : 'black', color: 'white', border: 'none',
                        fontSize: 16, cursor: busy ? 'not-allowed' : 'pointer', padding: 16,
                      }}
                    >
                      {busy ? statusMsg : 'Verify'}
                    </button>
                    <button
                      onClick={() => { setLoginState('phone'); setOtp(''); setErrorState(''); setStatusMsg(''); setSecretMode(false); }}
                      style={{ background: '#fff', color: '#000', border: '1px solid #000', fontSize: 16, cursor: 'pointer', padding: 16 }}
                    >
                      Back
                    </button>
                  </div>
                  <p style={{ marginTop: 6, minHeight: 18, fontSize: 13, color: errorState ? 'red' : '#000' }}>
                    {errorState || (busy ? '' : statusMsg) || (secretMode ? `Enter code for ${countryCode}${secretPhone}` : `OTP sent to ${countryCode}${phone}`)}
                  </p>
                </>
              );
            })()}
          </div>
        )}

        </div>

        <div id="recaptcha-container"></div>

      </header>
    </div>
  );
}

export default App;
